import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Users, 
  Truck, 
  Plus,
  X,
  Edit,
  Eye,
  EyeOff,
  RefreshCw,
  Clock,
  Trash2
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../App';
import DispatchConfirmationModal from './DispatchConfirmationModal';
import UpcomingVacations from './UpcomingVacations';
import { CancelJobModal, RebookJobModal } from './CancelJobModal';
import { ModifyCrewModal } from './DispatchRequests';
import DispatchSmsModal from './DispatchSmsModal';
import DispatchThreadPanel from './DispatchThreadPanel';
import ScheduleReconcileModal from './ScheduleReconcileModal';
import { ProjectionDialog, ProjectionEditor } from './schedule/ProjectionDialog';
import { ScheduleDayTabs } from './dailies/ScheduleDayTabs';
import { DayView } from './dailies/DayView';
import WorkPeriodPeek from './schedule/WorkPeriodPeek';
import { SheetTools } from './dailies/SheetTools';

// Timeline window: Monday of the current week → 4 weeks out (module-level so it can be used before the
// component's own helpers are declared)
const SCHEDULE_WINDOW_DAYS = 28;
const mondayOf = (date) => {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() - ((day + 6) % 7));
  return d;
};
const ymdLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const scheduleWindow = (date) => {
  const start = mondayOf(date);
  const end = new Date(start);
  end.setDate(start.getDate() + SCHEDULE_WINDOW_DAYS - 1);
  return { start, end, startKey: ymdLocal(start), endKey: ymdLocal(end) };
};

const ScheduleNew = () => {
  const { user, token, loading } = useAuth();
  
  console.log('🎬 ScheduleNew component rendering - token:', !!token, 'user:', !!user, 'loading:', loading);
  // Initialize to today's date
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  // Sheet-style tabs along the bottom: null = Monthly View grid, 'YYYY-MM-DD' = that day's dailies
  const [dayTab, setDayTab] = useState(null);
  const [dayTabKey, setDayTabKey] = useState(0);
  const [dayTabMonth, setDayTabMonth] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' }).slice(0, 7));
  const [projects, setProjects] = useState([]);
  // CRITICAL FIX: Load hidden projects from localStorage IMMEDIATELY on mount to prevent race condition
  const [hiddenProjects, setHiddenProjects] = useState(() => {
    try {
      const saved = localStorage.getItem('schedule_visible_projects');
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Set(parsed);
      }
    } catch (error) {
      console.error('Error loading visible projects:', error);
    }
    return new Set(); // Empty = nothing visible by default
  });
  const [showHiddenProjects, setShowHiddenProjects] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  const [hasLoadedHiddenProjects, setHasLoadedHiddenProjects] = useState(true);
  
  // Conflict resolution modal
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [workPeriods, setWorkPeriods] = useState([]);
  const [groupByPM, setGroupByPM] = useState(true); // Group projects by PM
  const [compactView, setCompactView] = useState(() => {
    const saved = localStorage.getItem('schedule_compact_view');
    return saved ? JSON.parse(saved) : true; // Default to compact view
  });
  const [managers, setManagers] = useState([]); // List of PMs for reassignment
  const [resources, setResources] = useState({
    crew: [],
    trucks: [],
    trailers: [],
    welders: []
  });
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedItemType, setDraggedItemType] = useState(null);
  // Crosshair highlight while dragging onto the grid: { projectKey, dateKey, conflict }
  const [dragHover, setDragHover] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('crew');
  const [selectedWorkPeriod, setSelectedWorkPeriod] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [cancelJobWP, setCancelJobWP] = useState(null);
  const [rebookWP, setRebookWP] = useState(null);
  const [schedModifyReq, setSchedModifyReq] = useState(null);
  const [schedResources, setSchedResources] = useState({ crew: [] });
  const [schedSmsReq, setSchedSmsReq] = useState(null);
  const [schedSmsRestrict, setSchedSmsRestrict] = useState(null);
  const [isDraggingWorkPeriod, setIsDraggingWorkPeriod] = useState(false);
  const [draggedWorkPeriod, setDraggedWorkPeriod] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [dragElement, setDragElement] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [justResized, setJustResized] = useState(false);
  // Add render key to force re-renders when needed
  const [renderKey, setRenderKey] = useState(0);
  // Tooltip state for compact view
  const [hoveredWorkPeriod, setHoveredWorkPeriod] = useState(null);
  const [projectionFor, setProjectionFor] = useState(null); // project → "+ projection" dialog
  const [hoveredDay, setHoveredDay] = useState(null); // Track which specific day is hovered
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  // User interaction tracking for drag-and-drop
  const isUserInteracting = useRef(false);
  // Dispatch system state
  const [dispatchStatuses, setDispatchStatuses] = useState({});
  // Label for the 4 weeks actually on screen, e.g. "Nov 2 – Nov 29, 2026" (follows the window — never stale)
  const windowLabel = React.useMemo(() => {
    const { start, end } = scheduleWindow(currentDate);
    const d = (x) => x.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${d(start)} – ${d(end)}, ${end.getFullYear()}`;
  }, [currentDate]);
  const [showDispatchConfirm, setShowDispatchConfirm] = useState(false);
  const [dispatchMessage, setDispatchMessage] = useState('');
  // Temporary scheduling objects
  const [tempScheduleObjects, setTempScheduleObjects] = useState(() => {
    const saved = localStorage.getItem('temp_schedule_objects');
    return saved ? JSON.parse(saved) : [];
  });

  const daysInWeek = 7;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Add/remove project from schedule
  const toggleProjectVisibility = (projectId) => {
    // Always use project_number for stability — IDs change on sync
    const proj = projects.find(p => p.id === projectId);
    const key = proj?.project_number || projectId;
    
    setHiddenProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
        toast.success('Project removed from schedule');
      } else {
        newSet.add(key);
        toast.success('Project added to schedule');
      }
      localStorage.setItem('schedule_visible_projects', JSON.stringify([...newSet]));
      return newSet;
    });
  };

  // Create temporary scheduling object
  const createTempScheduleObject = (pmName) => {
    const newTempObject = {
      id: `temp-${Date.now()}`,
      pmName: pmName,
      title: '',
      isEditing: true,
      isTemp: true
    };
    setTempScheduleObjects(prev => [...prev, newTempObject]);
    toast.success('Temporary schedule object created');
  };

  // Update temporary scheduling object title (while typing)
  const updateTempObjectTitle = (tempId, newTitle) => {
    setTempScheduleObjects(prev => 
      prev.map(obj => obj.id === tempId ? { ...obj, title: newTitle } : obj)
    );
  };

  // Finish editing temp object (called on blur or Enter)
  const finishEditingTempObject = (tempId) => {
    setTempScheduleObjects(prev =>
      prev.map(obj => obj.id === tempId ? { ...obj, isEditing: false } : obj)
    );
  };

  // Toggle edit mode for temp object
  const toggleTempObjectEdit = (tempId) => {
    setTempScheduleObjects(prev =>
      prev.map(obj => obj.id === tempId ? { ...obj, isEditing: !obj.isEditing } : obj)
    );
  };

  // Delete temporary scheduling object
  const deleteTempScheduleObject = (tempId) => {
    setTempScheduleObjects(prev => prev.filter(obj => obj.id !== tempId));
    // Also remove any work periods associated with this temp object
    setWorkPeriods(prev => prev.filter(wp => wp.project_id !== tempId));
    toast.success('Temporary object deleted');
  };

  // Filter visible projects — show pinned projects AND any project with a work period inside the visible
  // timeline window (jobs with nothing on the board auto-hide; they come back as soon as something is scheduled)
  const { startKey: windowStartKey, endKey: windowEndKey } = scheduleWindow(currentDate);
  const projectsWithWorkPeriods = React.useMemo(() => {
    const s = new Set();
    workPeriods.forEach((wp) => {
      if (wp.start_date <= windowEndKey && wp.end_date >= windowStartKey) s.add(wp.project_id);
    });
    return s;
  }, [workPeriods, windowStartKey, windowEndKey]);
  const filteredProjects = React.useMemo(() => 
    projects.filter(p => {
      const pn = p.project_number;
      return showHiddenProjects || hiddenProjects.has(pn) || projectsWithWorkPeriods.has(p.id) || projectsWithWorkPeriods.has(pn);
    }),
    [projects, showHiddenProjects, hiddenProjects, projectsWithWorkPeriods]
  );
  
  // Group projects by PM if enabled
  const groupedProjects = React.useMemo(() => {
    if (!groupByPM) {
      return { 'All Projects': filteredProjects };
    }
    
    // Valid PM names - standardized to "FirstName LastInitial" format
    // Includes initials (CG, JK), FirstName LastInitial (Craig G, Justin K), and full names
    const validPMs = [
      'Jeff H', 'Jeff Henhoeffer', 'JH',
      'Jeff D', 'Jeff DeGrazia', 'JD',
      'Justin K', 'Justin Koleszar', 'JK',
      'Carl L', 'Carl Long', 'Carl Laput', 'CL',
    ];
    
    const groups = {};
    filteredProjects.forEach(project => {
      let pm = project.assigned_manager || 'Unassigned';
      
      // FIRST: Strip trailing numbers from PM names (e.g., "Craig G -6" -> "Craig G")
      pm = pm.replace(/\s*-\s*\d+$/, '').trim();
      
      // Normalize ALL PM names to "FirstName LastInitial" format
      // Handles three formats: initials (CG), FirstName LastInitial (Craig G), Full Name (Craig Grover)
      const pmNormalization = {
        // Carl variations
        'Carl Laput': 'Carl L',
        'Carl Long': 'Carl L',
        'CL': 'Carl L',
        // Jeff H variations
        'Jeff Henhoeffer': 'Jeff H',
        'JH': 'Jeff H',
        // Jeff D variations
        'Jeff DeGrazia': 'Jeff D',
        'JD': 'Jeff D',
        // Justin variations
        'Justin Koleszar': 'Justin K',
        'JK': 'Justin K',
      };
      
      // Apply normalization if exists
      if (pmNormalization[pm]) {
        pm = pmNormalization[pm];
      }
      
      // If PM is not in valid list, move to Unassigned (prevents "cg/ms" and other invalid entries)
      if (pm !== 'Unassigned' && !validPMs.includes(pm)) {
        console.warn(`⚠️ Invalid PM name "${pm}" on project ${project.project_number}, moving to Unassigned`);
        pm = 'Unassigned';
      }
      
      if (!groups[pm]) {
        groups[pm] = [];
      }
      groups[pm].push(project);
    });
    
    // Add temporary scheduling objects to their respective PM groups at the top
    tempScheduleObjects.forEach(tempObj => {
      if (!groups[tempObj.pmName]) {
        groups[tempObj.pmName] = [];
      }
      // Add temp objects at the beginning of the array
      groups[tempObj.pmName].unshift(tempObj);
    });
    
    // Sort groups by custom PM order (normalized names)
    const pmOrder = ['Jeff H', 'Jeff D', 'Justin K', 'Carl L', 'Unassigned'];
    const sortedGroups = {};
    
    // Sort projects within each group by project_number descending (newest first)
    const sortByNumber = (a, b) => {
      const numA = a.project_number || '';
      const numB = b.project_number || '';
      return numB.localeCompare(numA);
    };
    
    // Add ALL PMs in order, even if they have no projects
    pmOrder.forEach(pm => {
      sortedGroups[pm] = (groups[pm] || []).sort(sortByNumber);
    });
    
    // Add any remaining groups not in the order list
    Object.keys(groups).forEach(pm => {
      if (!pmOrder.includes(pm)) {
        sortedGroups[pm] = groups[pm].sort(sortByNumber);
      }
    });
    
    return sortedGroups;
  }, [filteredProjects, groupByPM, tempScheduleObjects]);
  
  const visibleProjects = filteredProjects;

  // Main data fetching effect - triggers when auth completes or date changes
  useEffect(() => {
    console.log('🔥 Main useEffect triggered - loading:', loading, 'token:', !!token, 'currentDate:', currentDate.toLocaleDateString());
    
    // Wait for auth to finish loading
    if (loading) {
      return;
    }
    
    // Only fetch on initial load (when no projects loaded yet)
    if (!token) {
      return;
    }

    if (projects.length === 0) {
      fetchScheduleData();
      fetchResources();
      fetchManagers();
    }
  }, [currentDate, token, loading]);

  // Save currentDate to localStorage whenever it changes
  useEffect(() => {
    // Don't persist date — always start at today
  }, [currentDate]);

  // Save hiddenProjects to localStorage whenever it changes (but only AFTER initial load)
  useEffect(() => {
    if (hasLoadedHiddenProjects) {
      const hiddenArray = Array.from(hiddenProjects);
      localStorage.setItem('schedule_visible_projects', JSON.stringify(hiddenArray));
      console.log('💾 Saved visible projects to localStorage:', hiddenArray.length, 'projects');
    }
  }, [hiddenProjects, hasLoadedHiddenProjects]);

  // Save temp schedule objects to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('temp_schedule_objects', JSON.stringify(tempScheduleObjects));
  }, [tempScheduleObjects]);

  // Save compact view preference to localStorage
  useEffect(() => {
    localStorage.setItem('schedule_compact_view', JSON.stringify(compactView));
  }, [compactView]);

  // Separate effect for view mode changes that doesn't trigger data refetch
  useEffect(() => {


    
    // Just log the change, don't refetch data
    // The existing work periods will be filtered/displayed based on the new view mode
    // This prevents losing work periods when switching between week/month views
  }, [viewMode]);

  // Auto-refresh functionality - refresh schedule data every 5 minutes
  useEffect(() => {
    if (!token || !backendUrl) {
      return;
    }


    
    // Refresh every 5 minutes
    const autoRefreshInterval = setInterval(() => {

      
      // Only auto-refresh if the component is visible and no modal is open
      if (!document.hidden && !isDraggingWorkPeriod && !isDetailModalOpen) {
        fetchScheduleData();
        
        // Show subtle notification that data is refreshing
        const toastId = toast.loading('Refreshing schedule data...', { duration: 1000 });
        setTimeout(() => {
          toast.dismiss(toastId);
        }, 1000);
      } else {

      }
    }, 300000); // 5 minutes

    // Cleanup interval on unmount
    return () => {

      clearInterval(autoRefreshInterval);
    };
  }, [token, backendUrl, isDraggingWorkPeriod, isDetailModalOpen]); // Dependencies for refresh control

  // Keyboard shortcuts for better UX - ENHANCED AND DEBUGGED
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle shortcuts when not typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      

      
      switch (e.key) {
        case 'r':
        case 'R':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();

            handleBulletproofRefresh();
            toast.info('Keyboard shortcut: Ctrl+R refresh');
          }
          break;
        case 'Escape':
          if (isDetailModalOpen) {
            e.preventDefault();

            setIsDetailModalOpen(false);
            toast.info('Keyboard shortcut: ESC closed modal');
          }
          break;
        case 'w':
        case 'W':
          if (!isDetailModalOpen && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();

            setViewMode('week');
            toast.info('Keyboard shortcut: W for week view');
          }
          break;
        case 'm':
        case 'M':
          if (!isDetailModalOpen && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();

            setViewMode('month');
            toast.info('Keyboard shortcut: M for month view');
          }
          break;
        case '?':
          if (!isDetailModalOpen) {
            e.preventDefault();
            toast.info('Keyboard shortcuts: W=Week, M=Month, Ctrl+R=Refresh, ESC=Close modal, Shift+Drag=Move work period');
          }
          break;
      }
    };


    document.addEventListener('keydown', handleKeyDown);
    
    return () => {

      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDetailModalOpen]); // Add dependencies

  // Additional effect to refetch data when switching between views to ensure proper date ranges
  useEffect(() => {
    if (token) {

      fetchScheduleData();
    }
  }, [viewMode]);

  // DISABLED: These useEffect hooks cause race conditions during split/merge operations
  // They trigger when workPeriods.length === 0 during legitimate state updates
  // Commenting out to prevent work periods from disappearing
  
  /*
  // Handle page visibility changes (when user returns from other tabs/agents)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && workPeriods.length === 0) {
        fetchScheduleData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [workPeriods.length]);

  // Handle window focus events (when user returns to preview)
  useEffect(() => {
    const handleFocus = () => {
      if (workPeriods.length === 0 && token) {
        fetchScheduleData();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && workPeriods.length === 0 && token) {
        fetchScheduleData();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [workPeriods.length, token]);
  */

  // BULLETPROOF REFRESH - Safe data refresh without losing work periods
  const handleBulletproofRefresh = async () => {
    console.log('🔄 Bulletproof refresh triggered');
    console.log('🔒 Current hidden projects before refresh:', hiddenProjects.size);

    
    // Show loading state only on initial load
    const isInitialLoad = projects.length === 0;
    if (isInitialLoad) setIsLoading(true);
    
    try {
      // Store current work periods as backup
      const backupWorkPeriods = [...workPeriods];
      const backupProjects = [...projects];
      const backupResources = { ...resources };
      

      
      // Use appropriate date range based on view mode
      let startDate, endDate;
      if (viewMode === 'month') {
        startDate = getMonthStart(currentDate);
        endDate = getMonthEnd(currentDate);
      } else {
        // For week view, use extended range (7 days before month + month + 7 days after)
        const monthStart = getMonthStart(currentDate);
        const monthEnd = getMonthEnd(currentDate);
        startDate = new Date(monthStart);
        startDate.setDate(monthStart.getDate() - 7);
        endDate = new Date(monthEnd);
        endDate.setDate(monthEnd.getDate() + 7);
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Test authentication first
      try {
        await axios.get(`${backendUrl}/api/users`, { headers });

      } catch (authError) {
        if (authError.response?.status === 401) {
          console.error('❌ Token expired during refresh');
          toast.error('Your session has expired. Please login again.');
          localStorage.removeItem('token');
          window.location.href = '/';
          return;
        }
        throw authError;
      }

      // Fetch fresh data in parallel - FIXED: Only fetch WIP projects
      const [projectsResponse, workPeriodsResponse] = await Promise.all([
        axios.get(`${backendUrl}/api/projects`, { headers }),
        // FIXED: Remove date filtering to preserve all work periods
        axios.get(`${backendUrl}/api/schedule/work-periods`, { headers })
      ]);
      




      // Projects already filtered to WIP by backend
      const wipProjects = projectsResponse.data;


      // FIXED: Don't filter work periods by project status - preserve ALL work periods
      const allWorkPeriods = workPeriodsResponse.data;


      // Validate data integrity before applying
      const isDataValid = (
        Array.isArray(wipProjects) &&
        Array.isArray(allWorkPeriods)
        // REMOVED: requirement for projects > 0 since user might not have WIP projects
      );

      if (!isDataValid) {
        console.error('🔄 Data validation failed');
        toast.error('Refresh failed - invalid data returned from server');
        // Restore backup
        setProjects(backupProjects);
        setWorkPeriods(backupWorkPeriods);
        setResources(backupResources);
        setIsLoading(false);
        return;
      }

      // Apply fresh data - preserve ALL work periods
      setProjects(wipProjects);
      setWorkPeriods(allWorkPeriods);
      // DO NOT reset hiddenProjects here - it should persist!

      // Refresh resources
      await fetchResources();

      toast.success(`Refreshed: ${wipProjects.length} projects, ${allWorkPeriods.length} work periods`);

    } catch (error) {
      console.error('🔄 Refresh error:', error);
      console.error('🔄 Error details:', error.response?.data || error.message);
      
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        toast.error('Session expired. Please log in again.');
        window.location.href = '/login';
      } else {
        toast.error(`Refresh failed: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScheduleData = async (forceReplace = false, preserveScroll = false) => {
    console.log('🔄 fetchScheduleData called, stack trace:');
    console.trace();
    
    // Don't start loading if no token yet - just wait for it
    if (!token) {
      console.log('⏳ No token yet, waiting for authentication...');
      return;
    }
    
    setIsLoading(true);
    try {
      // Preserve scroll position if requested
      const scrollContainer = document.querySelector('.schedule-scroll-container');
      const scrollTop = preserveScroll && scrollContainer ? scrollContainer.scrollTop : 0;
      
      // Use appropriate date range based on view mode
      let startDate, endDate;
      if (viewMode === 'month') {
        startDate = getMonthStart(currentDate);
        endDate = getMonthEnd(currentDate);
      } else {
        // For week view, use extended range (7 days before month + month + 7 days after)
        const monthStart = getMonthStart(currentDate);
        const monthEnd = getMonthEnd(currentDate);
        startDate = new Date(monthStart);
        startDate.setDate(monthStart.getDate() - 7);
        endDate = new Date(monthEnd);
        endDate.setDate(monthEnd.getDate() + 7);
      }
      


      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Test authentication first to catch expired tokens
      try {

        const authTest = await axios.get(`${backendUrl}/api/users`, { headers });

      } catch (authError) {
        if (authError.response?.status === 401) {
          console.error('❌ Token expired during data fetch');
          toast.error('Your session has expired. Please login again.');
          localStorage.removeItem('token');
          window.location.href = '/';
          return;
        }
        console.warn('⚠️ Auth test failed but continuing:', authError.message);
      }

      // Fetch projects - ALL projects (not just WIP - Christine's data is inconsistent)
      const projectsResponse = await axios.get(`${backendUrl}/api/projects`, { headers });

      const allProjects = projectsResponse.data;
      
      setProjects(allProjects);

      // Fetch work periods for the date range

      const workPeriodsResponse = await axios.get(
        `${backendUrl}/api/schedule/work-periods`,
        { headers }
      );

      // FIXED: Don't filter by WIP - show ALL work periods
      const allWorkPeriods = workPeriodsResponse.data;
      
      // CRITICAL FIX: Use forceReplace for delete/split/merge operations
      if (forceReplace) {
        // Complete replacement - removes deleted items
        setWorkPeriods(allWorkPeriods);
      } else {
        // Smart merge for view changes only
        setWorkPeriods(prevWorkPeriods => {
          const existingMap = new Map(prevWorkPeriods.map(wp => [wp.id, wp]));
          allWorkPeriods.forEach(wp => {
            existingMap.set(wp.id, wp);
          });
          return Array.from(existingMap.values());
        });
      }

      // Restore scroll position
      if (preserveScroll && scrollContainer) {
        setTimeout(() => {
          scrollContainer.scrollTop = scrollTop;
        }, 0);
      }

      // Fetch resources
      await fetchResources();

    } catch (error) {
      console.error('Error fetching schedule data:', error);
      console.error('Error details:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        // Handle authentication errors - force user to re-login

        localStorage.removeItem('token');
        toast.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
        return;
      } else {
        toast.error('Failed to load schedule data. Please refresh the page.');
      }
    }
    setIsLoading(false);
  };

  // Resource availability logic — honours per-member start/end dates (partial-stint crew) and per-day exclusions,
  // so someone who only joins a job on the 20th is free to be booked elsewhere before that.
  const getResourceScheduledPeriods = (resourceId, resourceType) => {
    const scheduledPeriods = [];

    workPeriods.forEach(workPeriod => {
      if (workPeriod.status === 'cancelled') return; // cancelled jobs don't block the crew
      const res = (workPeriod.resources || []).find(r => r.id === resourceId && r.type === resourceType);
      if (!res) return;
      const startStr = res.start_date && res.start_date > workPeriod.start_date ? res.start_date : workPeriod.start_date;
      const endStr = res.end_date && res.end_date < workPeriod.end_date ? res.end_date : workPeriod.end_date;
      if (!startStr || !endStr || startStr > endStr) return;
      const days = new Set();
      const cur = new Date(`${startStr}T12:00:00`);
      const stop = new Date(`${endStr}T12:00:00`);
      let guard = 0;
      while (cur <= stop && guard < 400) {
        const key = formatDate(cur);
        const excl = (workPeriod.excluded_dates || {})[key] || [];
        if (!excl.includes(`${resourceType}_${resourceId}`)) days.add(key);
        cur.setDate(cur.getDate() + 1);
        guard += 1;
      }
      if (days.size === 0) return;
      scheduledPeriods.push({
        start: new Date(`${startStr}T12:00:00`),
        end: new Date(`${endStr}T12:00:00`),
        days,
        project_id: workPeriod.project_id,
        workPeriodId: workPeriod.id
      });
    });

    return scheduledPeriods;
  };

  // True when any day in [newStartDate, newEndDate] is one the resource is actually booked on
  const periodOverlaps = (period, newStartDate, newEndDate) => {
    const newStart = new Date(newStartDate);
    const newEnd = new Date(newEndDate);
    if (isNaN(newStart.getTime()) || isNaN(newEnd.getTime())) return false;
    const cur = new Date(newStart);
    let guard = 0;
    while (cur <= newEnd && guard < 400) {
      if (period.days.has(formatDate(cur))) return true;
      cur.setDate(cur.getDate() + 1);
      guard += 1;
    }
    return false;
  };

  // ENHANCED RESOURCE CONFLICT DETECTION - More robust and informative
  const hasScheduleConflict = (resourceId, resourceType, newStartDate, newEndDate, excludeWorkPeriodId = null) => {
    const scheduledPeriods = getResourceScheduledPeriods(resourceId, resourceType);
    return scheduledPeriods.some(period => {
      if (excludeWorkPeriodId && period.workPeriodId === excludeWorkPeriodId) return false;
      return periodOverlaps(period, newStartDate, newEndDate);
    });
  };

  // Enhanced conflict details for better user feedback
  const getScheduleConflictDetails = (resourceId, resourceType, newStartDate, newEndDate, excludeWorkPeriodId = null) => {
    const scheduledPeriods = getResourceScheduledPeriods(resourceId, resourceType);

    const conflicts = scheduledPeriods.filter(period => {
      if (excludeWorkPeriodId && period.workPeriodId === excludeWorkPeriodId) return false;
      return periodOverlaps(period, newStartDate, newEndDate);
    });
    
    return conflicts.map(conflict => {
      const project = projects.find(p => p.id === conflict.project_id);
      return {
        dateRange: `${formatDate(conflict.start)} to ${formatDate(conflict.end)}`,
        projectId: conflict.project_id,
        projectName: project ? `${project.project_number} - ${project.title}` : 'Unknown Project',
        workPeriodId: conflict.workPeriodId
      };
    });
  };

  const getResourceAvailabilityInfo = (resource, resourceType) => {
    // Only check current week — not all time
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0=Sun
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - dayOfWeek);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const allPeriods = getResourceScheduledPeriods(resource.id, resourceType);
    const thisWeekPeriods = allPeriods.filter(p => {
      return p.end >= weekStart && p.start <= weekEnd;
    });
    const isScheduled = thisWeekPeriods.length > 0;
    
    return {
      isScheduled,
      scheduledPeriods: thisWeekPeriods,
      availabilityStatus: isScheduled ? 'scheduled' : 'available'
    };
  };

  const sortResourcesByAvailability = (resources, resourceType) => {
    if (!resources || !Array.isArray(resources)) return [];
    
    return resources.sort((a, b) => {
      const aInfo = getResourceAvailabilityInfo(a, resourceType);
      const bInfo = getResourceAvailabilityInfo(b, resourceType);
      
      // Available resources first, then scheduled (greyed out) resources
      if (aInfo.isScheduled && !bInfo.isScheduled) return 1;
      if (!aInfo.isScheduled && bInfo.isScheduled) return -1;
      
      // If both have same availability status, sort alphabetically
      const aName = a.full_name || a.equipment_name || a.name || '';
      const bName = b.full_name || b.equipment_name || b.name || '';
      return aName.localeCompare(bName);
    });
  };

  const fetchResources = async () => {
    if (!token) return;
    
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch crew members  
      const crewResponse = await axios.get(`${backendUrl}/api/users`, { headers });
      const crewData = crewResponse.data.users || crewResponse.data || [];
      const fieldWorkers = crewData.filter(user => 
        user.is_active !== false && !user.archived && !/\btest\b/i.test(user.full_name || '') &&
        ['field_employee', 'foreman', 'crew', 'millwright', 'welder', 'fitter', 
         'millwright_foreman', 'apprentice', 'labourer', 'project_manager_estimator', 'super_admin'].includes(user.role.toLowerCase())
      );
      
      // Fetch trucks (from owned equipment)
      const trucksResponse = await axios.get(`${backendUrl}/api/owned-equipment?category=truck`, { headers });
      const trucks = trucksResponse.data || [];

      // Fetch trailers
      const trailersResponse = await axios.get(`${backendUrl}/api/owned-equipment?category=trailer`, { headers });
      const trailers = trailersResponse.data || [];

      // Fetch welders (diesel and propane fuel types only)
      // Note: Welders have fuel_type of diesel or propane, not category='welder'
      const allEquipmentResponse = await axios.get(`${backendUrl}/api/owned-equipment`, { headers });
      const allEquipment = allEquipmentResponse.data || [];
      // Filter for welders based on equipment name containing 'weld' or fuel_type being diesel/propane
      const welders = allEquipment.filter(eq => {
        const name = (eq.equipment_name || '').toLowerCase();
        const hasWelderInName = name.includes('weld') || name.includes('trail') || name.includes('miller') || name.includes('lincoln');
        const hasDieselOrPropane = eq.fuel_type === 'diesel' || eq.fuel_type === 'propane';
        return hasWelderInName && hasDieselOrPropane;
      });



      // Sort resources by availability (available first, scheduled at bottom)
      setResources({
        crew: sortResourcesByAvailability(fieldWorkers, 'crew'),
        trucks: sortResourcesByAvailability(trucks, 'trucks'),
        trailers: sortResourcesByAvailability(trailers, 'trailers'),
        welders: sortResourcesByAvailability(welders, 'welders')
      });
    } catch (error) {
      console.error('Error fetching resources:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  // Fetch managers for PM reassignment
  const fetchManagers = async () => {
    if (!token) return;
    
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const response = await axios.get(`${backendUrl}/api/users`, { headers });
      const allUsers = response.data.users || response.data || [];
      // Filter for users who can be PMs (managers, super admins, owner)
      const pmUsers = allUsers.filter(u => 
        u.is_active !== false && (
        u.role === 'project_manager_estimator' || 
        u.role === 'super_admin' || 
        u.role === 'owner')
      );
      setManagers(pmUsers);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  // Change PM for a project
  const changePM = async (projectId, newPM) => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      await axios.patch(
        `${backendUrl}/api/projects/${projectId}`,
        { assigned_manager: newPM },
        { headers }
      );
      
      // Update local state
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, assigned_manager: newPM } : p
      ));
      
      toast.success(`PM changed to ${newPM}`);
    } catch (error) {
      console.error('Error changing PM:', error);
      toast.error('Failed to change PM');
    }
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
    // FIXED: Use setDate but on a cloned date to avoid mutation
    const weekStart = new Date(d);
    weekStart.setDate(diff);
    return weekStart;
  };

  const getWeekEnd = (date) => {
    const weekStart = getWeekStart(date);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return weekEnd;
  };
  const getMonthStart = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getMonthEnd = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  const getExtendedCalendarDays = useCallback(() => {
    // Show full year (12 months) for free scrolling
    const monthStart = getMonthStart(currentDate);
    const startDate = getWeekStart(monthStart);
    
    const days = [];
    const current = new Date(startDate);
    
    // Generate 12 months worth of days (~365 days)
    for (let i = 0; i < 365; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  }, [currentDate]);

  const getMonthDays = useCallback(() => {
    // Legacy function - now just calls extended version
    return getExtendedCalendarDays();
  }, [currentDate]);

  const getDaysInWeek = useCallback(() => {
    const startDate = getWeekStart(currentDate);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  }, [currentDate]);

  // Timeline days for horizontal scrolling: Monday of the current week → 4 weeks (28 days)
  const getExtendedDateRange = useCallback(() => {
    const { start, end } = scheduleWindow(currentDate);
    const days = [];
    const current = new Date(start);
    while (current <= end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  }, [currentDate]);

  const formatDate = (date) => {
    // FIXED: Use UTC methods consistently to prevent timezone shifts
    if (!date) return new Date().toISOString().split('T')[0];
    
    const d = new Date(date);
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Manpower totals: the bubbles in the column added up (like the gsheet "Manpower Totals" row — a tally, so a
  // guy on two jobs the same day counts twice, exactly as the sheet does)
  const manpowerByDay = React.useMemo(() => {
    const map = {};
    const d10 = (s) => (s == null ? '' : String(s).slice(0, 10));
    workPeriods.forEach(wp => {
      if (wp.status === 'cancelled') return;
      const crew = (wp.resources || []).filter(r => r.type === 'crew');
      const planned = wp.planned_crew ? Number(wp.planned_crew) : 0; // projection "4?" / crew-TBD slots count like the sheet's plan numbers
      const hasPlan = wp.plan_by_day && Object.keys(wp.plan_by_day).length > 0; // sheet block with a number but no names yet
      if (crew.length === 0 && !planned && !hasPlan) return;
      const start = d10(wp.start_date);
      const end = d10(wp.end_date) || start;
      if (!start) return;
      const cur = new Date(`${start}T12:00:00`);
      const stop = new Date(`${end}T12:00:00`);
      let guard = 0;
      while (cur <= stop && guard < 120) {
        const key = formatDate(cur);
        // Respect per-member start/end dates (partial-stint crew) and per-day exclusions — same rule as the bar bubble
        const excl = (wp.excluded_dates || {})[key] || [];
        const sheetPlan = wp.plan_by_day && wp.plan_by_day[key]; // the number typed on the sheet's Monthly View for this day
        const n = sheetPlan != null ? Number(sheetPlan) : planned + crew.filter(c => (!c.start_date || c.start_date <= key) && (!c.end_date || c.end_date >= key) && !excl.includes(`crew_${c.id}`)).length;
        map[key] = (map[key] || 0) + n;
        cur.setDate(cur.getDate() + 1);
        guard += 1;
      }
    });
    return map;
    // eslint-disable-next-line
  }, [workPeriods]);

  const fieldCrewCapacity = React.useMemo(() => {
    const fieldRoles = ['field_employee', 'foreman', 'millwright', 'millwright_foreman', 'apprentice', 'labourer', 'crew'];
    return (resources.crew || []).filter(p => fieldRoles.includes(p.role?.toLowerCase())).length;
  }, [resources.crew]);

  // Dispatch-sheet totals per day (unique millwright names on each day tab) + "Current Manpower: N" from the Monthly View
  const [sheetTotals, setSheetTotals] = useState({ headcount: null, days: {} });
  useEffect(() => {
    const days = viewMode === 'month' ? getMonthDays() : getExtendedDateRange();
    if (!days.length) return;
    const token = localStorage.getItem('token');
    const tailEnd = new Date(days[days.length - 1]); tailEnd.setDate(tailEnd.getDate() + 7); // bottom tabs run 7 days into next month
    axios.get(`${backendUrl}/api/manpower/day-totals`, { params: { start: formatDate(days[0]), end: formatDate(tailEnd) }, headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSheetTotals(r.data || { headcount: null, days: {} }))
      .catch(() => {});
    // eslint-disable-next-line
  }, [currentDate, viewMode, workPeriods.length]);
  const crewHeadcount = sheetTotals.headcount || fieldCrewCapacity;
  // Numbers shown on the header + bottom tabs: the sheet's Monthly View total where it has one, else the Ops bars added up
  const headerCounts = React.useMemo(() => {
    const m = { ...manpowerByDay };
    Object.entries(sheetTotals.days || {}).forEach(([k, v]) => { if (v && v.plan_total != null) m[k] = v.plan_total; });
    return m;
  }, [manpowerByDay, sheetTotals]);

  // Helper function to sort resources by priority: Trucks > Trailers/Welders > Crew
  const sortResourcesByPriority = (resources) => {
    if (!resources || !Array.isArray(resources)) return [];
    
    return resources.sort((a, b) => {
      const getPriority = (resource) => {
        const type = resource.type?.toLowerCase() || '';
        if (type.includes('truck')) return 1;
        if (type.includes('trailer') || type.includes('welder')) return 2;
        if (type.includes('crew') || type.includes('employee')) return 3;
        return 4;
      };
      
      return getPriority(a) - getPriority(b);
    });
  };

  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, item, itemType) => {
    console.log('🎯 Drag started:', item.full_name || item.name, itemType);
    
    // Mark that user is interacting to prevent auto-refresh
    isUserInteracting.current = true;
    
    setDraggedItem(item);
    setDraggedItemType(itemType);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'resource',
      item: item,
      itemType: itemType
    }));
    e.dataTransfer.setData('text/plain', ''); // Required for drag and drop to work
  };
  
  const handleDragEnd = () => {
    console.log('🎯 Drag ended');
    setDragHover(null);
    // Clear interaction flag after a short delay
    setTimeout(() => {
      isUserInteracting.current = false;
      console.log('✅ User interaction flag cleared');
    }, 1000);
  };

  // Track which row (project) and column (date) the drag is currently over so the
  // grid can show a crosshair highlight. Also flags conflicts for the hovered cell.
  const handleCellDragEnter = (project, day) => {
    if (!draggedItem && !isDraggingWorkPeriod) return;
    const projectKey = project.id || project.project_number;
    const dateKey = formatDate(day);
    let conflict = false;
    if (draggedItem && draggedItemType) {
      const d10 = (s) => (s == null ? '' : String(s).slice(0, 10));
      const existingPeriod = workPeriods.find(wp =>
        (wp.project_id === projectKey || wp.project_id === project.project_number) &&
        d10(wp.start_date) <= dateKey && d10(wp.end_date) >= dateKey
      );
      const range = existingPeriod
        ? [new Date(existingPeriod.start_date), new Date(existingPeriod.end_date)]
        : [new Date(dateKey), new Date(dateKey)];
      conflict = hasScheduleConflict(draggedItem.id, draggedItemType, range[0], range[1]);
    }
    setDragHover(prev =>
      (prev && prev.projectKey === projectKey && prev.dateKey === dateKey && prev.conflict === conflict)
        ? prev
        : { projectKey, dateKey, conflict }
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (isDraggingWorkPeriod) {
      e.dataTransfer.dropEffect = 'move';
    } else if (draggedItem && draggedItemType) {
      e.dataTransfer.dropEffect = 'copy';
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDragLeave = (e) => {
    // Crosshair highlight is driven by React state (dragHover); no per-cell style to reset.
  };

  const handleDropOnCalendar = async (e, projectId, date) => {
    e.preventDefault();
    setDragHover(null);
    if (!canEdit) return;
    
    // Handle work period dragging first
    if (isDraggingWorkPeriod && draggedWorkPeriod) {

      try {
        await moveWorkPeriodToDate(draggedWorkPeriod, date);
        setIsDraggingWorkPeriod(false);
        setDraggedWorkPeriod(null);
        return;
      } catch (error) {
        console.error('Error moving work period:', error);
        setIsDraggingWorkPeriod(false);
        setDraggedWorkPeriod(null);
        return;
      }
    }
    
    // Try to handle work period drag via data transfer as fallback
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (dragData.type === 'workPeriod') {

        await moveWorkPeriodToDate(dragData.workPeriod, date);
        setIsDraggingWorkPeriod(false);
        setDraggedWorkPeriod(null);
        return;
      }
    } catch (error) {
      // Not a work period drag, continue with resource drag logic
    }
    
    // Handle resource dragging
    if (!draggedItem || !draggedItemType) {

      return;
    }



    try {
      // Check if there's an existing work period for this project and date
      // Compare dates as strings in YYYY-MM-DD format to avoid timezone issues
      const dateStr = formatDate(date);
      const existingPeriod = workPeriods.find(wp => 
        wp.project_id === projectId && 
        wp.start_date <= dateStr && 
        wp.end_date >= dateStr
      );

      if (existingPeriod) {

        
        // Check for resource availability conflict
        const startDate = new Date(existingPeriod.start_date);
        const endDate = new Date(existingPeriod.end_date);
        
        if (hasScheduleConflict(draggedItem.id, draggedItemType, startDate, endDate, existingPeriod.id)) {
          const resourceName = draggedItem.full_name || draggedItem.equipment_name || draggedItem.name;
          const conflictDetails = getScheduleConflictDetails(draggedItem.id, draggedItemType, startDate, endDate, existingPeriod.id);

          // Show confirmation modal for reassignment
          setConflictData({
            resource: draggedItem,
            resourceType: draggedItemType,
            resourceName,
            targetProject: projects.find(p => p.id === projectId),
            targetDate: date,
            targetWorkPeriod: existingPeriod,
            conflicts: conflictDetails
          });
          setShowConflictModal(true);
          return;
        }
        
        // Check if resource already exists in this work period
        const resourceAlreadyExists = existingPeriod.resources?.some(resource => 
          resource.id === draggedItem.id && resource.type === draggedItemType
        );
        
        if (resourceAlreadyExists) {
          toast.warning(`${draggedItem.full_name || draggedItem.equipment_name || draggedItem.name} is already assigned to this work period`);
          setDraggedItem(null);
          setDraggedItemType(null);
          return;
        }
        
        await updateExistingWorkPeriodWithResource(existingPeriod, draggedItem, draggedItemType);
      } else {

        
        // Check for conflicts when creating new single-day work period
        const endDate = new Date(date); // Same day initially
        if (hasScheduleConflict(draggedItem.id, draggedItemType, date, endDate)) {
          const resourceName = draggedItem.full_name || draggedItem.equipment_name || draggedItem.name;
          const conflictDetails = getScheduleConflictDetails(draggedItem.id, draggedItemType, date, endDate);

          // Show confirmation modal for reassignment (creating new work period case)
          setConflictData({
            resource: draggedItem,
            resourceType: draggedItemType,
            resourceName,
            targetProject: projects.find(p => p.id === projectId),
            targetDate: date,
            targetWorkPeriod: null, // Will create new work period
            conflicts: conflictDetails,
            isNewWorkPeriod: true // Flag to indicate we need to create a new WP
          });
          setShowConflictModal(true);
          return;
        }
        
        // Create new work period
        await createWorkPeriod(projectId, date, draggedItem, draggedItemType);
        
        // Work period should now be visible immediately via optimistic UI update

      }

      const resourceName = draggedItem.full_name || draggedItem.equipment_name || draggedItem.name;
      toast.success(`${resourceName} assigned successfully`);
      
      // REMOVED: fetchScheduleData() call that was causing work periods to disappear
      // The optimistic UI update in updateExistingWorkPeriodWithResource is sufficient
      // and fetchResources() is already called within that function to update availability
    } catch (error) {
      console.error('Error handling calendar drop:', error);
      console.error('Error details:', error.message);
      toast.error(`Failed to assign resource: ${error.message}`);
    }

    setDraggedItem(null);
    setDraggedItemType(null);
  };

  const handleConfirmReassignment = async () => {
    if (!conflictData) return;
    
    try {
      const { resource, resourceType, targetWorkPeriod, conflicts, targetDate, targetProject } = conflictData;
      const dateStr = formatDate(targetDate);
      
      // Validate required data
      if (!targetProject) {
        toast.error('Target project not found. Please try again.');
        setShowConflictModal(false);
        setConflictData(null);
        return;
      }
      
      console.log('🔧 Starting reassignment:', {
        resource: resource.full_name || resource.name,
        resourceType,
        targetDate: dateStr,
        targetProject: targetProject.project_number,
        conflicts: conflicts.length,
        isNewWorkPeriod: conflictData.isNewWorkPeriod
      });
      
      // Add exclusion to conflicting work periods for this specific date
      for (const conflict of conflicts) {
        const conflictWP = workPeriods.find(wp => wp.id === conflict.workPeriodId);
        if (!conflictWP) {
          console.warn('⚠️ Conflict work period not found:', conflict.workPeriodId);
          continue;
        }
        
        console.log('📝 Adding exclusion to:', conflictWP.id, conflict.projectName);
        
        // Get existing excluded_dates or create new object
        const excludedDates = conflictWP.excluded_dates || {};
        
        // Add this resource to the excluded list for this date with target project info
        if (!excludedDates[dateStr]) {
          excludedDates[dateStr] = [];
        }
        
        const resourceKey = `${resourceType}_${resource.id}`;
        const targetProjectNumber = targetProject.project_number || 'Unknown';
        
        // Store as "resourceKey|targetProjectNumber" to include where they were reassigned
        const exclusionEntry = `${resourceKey}|${targetProjectNumber}`;
        
        // Check if this resource is already excluded (remove old entry first)
        excludedDates[dateStr] = excludedDates[dateStr].filter(e => !e.startsWith(resourceKey + '|') && e !== resourceKey);
        excludedDates[dateStr].push(exclusionEntry);
        
        console.log('📤 Updating work period with exclusions:', excludedDates);
        
        // Update work period with exclusion
        const response = await axios.put(
          `${backendUrl}/api/schedule/work-periods/${conflictWP.id}`,
          { excluded_dates: excludedDates },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ Exclusion added successfully');
      }
      
      // Add resource to target work period (or create new one)
      if (conflictData.isNewWorkPeriod) {
        console.log('🆕 Creating new work period');
        // Create new work period with this resource
        const newWP = await axios.post(
          `${backendUrl}/api/schedule/work-periods`,
          {
            project_id: targetProject.id,
            start_date: formatDate(targetDate),
            end_date: formatDate(targetDate),
            start_time: "07:00",
            end_time: "15:00",
            resources: [{
              type: resourceType,
              id: resource.id,
              name: resource.full_name || resource.equipment_name || resource.name
            }]
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ New work period created:', newWP.data.id);
      } else {
        console.log('➕ Adding to existing work period');
        
        // Validate target work period
        if (!targetWorkPeriod) {
          toast.error('Target work period not found. Please try again.');
          setShowConflictModal(false);
          setConflictData(null);
          return;
        }
        
        // Add to existing work period
        const updatedResources = [...(targetWorkPeriod.resources || []), {
          type: resourceType,
          id: resource.id,
          name: resource.full_name || resource.equipment_name || resource.name
        }];
        
        await axios.put(
          `${backendUrl}/api/schedule/work-periods/${targetWorkPeriod.id}`,
          { resources: updatedResources },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ Resource added to existing work period');
      }
      
      toast.success(`${conflictData.resourceName} scheduled on new job. Original job shows exclusion.`);
      setShowConflictModal(false);
      setConflictData(null);
      setDraggedItem(null);
      setDraggedItemType(null);
      
      // Refresh data with force replace to update exclusions
      await fetchScheduleData(true);
      await fetchResources();
    } catch (error) {
      console.error('❌ Error reassigning resource:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error(`Failed to reassign resource: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Take one day out of a bar (e.g. a Sunday): the backend trims the bar and carries on with a copy from the day after,
  // keeping the crew, request link and notes. Sheet-mirrored bars are refused — their days come from the sheet.
  const handleExcludeDay = async (workPeriod, excludedDay) => {
    const excludedDate = formatDate(excludedDay);
    try {
      await axios.post(
        `${backendUrl}/api/schedule/work-periods/${workPeriod.id}/exclude-day`,
        { date: excludedDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${excludedDate} taken out — the bar now skips that day.`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) toast.warning(detail, { duration: 14000 });
      else toast.error(detail || `Failed to exclude day: ${error.message}`);
    }
    await fetchScheduleData(true, true);
  };

  const createWorkPeriod = async (projectId, startDate, resource, resourceType) => {
    const workPeriodData = {
      project_id: projectId,
      start_date: formatDate(startDate),
      end_date: formatDate(startDate), // Initially one day
      start_time: "07:00", // Default 7:00 AM
      end_time: "15:00",   // Default 3:00 PM (8 hours)
      resources: [{
        type: resourceType,
        id: resource.id,
        name: resource.full_name || resource.equipment_name || resource.name
      }]
    };



    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const response = await axios.post(`${backendUrl}/api/schedule/work-periods`, workPeriodData, { headers });

      
      // CRITICAL FIX: Add optimistic UI update to immediately show the created work period
      const newWorkPeriod = {
        id: response.data.id,
        ...workPeriodData,
        // Ensure we have a proper date format
        start_date: formatDate(startDate),
        end_date: formatDate(startDate)
      };
      

      setWorkPeriods(prevWorkPeriods => {
        const updatedWorkPeriods = [...prevWorkPeriods, newWorkPeriod];

        return updatedWorkPeriods;
      });
      
      return response.data;
    } catch (error) {
      console.error('Work period creation error:', error.response?.data || error);
      throw new Error(error.response?.data?.detail || 'Failed to create work period');
    }
  };

  const moveWorkPeriodToDate = async (workPeriod, targetDate) => {
    // FIXED: Normalize target date to avoid timezone issues
    const normalizedTargetDate = new Date(targetDate);
    normalizedTargetDate.setUTCHours(12, 0, 0, 0); // Set to noon UTC to avoid timezone shifts

    try {
      // Calculate the duration of the work period
      const startDate = new Date(workPeriod.start_date);
      const endDate = new Date(workPeriod.end_date);
      const durationDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

      // Set new dates using normalized target date
      const newStartDate = new Date(normalizedTargetDate);
      const newEndDate = new Date(normalizedTargetDate);
      newEndDate.setDate(newEndDate.getDate() + durationDays);

      // Check for potential merge with identical work periods on same project
      const otherWorkPeriods = workPeriods.filter(wp => wp.id !== workPeriod.id && wp.project_id === workPeriod.project_id);
      console.log('🔍 Checking for merge opportunities:', {
        currentWP: `${formatDate(newStartDate)} to ${formatDate(newEndDate)}`,
        otherWPsOnSameProject: otherWorkPeriods.length
      });
      
      for (const otherWP of otherWorkPeriods) {
        const otherStart = new Date(otherWP.start_date);
        const otherEnd = new Date(otherWP.end_date);
        
        // Check if work periods will overlap or be adjacent after move
        const willOverlapOrAdjacent = (newStartDate <= new Date(otherEnd.getTime() + 86400000)) && 
                                       (newEndDate >= new Date(otherStart.getTime() - 86400000));
        
        console.log('  Checking WP:', {
          dates: `${formatDate(otherStart)} to ${formatDate(otherEnd)}`,
          willOverlapOrAdjacent: willOverlapOrAdjacent
        });
        
        if (willOverlapOrAdjacent) {
          // Check if resources are identical
          const resourcesMatch = (
            workPeriod.resources.length === otherWP.resources.length &&
            workPeriod.resources.every(r1 => 
              otherWP.resources.some(r2 => r1.id === r2.id && r1.type === r2.type)
            )
          );
          
          console.log('    Resources match:', resourcesMatch, {
            wp1Count: workPeriod.resources.length,
            wp2Count: otherWP.resources.length
          });
          
          if (resourcesMatch) {
            console.log('✅ Detected merge opportunity with identical work period');
            
            // Confirm merge
            const confirmMerge = window.confirm(
              `Merge these work periods?\n\n` +
              `Period 1: ${formatDate(workPeriod.start_date)} to ${formatDate(workPeriod.end_date)}\n` +
              `Period 2: ${formatDate(otherWP.start_date)} to ${formatDate(otherWP.end_date)}\n\n` +
              `Result: Merged period covering all days with same resources.`
            );
            
            if (confirmMerge) {
              // Calculate merged date range
              const mergedStart = new Date(Math.min(newStartDate, otherStart));
              const mergedEnd = new Date(Math.max(newEndDate, otherEnd));
              
              console.log('🔗 Merging work periods:', {
                period1: `${formatDate(newStartDate)} to ${formatDate(newEndDate)}`,
                period2: `${formatDate(otherStart)} to ${formatDate(otherEnd)}`,
                merged: `${formatDate(mergedStart)} to ${formatDate(mergedEnd)}`
              });
              
              // Update the first work period with merged dates
              await axios.put(
                `${backendUrl}/api/schedule/work-periods/${workPeriod.id}`,
                {
                  start_date: formatDate(mergedStart),
                  end_date: formatDate(mergedEnd),
                  start_time: workPeriod.start_time,
                  end_time: workPeriod.end_time,
                  resources: workPeriod.resources,
                  foreman_id: workPeriod.foreman_id
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              // Delete the second work period
              try {
                await axios.delete(
                  `${backendUrl}/api/schedule/work-periods/${otherWP.id}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
              } catch (deleteError) {
                if (deleteError.response?.status === 404) {
                  console.log('⚠️ Work period already deleted, continuing...');
                } else {
                  throw deleteError;
                }
              }
              
              toast.success('Work periods merged successfully!');
              await fetchScheduleData(true);
              return; // Exit early after merge
            } else {
              toast.info('Merge cancelled');
              return; // Exit without moving
            }
          }
        }
      }
      
      // Check for resource conflicts in the new time period
      if (workPeriod.resources && workPeriod.resources.length > 0) {
        const conflictingResources = [];
        
        for (const resource of workPeriod.resources) {
          // Only check conflicts with OTHER work periods (exclude current one being moved)
          const resourceScheduledPeriods = [];
          
          otherWorkPeriods.forEach(wp => {
            if (wp.resources && wp.resources.some(r => r.id === resource.id && r.type === resource.type)) {
              resourceScheduledPeriods.push({
                start: new Date(wp.start_date),
                end: new Date(wp.end_date),
                project_id: wp.project_id,
                workPeriodId: wp.id
              });
            }
          });
          
          // Check if new dates conflict with existing scheduled periods
          const hasConflict = resourceScheduledPeriods.some(period => {
            return (newStartDate <= period.end && newEndDate >= period.start);
          });
          
          if (hasConflict) {
            conflictingResources.push(resource.name);
          }
        }
        
        if (conflictingResources.length > 0) {
          const resourceNames = conflictingResources.join(', ');

          toast.error(`Cannot move work period: ${resourceNames} ${conflictingResources.length > 1 ? 'are' : 'is'} already scheduled during the target period.`);
          return;
        }
      }

      const updateData = {
        start_date: formatDate(newStartDate),
        end_date: formatDate(newEndDate),
        start_time: workPeriod.start_time,
        end_time: workPeriod.end_time,
        resources: workPeriod.resources || []
      };



      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await axios.put(
        `${backendUrl}/api/schedule/work-periods/${workPeriod.id}`,
        updateData,
        { headers }
      );


      toast.success('Work period moved successfully');
      fetchScheduleData(true); // Refresh the display with force replace

    } catch (error) {
      console.error('❌ Error moving work period:', error);
      toast.error(`Failed to move work period: ${error.response?.data?.detail || error.message}`);
    }
  };

  const updateExistingWorkPeriodWithResource = async (existingWorkPeriod, draggedItem, draggedItemType) => {

    
    // Get current work period from state to avoid stale reference issues from resize
    const currentWorkPeriods = workPeriods;
    const currentWorkPeriod = currentWorkPeriods.find(wp => wp.id === existingWorkPeriod.id);
    
    if (!currentWorkPeriod) {
      console.error('🔵 Work period not found in current state - may have been corrupted by resize');
      throw new Error('Work period not found in current state');
    }
    

    
    // Create the new resource object
    const newResource = { 
      type: draggedItemType, 
      id: draggedItem.id, 
      name: draggedItem.full_name || draggedItem.equipment_name || draggedItem.name
    };
    

    
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      

      const response = await axios.post(`${backendUrl}/api/schedule/work-periods/${existingWorkPeriod.id}/resources`, newResource, { headers });

      
      // Instead of full refresh, use optimistic UI update to prevent work period disappearing

      
      // Get current work period from state (not stale reference) to avoid resize corruption
      setWorkPeriods(prevWorkPeriods => {
        return prevWorkPeriods.map(wp => {
          if (wp.id === existingWorkPeriod.id) {
            // Clean up any resize-related properties that might corrupt state
            const cleanWorkPeriod = { ...wp };
            delete cleanWorkPeriod.isResizing;
            delete cleanWorkPeriod.originalWidth;
            delete cleanWorkPeriod.originalStartDate;
            delete cleanWorkPeriod.originalEndDate;
            
            const updatedResources = [...(cleanWorkPeriod.resources || []), newResource];


            
            return { ...cleanWorkPeriod, resources: updatedResources };
          }
          return wp;
        });
      });
      
      // Also refresh resources to update availability status
      fetchResources();
      
      return response.data;
    } catch (error) {
      console.error('🔵 Error adding resource to work period:', error.response?.data || error);
      
      if (error.response?.status === 401) {

        localStorage.removeItem('token');
        toast.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
        return;
      }
      
      throw new Error(error.response?.data?.detail || 'Failed to add resource to work period');
    }
  };

  const navigateWeek = useCallback((direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + (direction * 7));
      return newDate;
    });
  }, []);

  const navigateMonth = useCallback((direction) => {
    // Page by the window itself (4 weeks) so the arrows never skip or repeat days
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + direction * SCHEDULE_WINDOW_DAYS);
      return newDate;
    });
  }, []);

  const navigate = (direction) => {
    navigateMonth(direction);
  };

  // Open the bar that matters now — running today, else the next one up, else the most recent — and only move
  // the window when that bar is off screen (jumping to the last bar months out looked like the schedule had emptied)
  const focusProjectWorkPeriod = (projectWPs) => {
    if (!projectWPs.length) return;
    const today = ymdLocal(new Date());
    const live = projectWPs.find(wp => wp.start_date <= today && wp.end_date >= today);
    const upcoming = projectWPs.filter(wp => wp.start_date > today).sort((a, b) => a.start_date.localeCompare(b.start_date))[0];
    const past = projectWPs.filter(wp => wp.end_date < today).sort((a, b) => b.end_date.localeCompare(a.end_date))[0];
    const wp = live || upcoming || past;
    if (wp.start_date > windowEndKey || wp.end_date < windowStartKey) setCurrentDate(new Date(`${wp.start_date}T12:00:00`));
    setSelectedWorkPeriod(wp);
    setIsDetailModalOpen(true);
  };

  const getWorkPeriodsForProject = (projectNumber) => {
    return workPeriods.filter(wp => wp.project_id === projectNumber);
  };

  const getWorkPeriodForDate = (projectNumber, dateKey) => {
    return workPeriods.find(wp => 
      wp.project_id === projectNumber && 
      wp.start_date <= dateKey && 
      wp.end_date >= dateKey
    );
  };

  const getWorkPeriodDuration = (workPeriod, weekStart) => {
    if (!workPeriod) return 1;
    const startDate = new Date(workPeriod.start_date);
    const endDate = new Date(workPeriod.end_date);
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  };

  // Work period resize functionality
  const handleResizeStart = (e, workPeriod, direction) => {



    
    e.preventDefault();
    e.stopPropagation();

    
    setIsResizing(true);
    setJustResized(false);
    
    const startX = e.clientX;
    const originalStartDate = new Date(workPeriod.start_date);
    const originalEndDate = new Date(workPeriod.end_date);
    
    // Calculate actual pixels per day from the calendar grid
    const calendarGrid = document.querySelector('.calendar-grid, [class*="grid"], [class*="week"]');
    let pixelsPerDay = 50; // Fallback default
    
    if (calendarGrid) {
      const gridRect = calendarGrid.getBoundingClientRect();
      pixelsPerDay = gridRect.width / 7; // 7 days per week

    } else {

    }
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const daysDelta = Math.round(deltaX / pixelsPerDay); // Use actual calculated pixels per day
      

      
      let newStartDate = new Date(originalStartDate);
      let newEndDate = new Date(originalEndDate);
      
      if (direction === 'left') {
        // Resize from the left (change ONLY start date, keep end date fixed)
        // Make this exactly like right resize but in reverse direction
        newEndDate = new Date(originalEndDate); // Keep end date unchanged
        newStartDate.setDate(originalStartDate.getDate() + daysDelta);
        

        
        // Don't let start date go past end date (same validation as right side)
        if (newStartDate >= newEndDate) {
          newStartDate = new Date(newEndDate);
          newStartDate.setDate(newStartDate.getDate() - 1);
        }
      } else {
        // Resize from the right (change ONLY end date, keep start date fixed)
        newStartDate = new Date(originalStartDate); // Keep start date unchanged
        newEndDate.setDate(originalEndDate.getDate() + daysDelta);
        

        
        // Don't let end date go before start date
        if (newEndDate <= newStartDate) {
          newEndDate = new Date(newStartDate);
          newEndDate.setDate(newEndDate.getDate() + 1);
        }
      }
      
      // Visual feedback during resize - UPDATE BOTH DATES for proper width calculation
      setWorkPeriods(currentWorkPeriods => 
        currentWorkPeriods.map(wp => 
          wp.id === workPeriod.id 
            ? { 
                ...wp, 
                // Always update both dates for correct visual width calculation
                start_date: formatDate(newStartDate),
                end_date: formatDate(newEndDate),
                isResizing: true
              }
            : wp
        )
      );
    };
    
    const handleMouseUp = async (e) => {

      
      const deltaX = e.clientX - startX;
      
      // Use the same pixels per day calculation as mouse move
      const rawDaysDelta = deltaX / pixelsPerDay;
      
      // Determine if we should snap to half-day or full-day
      const wholeDays = Math.floor(rawDaysDelta);
      const fractionalPart = rawDaysDelta - wholeDays;
      
      let snappedDaysDelta;
      if (Math.abs(fractionalPart) < 0.25) {
        // Snap to whole day (beginning)
        snappedDaysDelta = wholeDays;
      } else if (Math.abs(fractionalPart) < 0.75) {
        // Snap to half day (middle) - but since we can't store half days, round to nearest whole
        snappedDaysDelta = fractionalPart > 0 ? wholeDays + 1 : wholeDays;
      } else {
        // Snap to next whole day (end)
        snappedDaysDelta = fractionalPart > 0 ? wholeDays + 1 : wholeDays;
      }
      
      const finalDaysDelta = Math.round(snappedDaysDelta);
      

      
      if (Math.abs(finalDaysDelta) > 0) {
        let newStartDate = new Date(originalStartDate);
        let newEndDate = new Date(originalEndDate);
        
        if (direction === 'left') {
          // Resize from the left (change ONLY start date, keep end date fixed)
          // Make this exactly like right resize but in reverse direction
          newEndDate = new Date(originalEndDate); // Keep end date unchanged  
          newStartDate.setDate(originalStartDate.getDate() + finalDaysDelta);
          
          if (newStartDate >= newEndDate) {

            newStartDate = new Date(newEndDate);
            newStartDate.setDate(newStartDate.getDate() - 1);
          }
        } else {
          // Resize from the right (change ONLY end date, keep start date fixed)
          newStartDate = new Date(originalStartDate); // Keep start date unchanged
          newEndDate.setDate(originalEndDate.getDate() + finalDaysDelta);
          
          if (newEndDate <= newStartDate) {

            newEndDate = new Date(newStartDate);
            newEndDate.setDate(newEndDate.getDate() + 1);
          }
        }
        
        // Safety check - ensure dates are valid
        if (newStartDate >= newEndDate) {
          console.error('🔄 Invalid date range detected, aborting resize');
          return;
        }
        

        
        // Update the work period on the backend
        try {
          // CRITICAL FIX: Always send both dates to backend for proper duration calculation
          const updateData = {
            project_id: workPeriod.project_id,
            start_date: formatDate(newStartDate),
            end_date: formatDate(newEndDate),
            start_time: workPeriod.start_time,
            end_time: workPeriod.end_time,
            resources: workPeriod.resources || [],
            notes: workPeriod.notes || ''
          };
          
          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };
          
          const response = await axios.put(`${backendUrl}/api/schedule/work-periods/${workPeriod.id}`, updateData, { headers });


          
          // Update local state with confirmed backend data
          setWorkPeriods(currentWorkPeriods => 
            currentWorkPeriods.map(wp => 
              wp.id === workPeriod.id 
                ? { ...wp, ...updateData, isResizing: false }
                : wp
            )
          );
          
          // Calculate actual duration change for user feedback
          const originalDuration = Math.ceil((originalEndDate - originalStartDate) / (1000 * 60 * 60 * 24)) + 1;
          const newDuration = Math.ceil((newEndDate - newStartDate) / (1000 * 60 * 60 * 24)) + 1;
          const durationChange = newDuration - originalDuration;
          

          
          // Check if the work period is now outside the current week view
          const weekStart = getWeekStart(currentDate);
          const weekEnd = getWeekEnd(currentDate);
          
          if (newStartDate > weekEnd || newEndDate < weekStart) {
            toast.success(`Work period resized by ${Math.abs(durationChange)} day(s) and moved outside current week view`);
          } else {
            const changeText = durationChange > 0 ? 'extended' : durationChange < 0 ? 'shortened' : 'adjusted';
            toast.success(`Work period ${changeText} by ${Math.abs(durationChange)} day(s) - now ${newDuration} day(s) total`);
          }
          
          // DO NOT call fetchScheduleData() - keep the visual position as-is
          
        } catch (error) {
          console.error('🔄 Error resizing work period:', error);
          toast.error(error.response?.data?.detail || 'Failed to resize work period', { duration: error.response?.status === 409 ? 14000 : 5000 });
          await fetchScheduleData(true, true); // snap the bar back to what the server has
        }
      }
      
      // Clean up resize state
      setIsResizing(false);
      setJustResized(true);
      
      // Clear the "just resized" flag after a short delay to allow click prevention
      setTimeout(() => {
        setJustResized(false);
      }, 100);
      
      // Clean up the isResizing flag from the work period
      setWorkPeriods(currentWorkPeriods => 
        currentWorkPeriods.map(wp => 
          wp.id === workPeriod.id 
            ? { ...wp, isResizing: false }
            : wp
        )
      );
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    // FIXED: Add specific event listeners for resize operation
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    setIsResizing(true);
    setIsDraggingWorkPeriod(true);
    setDraggedWorkPeriod(workPeriod);
  };
  // BULLETPROOF RESIZE SYSTEM - Stable, full-day snapping, no data loss
  // FIX: Proper cleanup for mouse event listeners to prevent memory leaks
  useEffect(() => {
    // Global mouse event handlers for drag operations - optimized to prevent performance issues
    const globalMouseMove = (e) => {
      // Handle mouse move events for various drag operations
      if (isDraggingWorkPeriod) {
        // Throttle mouse position updates to prevent excessive renders
        const now = Date.now();
        if (!globalMouseMove.lastUpdate || now - globalMouseMove.lastUpdate > 16) { // ~60fps
          setMousePosition({ x: e.clientX, y: e.clientY });
          globalMouseMove.lastUpdate = now;
        }
      }
    };

    const globalMouseUp = (e) => {
      // Clean up any ongoing drag operations
      if (isDraggingWorkPeriod) {
        setIsDraggingWorkPeriod(false);
        setDraggedWorkPeriod(null);
      }
      if (isResizing) {
        setIsResizing(false);
        setJustResized(true);
        setTimeout(() => setJustResized(false), 100);
      }
    };

    // Only add listeners when actually dragging to reduce overhead
    if (isDraggingWorkPeriod || isResizing) {
      document.addEventListener('mousemove', globalMouseMove);
      document.addEventListener('mouseup', globalMouseUp);
    }

    // Cleanup function
    return () => {
      document.removeEventListener('mousemove', globalMouseMove);
      document.removeEventListener('mouseup', globalMouseUp);
    };
  }, [isDraggingWorkPeriod, isResizing]); // Dependencies ensure proper cleanup

  // REMOVED INLINE EVENT LISTENER ADDITIONS - Now handled by useEffect above
  const handleBulletproofResize = (e, workPeriod, direction) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEdit) return;
    
    setIsResizing(true);
    setJustResized(false);
    
    const startX = e.clientX;
    const originalStartDate = new Date(workPeriod.start_date);
    const originalEndDate = new Date(workPeriod.end_date);
    
    // Calculate pixels per day more accurately based on actual calendar cell width
    const getPixelsPerDay = () => {
      const calendarCell = document.querySelector('[data-date]');
      if (calendarCell) {
        const cellRect = calendarCell.getBoundingClientRect();
        return cellRect.width;
      }
      return 100; // Fallback
    };
    
    const pixelsPerDay = getPixelsPerDay();

    
    // Store original work period for revert on error
    const originalWorkPeriod = { ...workPeriod };
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const daysDelta = Math.round(deltaX / pixelsPerDay);
      
      let newStartDate = new Date(originalStartDate);
      let newEndDate = new Date(originalEndDate);
      
      if (direction === 'left') {
        // Left resize: adjust start date, keep end date
        newStartDate.setDate(originalStartDate.getDate() + daysDelta);
        newEndDate = new Date(originalEndDate); // Keep original end date
        
        // Allow same-day work periods (start_date == end_date)
        if (newStartDate > newEndDate) {
          newStartDate = new Date(newEndDate); // Same day is OK
        }
      } else {
        // Right resize: adjust end date, keep start date  
        newStartDate = new Date(originalStartDate); // Keep original start date
        newEndDate.setDate(originalEndDate.getDate() + daysDelta);
        
        // Allow same-day work periods (start_date == end_date)
        if (newEndDate < newStartDate) {
          newEndDate = new Date(newStartDate); // Same day is OK
        }
      }
      
      // Visual feedback during resize
      setWorkPeriods(currentWorkPeriods => 
        currentWorkPeriods.map(wp => 
          wp.id === workPeriod.id 
            ? { 
                ...wp, 
                start_date: formatDate(newStartDate),
                end_date: formatDate(newEndDate),
                isResizing: true
              }
            : wp
        )
      );
    };
    
    const handleMouseUp = async (e) => {
      console.log('🎯 Resize handleMouseUp called', { direction, startX, clientX: e.clientX });
      
      const deltaX = e.clientX - startX;
      const daysDelta = Math.round(deltaX / pixelsPerDay);
      
      console.log('📏 Resize delta:', { deltaX, daysDelta, pixelsPerDay });
      
      if (Math.abs(daysDelta) > 0) {
        let newStartDate = new Date(originalStartDate);
        let newEndDate = new Date(originalEndDate);
        
        if (direction === 'left') {
          newStartDate.setDate(originalStartDate.getDate() + daysDelta);
          newEndDate = new Date(originalEndDate);
          
          // Allow same-day work periods
          if (newStartDate > newEndDate) {
            newStartDate = new Date(newEndDate);
          }
        } else {
          newStartDate = new Date(originalStartDate);
          newEndDate.setDate(originalEndDate.getDate() + daysDelta);
          
          // Allow same-day work periods
          if (newEndDate < newStartDate) {
            newEndDate = new Date(newStartDate);
          }
        }
        
        // Validate date range - allow same day (start == end)
        if (newStartDate > newEndDate) {
          console.error('🔧 Invalid date range, aborting resize');
          toast.error('Invalid date range');
          // Revert to original
          setWorkPeriods(currentWorkPeriods => 
            currentWorkPeriods.map(wp => 
              wp.id === workPeriod.id ? { ...originalWorkPeriod, isResizing: false } : wp
            )
          );
          setIsResizing(false);
          setJustResized(false);
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          return;
        }
        
        // Check for potential merge with identical work periods on same project
        const otherWorkPeriods = workPeriods.filter(wp => wp.id !== workPeriod.id && wp.project_id === workPeriod.project_id);
        console.log('🔍 Resize: Checking for merge opportunities');
        
        for (const otherWP of otherWorkPeriods) {
          const otherStart = new Date(otherWP.start_date);
          const otherEnd = new Date(otherWP.end_date);
          
          // Check if work periods will overlap or be adjacent after resize
          const willOverlapOrAdjacent = (newStartDate <= new Date(otherEnd.getTime() + 86400000)) && 
                                         (newEndDate >= new Date(otherStart.getTime() - 86400000));
          
          if (willOverlapOrAdjacent) {
            // Check if resources are identical
            const resourcesMatch = (
              workPeriod.resources.length === otherWP.resources.length &&
              workPeriod.resources.every(r1 => 
                otherWP.resources.some(r2 => r1.id === r2.id && r1.type === r2.type)
              )
            );
            
            if (resourcesMatch) {
              console.log('✅ Resize detected merge opportunity');
              
              // Stop resizing
              setIsResizing(false);
              setJustResized(false);
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
              
              // Revert to original first
              setWorkPeriods(currentWorkPeriods => 
                currentWorkPeriods.map(wp => 
                  wp.id === workPeriod.id ? { ...originalWorkPeriod, isResizing: false } : wp
                )
              );
              
              // Confirm merge
              const confirmMerge = window.confirm(
                `Merge these work periods?\n\n` +
                `Period 1: ${formatDate(newStartDate)} to ${formatDate(newEndDate)}\n` +
                `Period 2: ${formatDate(otherStart)} to ${formatDate(otherEnd)}\n\n` +
                `Result: Merged period covering all days with same resources.`
              );
              
              if (confirmMerge) {
                // Calculate merged date range
                const mergedStart = new Date(Math.min(newStartDate, otherStart));
                const mergedEnd = new Date(Math.max(newEndDate, otherEnd));
                
                console.log('🔗 Merging via resize:', {
                  merged: `${formatDate(mergedStart)} to ${formatDate(mergedEnd)}`
                });
                
                // Update the first work period with merged dates
                try {
                  await axios.put(
                    `${backendUrl}/api/schedule/work-periods/${workPeriod.id}`,
                    {
                      start_date: formatDate(mergedStart),
                      end_date: formatDate(mergedEnd),
                      start_time: workPeriod.start_time,
                      end_time: workPeriod.end_time,
                      resources: workPeriod.resources,
                      foreman_id: workPeriod.foreman_id
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  
                  // Delete the second work period
                  try {
                    await axios.delete(
                      `${backendUrl}/api/schedule/work-periods/${otherWP.id}`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                  } catch (deleteError) {
                    if (deleteError.response?.status === 404) {
                      console.log('⚠️ Work period already deleted, continuing...');
                    } else {
                      throw deleteError; // Re-throw other errors
                    }
                  }
                  
                  toast.success('Work periods merged successfully!');
                  await fetchScheduleData(true);
                } catch (error) {
                  console.error('❌ Error merging:', error);
                  toast.error('Failed to merge work periods');
                  await fetchScheduleData(true); // Refresh even on error to clear stale data
                }
              } else {
                toast.info('Merge cancelled');
              }
              
              return; // Exit after handling merge
            }
          }
        }
        
        // Check for resource conflicts in new date range
        const conflictingResources = [];
        if (workPeriod.resources && workPeriod.resources.length > 0) {
          for (const resource of workPeriod.resources) {
            if (hasScheduleConflict(resource.id, resource.type, newStartDate, newEndDate, workPeriod.id)) {
              conflictingResources.push(resource.name);
            }
          }
        }
        
        if (conflictingResources.length > 0) {

          toast.error(`Cannot resize: ${conflictingResources.join(', ')} already scheduled during this period`);
          // Revert to original
          setWorkPeriods(currentWorkPeriods => 
            currentWorkPeriods.map(wp => 
              wp.id === workPeriod.id ? { ...originalWorkPeriod, isResizing: false } : wp
            )
          );
          setIsResizing(false);
          setJustResized(false);
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          return;
        }
        
        // Update backend
        try {
          const updateData = {
            project_id: workPeriod.project_id,
            start_date: formatDate(newStartDate),
            end_date: formatDate(newEndDate),
            start_time: workPeriod.start_time,
            end_time: workPeriod.end_time,
            resources: workPeriod.resources || [],
            notes: workPeriod.notes || ''
          };
          
          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };
          

          const response = await axios.put(`${backendUrl}/api/schedule/work-periods/${workPeriod.id}`, updateData, { headers });

          
          // Update state with confirmed data
          setWorkPeriods(currentWorkPeriods => 
            currentWorkPeriods.map(wp => 
              wp.id === workPeriod.id 
                ? { ...wp, ...updateData, isResizing: false }
                : wp
            )
          );
          
          const daysChanged = Math.abs(daysDelta);
          const direction_text = direction === 'left' ? 'start' : 'end';
          toast.success(`Work period ${direction_text} date ${daysDelta > 0 ? 'extended' : 'shortened'} by ${daysChanged} day(s)`);
          
        } catch (error) {
          console.error('🔧 Resize API error:', error);
          toast.error(error.response?.data?.detail || 'Failed to resize work period', { duration: error.response?.status === 409 ? 14000 : 5000 });
          await fetchScheduleData(true, true);
          // Revert to original on error
          setWorkPeriods(currentWorkPeriods => 
            currentWorkPeriods.map(wp => 
              wp.id === workPeriod.id ? { ...originalWorkPeriod, isResizing: false } : wp
            )
          );
        }
      } else {
        // No change, just clean up
        setWorkPeriods(currentWorkPeriods => 
          currentWorkPeriods.map(wp => 
            wp.id === workPeriod.id ? { ...wp, isResizing: false } : wp
          )
        );
      }
      
      // Clean up resize state
      setIsResizing(false);
      setJustResized(true);
      
      // Clear the "just resized" flag after a short delay
      setTimeout(() => {
        setJustResized(false);
      }, 200);
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    // FIXED: Add specific event listeners for resize operation
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    setIsResizing(true);
    setIsDraggingWorkPeriod(true);
    setDraggedWorkPeriod(workPeriod);
  };

  // Full-day resize functionality - simpler and more stable
  const handleFullDayResize = (e, workPeriod, direction) => {
    // This was the previous implementation - keeping for reference
    // Current implementation uses handleBulletproofResize instead

  };

  // SMS DISPATCH SYSTEM FUNCTIONS
  const handleDispatchWorkPeriod = async (workPeriod) => {

    
    // Check if work period has crew members
    const crewMembers = workPeriod.resources?.filter(r => r.type === 'crew') || [];
    if (crewMembers.length === 0) {
      toast.error('No crew members found in this work period to dispatch');
      return;
    }
    
    // Generate the dispatch message
    const dispatchMessage = generateDispatchMessage(workPeriod);
    setDispatchMessage(dispatchMessage);
    setSelectedWorkPeriod(workPeriod);
    setShowDispatchConfirm(true);
  };
  
  const generateDispatchMessage = (workPeriod) => {
    // Get project details - check if it's a temp object first, then find in projects
    const tempObject = tempScheduleObjects.find(t => t.id === workPeriod.project_id);
    const project = tempObject || projects.find(p => p.id === workPeriod.project_id || p.project_number === workPeriod.project_id);
    
    // For temp objects, use the title as the main identifier
    const projectNumber = tempObject ? tempObject.title : (project?.project_number || project?.title || workPeriod.project_id || 'TBD');
    const siteName = tempObject ? tempObject.title : (project?.site_name || `Project ${projectNumber}`);
    const siteAddress = tempObject ? 'Shop/Special Assignment' : (workPeriod.site_address || project?.site_address || 'Address TBD');
    const siteContact = tempObject ? 'See PM' : (workPeriod.site_contact || project?.site_contact_name || 'Contact TBD');
    const workOrder = projectNumber;
    
    // Get equipment/resources list and crew members
    const equipment = workPeriod.resources?.filter(r => r.type !== 'crew').map(r => r.name) || [];
    const crewResources = workPeriod.resources?.filter(r => r.type === 'crew') || [];
    const crewMembers = crewResources.map(r => r.name) || [];
    
    // Find foreman
    const foreman = crewResources.find(r => r.id === workPeriod.foreman_id);
    const foremanText = foreman ? `\n👑 Foreman: ${foreman.name}` : '';
    
    const crewText = crewMembers.length > 0 ? `\nCrew: ${crewMembers.join(', ')}` : '';
    const equipmentText = equipment.length > 0 ? `\nEquipment: ${equipment.join(', ')}` : '';
    
    // Rental equipment info
    const rentalEquipment = workPeriod.rental_equipment || [];
    let rentalText = '';
    if (rentalEquipment.length > 0) {
      rentalText = '\n\n🚚 RENTAL EQUIPMENT:';
      rentalEquipment.forEach(rental => {
        rentalText += `\n• ${rental.equipment_name} from ${rental.vendor}`;
        if (rental.arrival_time) {
          rentalText += ` (Arriving: ${rental.arrival_time})`;
        }
      });
    }
    
    return `🔧 WORK DISPATCH - ${siteName}
📍 Location: ${siteAddress}
📞 Site Contact: ${siteContact}
🔖 Work Order: ${workOrder}
📅 Date: ${workPeriod.start_date} to ${workPeriod.end_date}
⏰ Time: ${workPeriod.start_time} - ${workPeriod.end_time}${foremanText}${crewText}${equipmentText}${rentalText}

Tap ✓ Confirm or ✗ Can't make it in the job chat.`;
  };
  
  const sendDispatch = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const dispatchRequest = {
        work_period_id: selectedWorkPeriod.id,
        work_order_number: selectedWorkPeriod.project_id,
        custom_message: dispatchMessage
      };
      

      
      const response = await axios.post(`${backendUrl}/api/dispatch/send`, dispatchRequest, { headers });
      

      toast.success(`Dispatch posted to the job chat — ${response.data.crew_dispatches} crew notified`);
      
      // Update dispatch status for this work period
      await fetchDispatchStatus(selectedWorkPeriod.id);
      
      // Close both modals
      setShowDispatchConfirm(false);
      setIsDetailModalOpen(false);
      
    } catch (error) {
      console.error('❌ Dispatch error:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to send dispatches');
      } else {
        toast.error(`Failed to send dispatch: ${error.response?.data?.detail || error.message}`);
      }
    }
  };
  
  const sendIndividualDispatch = async (workPeriod, crewId, crewName) => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const response = await axios.post(`${backendUrl}/api/dispatch/send`, {
        work_period_id: workPeriod.id,
        work_order_number: workPeriod.project_id,
        only_ids: [crewId]
      }, { headers });
      if (response.data?.crew_dispatches > 0) {
        toast.success(`Dispatch posted for ${crewName} — they've been notified`);
      } else {
        toast.error(`Could not dispatch ${crewName}`);
      }
      await fetchDispatchStatus(workPeriod.id);
    } catch (error) {
      toast.error(`Failed to dispatch ${crewName}: ${error.response?.data?.detail || error.message}`, { duration: 8000 });
    }
  };

  const fetchDispatchStatus = async (workPeriodId) => {
    try {
      if (!token) {

        return;
      }


      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const response = await axios.get(`${backendUrl}/api/dispatch/work-period/${workPeriodId}`, { headers });
      

      
      setDispatchStatuses(prev => ({
        ...prev,
        [workPeriodId]: response.data
      }));
      
    } catch (error) {
      console.error('Failed to fetch dispatch status:', error);
    }
  };
  
  // One batched call for every bar on screen (was one request per bar every 10 s → rate-limited)
  const fetchAllDispatchStatuses = async (wps) => {
    if (!token || !wps?.length) return;
    try {
      const ids = wps.map(wp => wp.id).filter(Boolean);
      const merged = {};
      for (let i = 0; i < ids.length; i += 200) {
        const r = await axios.get(`${backendUrl}/api/dispatch/work-period-statuses`, { params: { ids: ids.slice(i, i + 200).join(',') }, headers: { Authorization: `Bearer ${token}` } });
        Object.assign(merged, r.data || {});
      }
      setDispatchStatuses(prev => ({ ...prev, ...merged }));
    } catch (error) {
      console.error('Failed to fetch dispatch statuses:', error);
    }
  };
  const workPeriodsRef = React.useRef(workPeriods);
  workPeriodsRef.current = workPeriods;

  // Fetch dispatch statuses for all work periods when they change
  useEffect(() => {
    if (workPeriods.length > 0) fetchAllDispatchStatuses(workPeriods);
  }, [workPeriods]);

  // Periodic polling for dispatch status updates (every 30 seconds, one call)
  useEffect(() => {
    if (!token || !backendUrl) {

      return;
    }
    const pollInterval = setInterval(() => { if (!document.hidden) fetchAllDispatchStatuses(workPeriodsRef.current); }, 30000);
    return () => {

      clearInterval(pollInterval);
    };
  }, [backendUrl]); // FIXED: Removed workPeriods dependency to prevent infinite loop

  // BULK DELETE FUNCTION - For cleanup purposes
  const handleBulkDeleteWorkPeriods = async () => {
    const confirmMessage = `🧹 BULK DELETE WARNING!\n\nThis will delete ALL ${workPeriods.length} work periods permanently.\n\nAre you absolutely sure? This cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    // Double confirmation for safety
    if (!window.confirm('Last chance! Delete ALL work periods? Type YES in your mind and click OK.')) {
      return;
    }
    
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      

      
      const response = await axios.delete(`${backendUrl}/api/schedule/work-periods/bulk-delete`, { headers });
      

      
      // Clear local state
      setWorkPeriods([]);
      setDispatchStatuses({});
      
      toast.success(`🧹 Successfully deleted ${response.data.deleted_count} work periods!`);
      
      // Refresh with force replace instead of render key
      await fetchScheduleData(true);
      
    } catch (error) {
      console.error('🧹 Bulk delete error:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to bulk delete work periods');
      } else {
        toast.error(`Failed to delete work periods: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // BULLETPROOF WORK PERIOD DRAGGING - Stable, no data loss, proper conflict detection
  const handleBulletproofWorkPeriodDrag = (e, workPeriod) => {

    
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent dragging during resize
    if (isResizing) {

      return;
    }
    
    setIsDraggingWorkPeriod(true);
    setDraggedWorkPeriod(workPeriod);
    
    const startX = e.clientX;
    const startY = e.clientY;
    
    // Store original work period for revert on error
    const originalWorkPeriod = { ...workPeriod };
    const originalStartDate = new Date(workPeriod.start_date);
    const originalEndDate = new Date(workPeriod.end_date);
    const duration = Math.ceil((originalEndDate - originalStartDate) / (1000 * 60 * 60 * 24)) + 1;
    
    // Calculate pixels per day
    const getPixelsPerDay = () => {
      const calendarCell = document.querySelector('[data-date]');
      if (calendarCell) {
        const cellRect = calendarCell.getBoundingClientRect();
        return cellRect.width;
      }
      return 100;
    };
    
    const pixelsPerDay = getPixelsPerDay();
    
    // Visual feedback element
    const createDragPreview = () => {
      const preview = document.createElement('div');
      preview.style.cssText = `
        position: fixed;
        width: ${pixelsPerDay * duration}px;
        height: 60px;
        background: rgba(59, 130, 246, 0.8);
        border: 2px solid #1d4ed8;
        border-radius: 8px;
        color: white;
        z-index: 10000;
        pointer-events: none;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 500;
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
        transform: rotate(2deg) scale(1.05);
      `;
      preview.innerHTML = `${workPeriod.start_time} - ${workPeriod.end_time}<br/>${workPeriod.resources?.length || 0} resources`;
      document.body.appendChild(preview);
      return preview;
    };
    
    const dragPreview = createDragPreview();
    
    const handleMouseMove = (e) => {
      // Update drag preview position
      dragPreview.style.left = (e.clientX - pixelsPerDay * duration / 2) + 'px';
      dragPreview.style.top = (e.clientY - 30) + 'px';
      
      // Calculate potential new position
      const deltaX = e.clientX - startX;
      const daysDelta = Math.round(deltaX / pixelsPerDay);
      
      if (Math.abs(daysDelta) > 0) {
        const newStartDate = new Date(originalStartDate);
        const newEndDate = new Date(originalEndDate);
        
        newStartDate.setDate(originalStartDate.getDate() + daysDelta);
        newEndDate.setDate(originalEndDate.getDate() + daysDelta);
        
        // Visual feedback during drag (without committing changes)
        setWorkPeriods(currentWorkPeriods => 
          currentWorkPeriods.map(wp => 
            wp.id === workPeriod.id 
              ? { 
                  ...wp, 
                  start_date: formatDate(newStartDate),
                  end_date: formatDate(newEndDate),
                  isDragging: true
                }
              : wp
          )
        );
      }
    };
    
    const handleMouseUp = async (e) => {

      
      // Remove drag preview
      if (document.body.contains(dragPreview)) {
        document.body.removeChild(dragPreview);
      }
      
      const deltaX = e.clientX - startX;
      const daysDelta = Math.round(deltaX / pixelsPerDay);
      
      if (Math.abs(daysDelta) > 0) {
        const newStartDate = new Date(originalStartDate);
        const newEndDate = new Date(originalEndDate);
        
        newStartDate.setDate(originalStartDate.getDate() + daysDelta);
        newEndDate.setDate(originalEndDate.getDate() + daysDelta);
        
        // Validate new dates are within reasonable bounds (not too far in past/future)
        const today = new Date();
        const maxFutureDate = new Date(today.getFullYear() + 2, 11, 31); // 2 years in future
        const minPastDate = new Date(today.getFullYear() - 1, 0, 1); // 1 year in past
        
        if (newStartDate < minPastDate || newEndDate > maxFutureDate) {

          toast.error('Cannot move work period too far into past or future');
          // Revert to original
          setWorkPeriods(currentWorkPeriods => 
            currentWorkPeriods.map(wp => 
              wp.id === workPeriod.id ? { ...originalWorkPeriod, isDragging: false } : wp
            )
          );
          setIsDraggingWorkPeriod(false);
          setDraggedWorkPeriod(null);
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          return;
        }
        
        // Check for resource conflicts in new date range
        const conflictingResources = [];
        if (workPeriod.resources && workPeriod.resources.length > 0) {
          for (const resource of workPeriod.resources) {
            if (hasScheduleConflict(resource.id, resource.type, newStartDate, newEndDate, workPeriod.id)) {
              conflictingResources.push(resource.name);
            }
          }
        }
        
        if (conflictingResources.length > 0) {

          toast.error(`Cannot move work period: ${conflictingResources.join(', ')} already scheduled during target dates`);
          // Revert to original
          setWorkPeriods(currentWorkPeriods => 
            currentWorkPeriods.map(wp => 
              wp.id === workPeriod.id ? { ...originalWorkPeriod, isDragging: false } : wp
            )
          );
          setIsDraggingWorkPeriod(false);
          setDraggedWorkPeriod(null);
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          return;
        }
        
        // Update backend
        try {
          const updateData = {
            project_id: workPeriod.project_id,
            start_date: formatDate(newStartDate),
            end_date: formatDate(newEndDate),
            start_time: workPeriod.start_time,
            end_time: workPeriod.end_time,
            resources: workPeriod.resources || [],
            notes: workPeriod.notes || ''
          };
          
          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };
          

          const response = await axios.put(`${backendUrl}/api/schedule/work-periods/${workPeriod.id}`, updateData, { headers });

          
          // Update state with confirmed data
          setWorkPeriods(currentWorkPeriods => 
            currentWorkPeriods.map(wp => 
              wp.id === workPeriod.id 
                ? { ...wp, ...updateData, isDragging: false }
                : wp
            )
          );
          
          const direction = daysDelta > 0 ? 'forward' : 'backward';
          toast.success(`Work period moved ${direction} by ${Math.abs(daysDelta)} day(s)`);
          
        } catch (error) {
          console.error('🔧 Drag API error:', error);
          toast.error(error.response?.data?.detail || 'Failed to move work period', { duration: error.response?.status === 409 ? 14000 : 5000 });
          await fetchScheduleData(true, true);
          // Revert to original on error
          setWorkPeriods(currentWorkPeriods => 
            currentWorkPeriods.map(wp => 
              wp.id === workPeriod.id ? { ...originalWorkPeriod, isDragging: false } : wp
            )
          );
        }
      } else {
        // No movement, just clean up
        setWorkPeriods(currentWorkPeriods => 
          currentWorkPeriods.map(wp => 
            wp.id === workPeriod.id ? { ...wp, isDragging: false } : wp
          )
        );
      }
      
      // Clean up drag state
      setIsDraggingWorkPeriod(false);
      setDraggedWorkPeriod(null);
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    // FIXED: Add specific event listeners for drag operation
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    setIsDraggingWorkPeriod(true);
    setDraggedWorkPeriod(workPeriod);
  };

  // Drag functionality for moving work periods
  const handleWorkPeriodDragStart = (e, workPeriod) => {
    e.preventDefault();

    
    const startX = e.clientX;
    const startY = e.clientY;
    
    setIsDraggingWorkPeriod(true);
    setDraggedWorkPeriod(workPeriod);
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const daysDelta = Math.round(deltaX / 80); // Same sensitivity as resize
      
      if (Math.abs(daysDelta) > 0) {
        const originalStart = new Date(workPeriod.start_date);
        const originalEnd = new Date(workPeriod.end_date);
        
        const newStartDate = new Date(originalStart);
        const newEndDate = new Date(originalEnd);
        
        newStartDate.setDate(originalStart.getDate() + daysDelta);
        newEndDate.setDate(originalEnd.getDate() + daysDelta);
        
        // Update visually during drag
        const updatedWorkPeriods = workPeriods.map(wp => 
          wp.id === workPeriod.id 
            ? { 
                ...wp, 
                start_date: formatDate(newStartDate), 
                end_date: formatDate(newEndDate),
                isDragging: true
              }
            : wp
        );
        setWorkPeriods(updatedWorkPeriods);
      }
    };
    
    const handleMouseUp = async (e) => {
      const deltaX = e.clientX - startX;
      const daysDelta = Math.round(deltaX / 80);
      
      if (Math.abs(daysDelta) > 0) {
        const originalStart = new Date(workPeriod.start_date);
        const originalEnd = new Date(workPeriod.end_date);
        
        const newStartDate = new Date(originalStart);
        const newEndDate = new Date(originalEnd);
        
        newStartDate.setDate(originalStart.getDate() + daysDelta);
        newEndDate.setDate(originalEnd.getDate() + daysDelta);
        
        // Update on backend
        try {
          const updateData = {
            project_id: workPeriod.project_id,
            start_date: formatDate(newStartDate),
            end_date: formatDate(newEndDate),
            start_time: workPeriod.start_time,
            end_time: workPeriod.end_time,
            resources: workPeriod.resources || [],
            notes: workPeriod.notes || ''
          };
          
          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };
          
          await axios.put(`${backendUrl}/api/schedule/work-periods/${workPeriod.id}`, updateData, { headers });

          toast.success('Work period moved successfully');
          
          // DO NOT call fetchScheduleData() - keep the visual position as-is
          // The work period state has already been updated visually during drag
          
        } catch (error) {
          console.error('🔵 Error moving work period:', error);
          toast.error(error.response?.data?.detail || 'Failed to move work period', { duration: error.response?.status === 409 ? 14000 : 5000 });
          await fetchScheduleData(true, true);
          await fetchScheduleData(); // Revert on error
        }
      }
      
      setIsDraggingWorkPeriod(false);
      setDraggedWorkPeriod(null);
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    // FIXED: Add specific event listeners for drag operation
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    setIsDraggingWorkPeriod(true);
    setDraggedWorkPeriod(workPeriod);
  };

  // Custom drag system using mouse events
  const handleWorkPeriodMouseDown = (e, workPeriod) => {
    e.preventDefault();

    
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    // Create floating drag element
    const dragEl = document.createElement('div');
    dragEl.innerHTML = e.currentTarget.innerHTML;
    dragEl.style.cssText = `
      position: fixed;
      width: ${rect.width}px;
      height: ${rect.height}px;
      background: rgba(59, 130, 246, 0.9);
      border: 2px solid #1D4ED8;
      border-radius: 8px;
      color: white;
      z-index: 10000;
      pointer-events: none;
      transform: rotate(3deg) scale(1.1);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 500;
      opacity: 0.95;
    `;
    
    document.body.appendChild(dragEl);
    
    // Position the drag element
    const updatePosition = (clientX, clientY) => {
      dragEl.style.left = (clientX - offsetX) + 'px';
      dragEl.style.top = (clientY - offsetY) + 'px';
    };
    
    updatePosition(e.clientX, e.clientY);
    
    setIsDraggingWorkPeriod(true);
    setDraggedWorkPeriod(workPeriod);
    setDragElement(dragEl);
    setDragOffset({ x: offsetX, y: offsetY });
    
    // Make original element semi-transparent
    e.currentTarget.style.opacity = '0.3';
    
    const handleMouseMove = (e) => {
      updatePosition(e.clientX, e.clientY);
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    const handleMouseUp = (e) => {

      
      // Remove drag element
      if (document.body.contains(dragEl)) {
        document.body.removeChild(dragEl);
      }
      
      // Restore original element
      e.currentTarget.style.opacity = '';
      
      // Find drop target
      const elementBelow = document.elementFromPoint(e.clientX, e.clientY);
      const calendarCell = elementBelow?.closest('[data-date]');
      
      if (calendarCell && calendarCell.dataset.date && calendarCell.dataset.project) {
        const targetDate = new Date(calendarCell.dataset.date);
        const projectId = calendarCell.dataset.project;
        

        moveWorkPeriodToDate(workPeriod, targetDate);
      }
      
      // Clean up
      setIsDraggingWorkPeriod(false);
      setDraggedWorkPeriod(null);
      setDragElement(null);
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    // FIXED: Add specific event listeners for drag operation
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    setIsDraggingWorkPeriod(true);
    setDraggedWorkPeriod(workPeriod);
  };

  const handleWorkPeriodDragEnd = (e) => {

    setIsDraggingWorkPeriod(false);
    setDraggedWorkPeriod(null);
    setDragOffset({ x: 0, y: 0 });
    
    // Clean up any remaining drag elements
    const dragElements = document.querySelectorAll('[data-drag-temp="true"]');
    dragElements.forEach(el => {
      if (document.body.contains(el)) {
        document.body.removeChild(el);
      }
    });
  };

  const handleWorkPeriodDrop = async (e, targetWorkPeriod) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));

      
      if (data.type === 'resource') {
        // Add resource to existing work period
        await updateExistingWorkPeriodWithResource(targetWorkPeriod, data.item, data.itemType);
        // Optimistic update handles display refresh - no need for fetchScheduleData()
      }
    } catch (error) {
      console.error('Error handling work period drop:', error);
    }
  };

  // Duplicate function removed

  const getWorkPeriodForCell = (projectId, date) => {
    const dateStr = formatDate(date);
    // Normalize stored dates to YYYY-MM-DD — a work period can transiently hold a
    // full datetime string (e.g. "2026-06-11T00:00:00.000Z") after an optimistic
    // update; a raw lexical compare against "2026-06-11" would then match no day
    // and the block would vanish even though the period (and its crew) still exist.
    const d10 = (s) => (s == null ? s : String(s).slice(0, 10));
    // Resolve the project by id OR project_number (some records have no id field),
    // then match work periods against any of the project's identifiers. Trim every
    // value so a stray space in imported data can't break the exact-string match.
    const norm = (v) => (v == null ? '' : String(v).trim());
    const pid = norm(projectId);
    const proj = projects.find(p => (p.id && norm(p.id) === pid) || norm(p.project_number) === pid);
    const ids = new Set([pid, norm(proj?.id), norm(proj?.project_number)].filter(Boolean));
    const matchingPeriod = workPeriods.find(wp => 
      ids.has(norm(wp.project_id)) && 
      d10(wp.start_date) <= dateStr && 
      d10(wp.end_date) >= dateStr
    );
    return matchingPeriod;
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'crew': return Users;
      case 'trucks': return Truck;
      case 'trailers': return '🚛';
      case 'welders': return '⚡';
      default: return Users;
    }
  };

  const openWorkPeriodDetail = (workPeriod) => {
    setSelectedWorkPeriod(workPeriod);
    setIsDetailModalOpen(true);
  };

  const updateWorkPeriod = async (workPeriodId, updateData) => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      

      const response = await axios.put(`${backendUrl}/api/schedule/work-periods/${workPeriodId}`, updateData, { headers });

      toast.success('Work period updated successfully');
      
      // Enhanced optimistic UI update with proper date handling and duration recalculation
      setWorkPeriods(prevWorkPeriods => {
        return prevWorkPeriods.map(wp => {
          if (wp.id === workPeriodId) {


            
            // Create the updated work period with careful field handling
            const updatedWorkPeriod = { 
              ...wp, 
              // Dates - ensure proper format (YYYY-MM-DD)
              start_date: updateData.start_date || wp.start_date,
              end_date: updateData.end_date || wp.end_date,
              // Times
              start_time: updateData.start_time || wp.start_time,
              end_time: updateData.end_time || wp.end_time,
              // Resources - preserve existing if not provided
              resources: updateData.resources !== undefined ? updateData.resources : (wp.resources || []),
              // Foreman designation
              foreman_id: updateData.foreman_id !== undefined ? updateData.foreman_id : wp.foreman_id,
              // Rental equipment
              rental_equipment: updateData.rental_equipment !== undefined ? updateData.rental_equipment : (wp.rental_equipment || []),
              // Notes
              notes: updateData.notes !== undefined ? updateData.notes : (wp.notes || ''),
              // Other fields
              project_id: updateData.project_id !== undefined ? updateData.project_id : wp.project_id
            };
            
            // Calculate new duration for debugging
            const startDate = new Date(updatedWorkPeriod.start_date);
            const endDate = new Date(updatedWorkPeriod.end_date);
            const newDuration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
            



            
            return updatedWorkPeriod;
          }
          return wp;
        });
      });
      
      // Also update the selectedWorkPeriod if it's the one being updated (for modal consistency)
      setSelectedWorkPeriod(prev => {
        if (prev && prev.id === workPeriodId) {
          const updatedSelected = { 
            ...prev, 
            start_date: updateData.start_date || prev.start_date,
            end_date: updateData.end_date || prev.end_date,
            start_time: updateData.start_time || prev.start_time,
            end_time: updateData.end_time || prev.end_time,
            resources: updateData.resources !== undefined ? updateData.resources : (prev.resources || []),
            foreman_id: updateData.foreman_id !== undefined ? updateData.foreman_id : prev.foreman_id,
            rental_equipment: updateData.rental_equipment !== undefined ? updateData.rental_equipment : (prev.rental_equipment || []),
            notes: updateData.notes !== undefined ? updateData.notes : (prev.notes || '')
          };

          return updatedSelected;
        }
        return prev;
      });
      
      setIsDetailModalOpen(false);
      
      // Refresh data properly instead of force render
      await fetchScheduleData(true, true);

    } catch (error) {
      console.error('Error updating work period:', error);
      toast.error(error.response?.data?.detail || 'Failed to update work period', { duration: error.response?.status === 409 ? 14000 : 5000 });
      await fetchScheduleData(true, true);
    }
  };

  const deleteWorkPeriod = async (workPeriodId) => {
    try {
      // Enhanced authentication check
      if (!token) {
        console.error('🔴 NO TOKEN - User needs to login');
        toast.error('Please login to delete work periods');
        window.location.href = '/';
        return;
      }

      if (!user || (!['super_admin', 'dispatch', 'project_manager_estimator', 'admin'].includes(user.role) && !(user.extra_permissions || []).includes('schedule'))) {
        console.error('🔴 INSUFFICIENT PERMISSIONS - User role:', user?.role);
        toast.error('You do not have permission to delete work periods');
        return;
      }
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Find the work period being deleted
      const wpToDelete = workPeriods.find(wp => wp.id === workPeriodId);
      if (!wpToDelete) {
        toast.error('Work period not found');
        return;
      }
      
      console.log('🗑️ Deleting work period:', wpToDelete.id, 'with', wpToDelete.resources?.length || 0, 'resources');
      
      // Check if any resources in this work period have exclusions elsewhere
      const exclusionsToRestore = [];
      const startDate = new Date(wpToDelete.start_date);
      const endDate = new Date(wpToDelete.end_date);
      
      // Generate all dates in this work period
      const wpDates = [];
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        wpDates.push(formatDate(new Date(d)));
      }
      
      console.log('📅 Checking dates:', wpDates);
      
      // Check each resource in the work period
      for (const resource of wpToDelete.resources || []) {
        const resourceKey = `${resource.type}_${resource.id}`;
        console.log('🔍 Checking resource:', resource.name, 'key:', resourceKey);
        
        // Check all other work periods for exclusions of this resource on these dates
        for (const otherWP of workPeriods) {
          if (otherWP.id === workPeriodId) continue; // Skip the one being deleted
          if (!otherWP.excluded_dates) continue;
          
          console.log('  Checking work period:', otherWP.id, 'excluded_dates:', otherWP.excluded_dates);
          
          for (const date of wpDates) {
            // Check for both old format (just resourceKey) and new format (resourceKey|projectNumber)
            const hasExclusion = otherWP.excluded_dates[date]?.some(e => 
              e === resourceKey || e.startsWith(resourceKey + '|')
            );
            
            if (hasExclusion) {
              console.log('    ✓ Found exclusion on', date, 'for', resourceKey);
              exclusionsToRestore.push({
                workPeriodId: otherWP.id,
                workPeriod: otherWP,
                date: date,
                resourceKey: resourceKey,
                resourceName: resource.name
              });
            }
          }
        }
      }
      
      console.log('📋 Total exclusions found to restore:', exclusionsToRestore.length);
      
      // If there are exclusions to restore, ask user
      let shouldRestoreExclusions = false;
      if (exclusionsToRestore.length > 0) {
        const uniqueResources = [...new Set(exclusionsToRestore.map(e => e.resourceName))];
        const confirmMessage = `This work period has ${uniqueResources.length} resource(s) that were excluded from other jobs:\n\n${uniqueResources.join(', ')}\n\nWould you like to restore them to their original assignments?\n\n(This will remove the orange exclusion markers)`;
        shouldRestoreExclusions = window.confirm(confirmMessage);
        console.log('User choice for restoration:', shouldRestoreExclusions ? 'YES' : 'NO');
      } else {
        console.log('No exclusions found to restore');
      }
      
      // If user wants to restore exclusions, do it before deleting
      if (shouldRestoreExclusions) {
        console.log('🔄 Restoring exclusions for', exclusionsToRestore.length, 'entries');
        
        // Group by work period to minimize API calls
        const groupedByWP = {};
        for (const exclusion of exclusionsToRestore) {
          if (!groupedByWP[exclusion.workPeriodId]) {
            groupedByWP[exclusion.workPeriodId] = {
              workPeriod: exclusion.workPeriod,
              exclusions: []
            };
          }
          groupedByWP[exclusion.workPeriodId].exclusions.push(exclusion);
        }
        
        // Update each work period to remove the exclusions
        for (const wpId in groupedByWP) {
          const { workPeriod, exclusions } = groupedByWP[wpId];
          const updatedExcludedDates = { ...workPeriod.excluded_dates };
          
          // Remove the exclusions (handle both old and new format)
          for (const exclusion of exclusions) {
            if (updatedExcludedDates[exclusion.date]) {
              updatedExcludedDates[exclusion.date] = updatedExcludedDates[exclusion.date].filter(
                rKey => rKey !== exclusion.resourceKey && !rKey.startsWith(exclusion.resourceKey + '|')
              );
              // If no more exclusions for this date, remove the date entry
              if (updatedExcludedDates[exclusion.date].length === 0) {
                delete updatedExcludedDates[exclusion.date];
              }
            }
          }
          
          // Update the work period
          await axios.put(
            `${backendUrl}/api/schedule/work-periods/${wpId}`,
            { excluded_dates: updatedExcludedDates },
            { headers }
          );
          console.log('✅ Restored resources for work period:', wpId);
        }
        
        toast.success(`Resources restored! ${exclusionsToRestore.length} exclusion(s) removed.`);
      } else if (exclusionsToRestore.length > 0) {
        toast.info('Exclusions kept - resources remain assigned to this job');
      }
      
      const response = await axios.delete(`${backendUrl}/api/schedule/work-periods/${workPeriodId}`, { headers });
      
      if (response.status === 200) {
        toast.success('Work period deleted successfully');
        
        // Immediate UI update - remove from state
        setWorkPeriods(prev => prev.filter(wp => wp.id !== workPeriodId));
        
        // Force a full refresh to update all exclusion markers
        await fetchScheduleData(true);
        
        setIsDetailModalOpen(false);
      } else {
        console.error('🔴 Unexpected response status:', response.status);
        toast.error(`Delete failed with status: ${response.status}`);
      }
    } catch (error) {
      console.error('🔴 DELETE ERROR:', error);
      console.error('🔴 Error response status:', error.response?.status);
      console.error('🔴 Error response data:', error.response?.data);
      
      if (error.response?.status === 401) {
        console.error('🔴 AUTHENTICATION EXPIRED');
        toast.error('Your session has expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/';
      } else if (error.response?.status === 403) {
        console.error('🔴 PERMISSION DENIED');
        toast.error('Permission denied - insufficient privileges to delete work periods');
      } else if (error.response?.status === 404) {
        console.error('🔴 WORK PERIOD NOT FOUND');
        toast.error('Work period not found - it may have already been deleted');
        await fetchScheduleData();
        setIsDetailModalOpen(false);
      } else {
        console.error('🔴 UNKNOWN DELETE ERROR');
        toast.error(`Failed to delete work period: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // Everyone signed in can view the schedule; dispatch + super admins (or the per-user 'schedule' grant) can change it
  const canEdit = ['super_admin', 'dispatch'].includes(user?.role) || (user?.extra_permissions || []).includes('schedule');
  const canSchedule = canEdit;

  // Remove resource from work period functionality
  const handleRemoveResource = async (workPeriodId, resourceId, resourceType, resourceName) => {
    try {

      
      if (!workPeriodId || !resourceId || !resourceType) {
        console.error('❌ Missing required parameters');
        toast.error('Missing required information for resource removal');
        return;
      }
      
      if (!token || !backendUrl) {
        console.error('❌ Missing authentication or backend URL');
        toast.error('Authentication error - please refresh the page');
        return;
      }

      // STEP 1: Update UI immediately (optimistic update)

      setWorkPeriods(currentWorkPeriods => {

        const updated = currentWorkPeriods.map(wp => {
          if (wp.id === workPeriodId) {
            const beforeCount = wp.resources?.length || 0;
            const newResources = wp.resources?.filter(r => r.id !== resourceId) || [];
            const afterCount = newResources.length;

            return { ...wp, resources: newResources };
          }
          return wp;
        });

        return updated;
      });
      
      // Show immediate feedback
      toast.success(`Removed ${resourceName}`, { duration: 2000 });

      // STEP 2: Call backend API (but don't refetch afterward)

      const apiUrl = `${backendUrl}/api/schedule/work-periods/${workPeriodId}/resources/${resourceId}?resource_type=${resourceType}`;
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      const response = await axios.delete(apiUrl, { headers });

      
      // NO REFETCH - UI already updated optimistically

      
    } catch (error) {
      console.error('❌ Error removing resource from work period:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      
      if (error.response?.status === 404) {
        toast.error('Work period or resource not found');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to modify work periods');
      } else {
        toast.error(`Failed to remove ${resourceName}: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // DEBUG DISABLED: Removed render-blocking console logs that caused infinite performance issues

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading schedule...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64 bg-white text-slate-900">
        <div className="text-center">
          <p className="text-red-600">User not loaded</p>
          <p className="text-sm text-slate-500">Please refresh the page</p>
        </div>
      </div>
    );
  }

  // Authentication check in render method
  if (!token) {
    return (
      <div className="flex items-center justify-center h-screen bg-white text-slate-900">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Authentication Required</h2>
          <p className="text-slate-700 mb-4">You must be logged in to access the schedule.</p>
          <button 
            onClick={() => window.location.href = '/login'}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-white"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }


  // Modify crew of a dispatch-request-backed work period straight from the schedule
  const canModifySchedCrew = canEdit;
  // Swap / remove / add from the work-period modal: merge the server's fresh crew into our state
  const handleCrewChanged = (wpId, outM, inM, fresh) => {
    const apply = (wp) => fresh ? (fresh.planned_crew !== undefined ? { ...wp, planned_crew: fresh.planned_crew } : { ...wp, resources: fresh.resources || wp.resources, foreman_id: fresh.foreman_id ?? null, crew_overrides: fresh.crew_overrides || [] }) : ({
      ...wp,
      resources: outM && inM
        ? (wp.resources || []).map(r => r.type === 'crew' && r.id === outM.id ? { ...r, id: inM.id, name: inM.name } : r)
        : outM ? (wp.resources || []).filter(r => !(r.type === 'crew' && r.id === outM.id))
        : [...(wp.resources || []), { type: 'crew', id: inM.id, name: inM.name }],
      ...(outM && wp.foreman_id === outM.id ? { foreman_id: inM ? inM.id : null } : {}),
    });
    setWorkPeriods(prev => prev.map(wp => wp.id === wpId ? apply(wp) : wp));
    setSelectedWorkPeriod(prev => prev && prev.id === wpId ? apply(prev) : prev);
  };
  const openModifyCrew = async (wp) => {
    if (!wp?.request_id) return;
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [reqRes, resRes] = await Promise.all([
        axios.get(`${backendUrl}/api/dispatch/requests/${wp.request_id}`, { headers }),
        axios.get(`${backendUrl}/api/dispatch/resources`, { headers }),
      ]);
      setSchedResources(resRes.data || { crew: [] });
      setSchedModifyReq(reqRes.data);
    } catch (e) {
      toast.error('Could not load the dispatch request for this job');
    }
  };
  const renderCrewModals = () => (
    <>
      {schedModifyReq && (
        <ModifyCrewModal
          req={schedModifyReq}
          resources={schedResources}
          onClose={() => setSchedModifyReq(null)}
          onSaved={(data) => {
            setSchedModifyReq(null);
            setIsDetailModalOpen(false);
            handleBulletproofRefresh();
            if (data?.added?.length && data?.texts_sent_before) {
              setSchedSmsRestrict(data.added);
              setSchedSmsReq(data.request);
            }
          }}
        />
      )}
      {schedSmsReq && (
        <DispatchSmsModal
          req={schedSmsReq}
          restrictTo={schedSmsRestrict}
          onSkip={() => { setSchedSmsReq(null); setSchedSmsRestrict(null); }}
          onDone={() => { setSchedSmsReq(null); setSchedSmsRestrict(null); handleBulletproofRefresh(); }}
        />
      )}
    </>
  );

  // Mobile: show simple list view
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  if (isMobile) {
    const mToday = new Date();
    const mWeekStart = new Date(mToday);
    mWeekStart.setDate(mToday.getDate() - mToday.getDay());
    const mWeekEnd = new Date(mWeekStart);
    mWeekEnd.setDate(mWeekStart.getDate() + 6);
    const thisWeekWPs = workPeriods.filter(wp => wp.start_date <= formatDate(mWeekEnd) && wp.end_date >= formatDate(mWeekStart));

    return (
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900">Millwright Schedule</h1>
          <button onClick={handleBulletproofRefresh} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs">Refresh</button>
        </div>
        <p className="text-xs text-slate-500">Week of {formatDate(mWeekStart)} — full calendar on desktop</p>
        {thisWeekWPs.length === 0 ? (
          <p className="text-center py-8 text-slate-500">No work periods this week</p>
        ) : thisWeekWPs.map(wp => {
          const proj = projects.find(p => p.id === wp.project_id || p.project_number === wp.project_id);
          const crewCount = (wp.resources || []).filter(r => r.type === 'crew').length;
          return (
            <div key={wp.id} onClick={() => { setSelectedWorkPeriod(wp); setIsDetailModalOpen(true); }}
              className={`bg-white rounded-xl border p-4 cursor-pointer ${wp.status === 'cancelled' ? 'border-red-300 opacity-70' : wp.dispatch_status === 'tentative' ? 'border-amber-200' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono text-blue-700 font-bold">{proj?.project_number || wp.project_id}</span>
                  {wp.status === 'cancelled' && <span className="ml-2 text-[10px] font-bold text-red-600">CANCELLED</span>}
                  <span className="text-slate-900 ml-2">{proj?.client_name || ''}</span>
                </div>
                <span className="text-sm text-slate-500">{crewCount} crew</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">{wp.start_date} to {wp.end_date}</p>
              {wp.notes && <p className="text-xs text-slate-500 mt-1">{wp.notes}</p>}
            </div>
          );
        })}
        {isDetailModalOpen && selectedWorkPeriod && !canEdit && (
          <WorkPeriodPeek workPeriod={selectedWorkPeriod} project={(projects || []).find(p => p.id === selectedWorkPeriod.project_id || p.project_number === selectedWorkPeriod.project_id)} onClose={() => setIsDetailModalOpen(false)} />
        )}
        {isDetailModalOpen && selectedWorkPeriod && canEdit && (
          <WorkPeriodDetailModal
            workPeriod={selectedWorkPeriod}
            onClose={() => setIsDetailModalOpen(false)}
            onUpdate={updateWorkPeriod}
            onDelete={deleteWorkPeriod}
            onDispatch={handleDispatchWorkPeriod}
            onDispatchOne={sendIndividualDispatch}
            onDispatchStatusRefresh={fetchDispatchStatus}
            onCancelJob={(wp) => setCancelJobWP(wp)}
            onRebookJob={(wp) => setRebookWP(wp)}
            onModifyCrew={canModifySchedCrew ? openModifyCrew : undefined}
            onCrewSwapped={handleCrewChanged}
            dispatchStatuses={dispatchStatuses}
            formatDate={formatDate}
            projects={projects}
          />
        )}
        {renderCrewModals()}
        {cancelJobWP && (
          <CancelJobModal
            workPeriodId={cancelJobWP.id}
            onClose={() => setCancelJobWP(null)}
            onCancelled={() => {
              setCancelJobWP(null);
              setIsDetailModalOpen(false);
              setWorkPeriods(prev => prev.map(wp => wp.id === cancelJobWP.id ? { ...wp, status: 'cancelled' } : wp));
            }}
          />
        )}
        {rebookWP && (
          <RebookJobModal
            workPeriodId={rebookWP.id}
            jobLabel={`Was scheduled ${rebookWP.start_date} to ${rebookWP.end_date}`}
            onClose={() => setRebookWP(null)}
            onRebooked={() => {
              setRebookWP(null);
              setIsDetailModalOpen(false);
              handleBulletproofRefresh();
            }}
          />
        )}
        {showDispatchConfirm && selectedWorkPeriod && (
          <DispatchConfirmationModal
            workPeriod={selectedWorkPeriod}
            dispatchMessage={dispatchMessage}
            setDispatchMessage={setDispatchMessage}
            onConfirm={sendDispatch}
            onCancel={() => setShowDispatchConfirm(false)}
            projects={projects}
          />
        )}
      </div>
    );
  }


  const weekDays = getDaysInWeek();

  // Header number = the bubbles below it added up (the sheet's "Manpower Totals" row works the same way).
  // Colour = scheduled vs the guys actually available that day (sheet headcount − booked off):
  //   ≤ avail−2 green · avail−1..avail yellow · avail+1..avail+3 orange · ≥ avail+4 red
  const manpowerTone = (c, avail) => {
    if (!c) return 'bg-white/50 text-slate-500 border-slate-300';
    if (!avail) return 'bg-emerald-100 text-emerald-700 border-emerald-400';
    if (c <= avail - 2) return 'bg-emerald-100 text-emerald-700 border-emerald-400';
    if (c <= avail) return 'bg-yellow-100 text-yellow-800 border-yellow-400';
    if (c < avail + 4) return 'bg-orange-100 text-orange-800 border-orange-400';
    return 'bg-red-100 text-red-700 border-red-400';
  };
  const manpowerBadge = (day) => {
    const key = formatDate(day);
    const bars = manpowerByDay[key] || 0;
    const sheet = sheetTotals.days[key];
    // Sheet wins: show the sheet's own Monthly View total for the day when it has one; bars only on days the sheet doesn't cover
    const c = sheet?.plan_total != null ? sheet.plan_total : bars;
    const off = sheet?.off || 0;
    const avail = crewHeadcount ? Math.max(crewHeadcount - off, 0) : 0;
    const bits = sheet?.plan_total != null
      ? [`${c} on the dispatch sheet's Monthly View totals row`, `${bars} on the Ops bars below${bars !== c ? ` (${Math.abs(bars - c)} ${bars < c ? 'fewer' : 'more'} — open the day tab to compare)` : ''}`]
      : [`${c} scheduled (bars below added up)`];
    if (sheet) {
      bits.push(`day tab: ${sheet.millwrights} millwright names${sheet.shop ? ` + ${sheet.shop} shop` : ''}`);
      (sheet.no_wo || []).forEach(x => bits.push(`${x.count} ${x.customer} on the sheet without a WO (no bar)`));
    }
    if (crewHeadcount) bits.push(`${avail} of ${crewHeadcount} available${off ? ` (${off} booked off)` : ''}`);
    const drift = sheet?.plan_total != null && Math.abs(bars - c) >= 2;
    return (
      <div className={`relative mt-0.5 mx-auto w-[26px] leading-4 text-[9px] font-bold rounded border ${manpowerTone(c, avail)}`}
        data-testid={`manpower-${key}`} data-count={c} data-bars={bars} data-available={avail}
        title={bits.join(' · ')}>
        {c}
        {drift && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-500 border border-white" data-testid={`manpower-drift-${key}`} />}
      </div>
    );
  };
  const extendedDays = getExtendedDateRange(); // 45 days for horizontal scrolling
  const displayDays = viewMode === 'month' ? getMonthDays() : extendedDays; // Use extended range for week view

  return (
    <div className="flex h-full min-h-0" data-testid="schedule-root">
      
      {/* Sidebar with Resources */}
      {canSchedule && (
        <div className="w-80 bg-white/55 backdrop-blur-xl border-r border-slate-200/80 flex flex-col flex-shrink-0 z-20">
          <div className="p-4 border-b border-slate-200 flex-shrink-0">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-slate-900">Resources</h2>
              <span className="text-[11px] text-slate-500" title="Shortcuts: W = week · M = month · Ctrl+R = refresh · ? = help" data-testid="schedule-resources-hint">Drag onto a job row to schedule</span>
            </div>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="px-4">
              <TabsList className="grid w-full grid-cols-4 gap-1 bg-white/40 backdrop-blur-sm px-3 py-2 my-2 flex-shrink-0 rounded-lg">
                <TabsTrigger value="crew" className="text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-gray-700">Crew</TabsTrigger>
                <TabsTrigger value="trucks" className="text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-gray-700">Trucks</TabsTrigger>
                <TabsTrigger value="trailers" className="text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-gray-700">Trailers</TabsTrigger>
                <TabsTrigger value="welders" className="text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-gray-700">Welders</TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-4 min-h-0">
              <TabsContent value="crew" className="mt-0 h-full">
                <div className="space-y-2">
                  {/* Split crew into Field / Shop / Office */}
                  {(() => {
                    const fieldRoles = ['field_employee', 'foreman', 'millwright', 'millwright_foreman', 'apprentice', 'labourer', 'crew'];
                    const shopRoles = ['fabrication', 'shop_foreman', 'welder', 'fitter', 'tool_crib'];
                    const field = resources.crew.filter(p => fieldRoles.includes(p.role?.toLowerCase()));
                    const shop = resources.crew.filter(p => shopRoles.includes(p.role?.toLowerCase()));
                    const office = resources.crew.filter(p => !fieldRoles.includes(p.role?.toLowerCase()) && !shopRoles.includes(p.role?.toLowerCase()));

                    const renderPerson = (person) => {
                      const availabilityInfo = getResourceAvailabilityInfo(person, 'crew');
                      const isScheduled = availabilityInfo.isScheduled;
                    
                    return (
                      <div
                        key={person.id}
                        draggable={true}
                        onDragStart={(e) => {
                          handleDragStart(e, person, 'crew');
                        }}
                        onDragEnd={handleDragEnd}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setDraggedItem(person);
                          setDraggedItemType('crew');
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDraggedItem(person);
                          setDraggedItemType('crew');
                          const statusText = isScheduled ? ' (Currently Scheduled)' : '';
                          toast.info(`Selected ${person.full_name}${statusText} - click on a calendar cell to assign`);
                        }}
                        className={`p-3 rounded-lg cursor-move transition-colors border-2 border-transparent hover:border-blue-500 ${
                          isScheduled 
                            ? 'bg-slate-900/10 opacity-60 hover:bg-white/40' 
                            : 'bg-white/50 backdrop-blur-sm hover:bg-white/60'
                        }`}
                        style={{ touchAction: 'none' }}
                      >
                        <div className="flex items-center gap-2">
                          <Users className={`h-4 w-4 ${isScheduled ? 'text-slate-500' : 'text-blue-600'}`} />
                          <span className={`font-medium ${isScheduled ? 'text-slate-500' : 'text-gray-900'}`}>
                            {person.full_name}
                          </span>
                          {isScheduled && (
                            <span className="ml-auto text-xs px-2 py-1 bg-yellow-500 text-yellow-900 rounded">
                              Scheduled
                            </span>
                          )}
                        </div>
                        <p className={`text-xs capitalize ${isScheduled ? 'text-slate-500' : 'text-gray-700'}`}>
                          {person.role.replace('_', ' ')}
                        </p>
                        {isScheduled && availabilityInfo.scheduledPeriods.length > 0 && (
                          <p className="text-xs text-amber-700 mt-1 cursor-pointer hover:text-yellow-700"
                            onClick={() => {
                              const wp = workPeriods.find(w => w.id === availabilityInfo.scheduledPeriods[0].workPeriodId);
                              if (wp) { setSelectedWorkPeriod(wp); setIsDetailModalOpen(true); }
                            }}>
                            {availabilityInfo.scheduledPeriods.length} period(s) — click to view
                          </p>
                        )}
                      </div>
                    );
                    };

                    return (
                      <>
                        {field.length > 0 && (
                          <>
                            <div className="text-xs font-bold text-blue-700 uppercase tracking-wider py-1 px-2 bg-blue-50 rounded">Field ({field.length})</div>
                            {field.map(renderPerson)}
                          </>
                        )}
                        {shop.length > 0 && (
                          <>
                            <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider py-1 px-2 bg-emerald-50 rounded mt-3 border-t border-slate-300 pt-2">Shop ({shop.length})</div>
                            {shop.map(renderPerson)}
                          </>
                        )}
                        {office.length > 0 && (
                          <>
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider py-1 px-2 bg-slate-100 rounded mt-3 border-t border-slate-300 pt-2">Office ({office.length})</div>
                            {office.map(renderPerson)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </TabsContent>

              <TabsContent value="trucks" className="mt-0">
                <div className="space-y-2">
                  {resources.trucks.map((truck) => {
                    const availabilityInfo = getResourceAvailabilityInfo(truck, 'trucks');
                    const isScheduled = availabilityInfo.isScheduled;
                    
                    return (
                      <div
                        key={truck.id}
                        draggable="true"
                        onDragStart={(e) => {
                          handleDragStart(e, truck, 'trucks');
                        }}
                        onDragEnd={handleDragEnd}
                        className={`p-3 rounded-lg cursor-move transition-colors border-2 border-transparent hover:border-blue-500 ${
                          isScheduled 
                            ? 'bg-slate-900/10 opacity-60 hover:bg-white/40' 
                            : 'bg-white/50 backdrop-blur-sm hover:bg-white/60'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Truck className={`h-4 w-4 ${isScheduled ? 'text-slate-500' : 'text-green-600'}`} />
                          <span className={`font-medium ${isScheduled ? 'text-slate-500' : 'text-gray-900'}`}>
                            {truck.equipment_name}
                          </span>
                          {isScheduled && (
                            <span className="ml-auto text-xs px-2 py-1 bg-yellow-500 text-yellow-900 rounded">
                              Scheduled
                            </span>
                          )}
                        </div>
                        <p className={`text-xs ${isScheduled ? 'text-slate-500' : 'text-gray-700'}`}>
                          {truck.model} • {truck.year}
                        </p>
                        {isScheduled && availabilityInfo.scheduledPeriods.length > 0 && (
                          <p className="text-xs text-amber-700 mt-1 cursor-pointer hover:text-yellow-700"
                            onClick={() => {
                              const wp = workPeriods.find(w => w.id === availabilityInfo.scheduledPeriods[0].workPeriodId);
                              if (wp) { setSelectedWorkPeriod(wp); setIsDetailModalOpen(true); }
                            }}>
                            {availabilityInfo.scheduledPeriods.length} period(s) — click to view
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="trailers" className="mt-0">
                <div className="space-y-2">
                  {resources.trailers.map((trailer) => {
                    const availabilityInfo = getResourceAvailabilityInfo(trailer, 'trailers');
                    const isScheduled = availabilityInfo.isScheduled;
                    
                    return (
                      <div
                        key={trailer.id}
                        draggable="true"
                        onDragStart={(e) => {
                          handleDragStart(e, trailer, 'trailers');
                        }}
                        onDragEnd={handleDragEnd}
                        className={`p-3 rounded-lg cursor-move transition-colors border-2 border-transparent hover:border-blue-500 ${
                          isScheduled 
                            ? 'bg-slate-900/10 opacity-60 hover:bg-white/40' 
                            : 'bg-white/50 backdrop-blur-sm hover:bg-white/60'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`${isScheduled ? 'text-slate-500' : 'text-orange-600'}`}>🚛</span>
                          <span className={`font-medium ${isScheduled ? 'text-slate-500' : 'text-gray-900'}`}>
                            {trailer.equipment_name}
                          </span>
                          {isScheduled && (
                            <span className="ml-auto text-xs px-2 py-1 bg-yellow-500 text-yellow-900 rounded">
                              Scheduled
                            </span>
                          )}
                        </div>
                        <p className={`text-xs ${isScheduled ? 'text-slate-500' : 'text-gray-700'}`}>
                          {trailer.model}
                        </p>
                        {isScheduled && availabilityInfo.scheduledPeriods.length > 0 && (
                          <p className="text-xs text-amber-700 mt-1 cursor-pointer hover:text-yellow-700"
                            onClick={() => {
                              const wp = workPeriods.find(w => w.id === availabilityInfo.scheduledPeriods[0].workPeriodId);
                              if (wp) { setSelectedWorkPeriod(wp); setIsDetailModalOpen(true); }
                            }}>
                            {availabilityInfo.scheduledPeriods.length} period(s) — click to view
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="welders" className="mt-0">
                <div className="space-y-2">
                  {resources.welders.map((welder) => {
                    const availabilityInfo = getResourceAvailabilityInfo(welder, 'welders');
                    const isScheduled = availabilityInfo.isScheduled;
                    
                    return (
                      <div
                        key={welder.id}
                        draggable="true"
                        onDragStart={(e) => {
                          handleDragStart(e, welder, 'welders');
                        }}
                        onDragEnd={handleDragEnd}
                        className={`p-3 rounded-lg cursor-move transition-colors border-2 border-transparent hover:border-blue-500 ${
                          isScheduled 
                            ? 'bg-slate-900/10 opacity-60 hover:bg-white/40' 
                            : 'bg-white/50 backdrop-blur-sm hover:bg-white/60'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`${isScheduled ? 'text-slate-500' : 'text-yellow-600'}`}>⚡</span>
                          <span className={`font-medium ${isScheduled ? 'text-slate-500' : 'text-gray-900'}`}>
                            {welder.equipment_name}
                          </span>
                          {isScheduled && (
                            <span className="ml-auto text-xs px-2 py-1 bg-yellow-500 text-yellow-900 rounded">
                              Scheduled
                            </span>
                          )}
                        </div>
                        <p className={`text-xs ${isScheduled ? 'text-slate-500' : 'text-gray-700'}`}>
                          {welder.fuel_type} • {welder.model}
                        </p>
                        {isScheduled && availabilityInfo.scheduledPeriods.length > 0 && (
                          <p className="text-xs text-amber-700 mt-1 cursor-pointer hover:text-yellow-700"
                            onClick={() => {
                              const wp = workPeriods.find(w => w.id === availabilityInfo.scheduledPeriods[0].workPeriodId);
                              if (wp) { setSelectedWorkPeriod(wp); setIsDetailModalOpen(true); }
                            }}>
                            {availabilityInfo.scheduledPeriods.length} period(s) — click to view
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      )}

      {/* Main Calendar Area */}
      <div className="flex-1 flex flex-col min-w-0 bg-white/40">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200/80 flex-shrink-0 bg-white/60 backdrop-blur-xl">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-baseline gap-3 flex-wrap">
                <h1 className="text-2xl font-black tracking-tight text-slate-900">Millwright Schedule</h1>
                <p className="text-sm font-semibold text-sky-800" data-testid="schedule-month-label">
                  {windowLabel}
                </p>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-600 whitespace-nowrap flex-wrap" data-testid="schedule-legend">
                <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2.5 rounded-sm" style={{ background: '#dbeafe', borderLeft: '3px solid #1d4ed8' }} /> Dispatched</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2.5 rounded-sm" style={{ background: '#fef3c7', borderLeft: '3px solid #d97706', outline: '1px dashed #d97706' }} /> Tentative</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3.5 h-2.5 rounded-sm" style={{ background: '#ccfbf1', borderLeft: '3px solid #0f766e' }} /> Dailies / sheet</span>
                <span className="flex items-center gap-1" title="Booked training session — crew are blocked out for these days (Safety & Training → Mass Training)"><span className="inline-block w-3.5 h-2.5 rounded-sm" style={{ background: '#ede9fe', borderLeft: '3px solid #7c3aed' }} /> Training</span>
                <span className="flex items-center gap-1" title="Projection — a headcount dropped on the job before anyone is named (the + on a job row). Name the crew on the Dailies page and it firms up."><span className="inline-flex items-center justify-center rounded-full text-white font-bold text-[8px] leading-none px-1 h-3" style={{ background: '#d97706' }}>4?</span> Projection</span>
                <span className="flex items-center gap-1 whitespace-nowrap" title="Past bar rebuilt from the field timesheets — one clean bar per week, crew as they actually worked"><span className="inline-block w-3.5 h-2.5 rounded-sm" style={{ background: '#d1fae5', borderLeft: '3px solid #047857' }} /> As worked <span className="inline-block px-1 rounded-sm bg-emerald-700 text-white text-[8px] font-bold leading-3">⏱</span></span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500" data-testid="schedule-hint">{canEdit ? 'Click a bar to edit · Shift+drag to move · drag the edges to resize' : 'View only — click a bar for details'}</span>
                {crewHeadcount > 0 && (
                  <span className="flex items-center gap-1 whitespace-nowrap" data-testid="schedule-headcount-legend"
                    title={`Daily total = the bar bubbles in that column added up; colour compares it with the ${crewHeadcount} millwrights we have (sheet "Current Manpower") minus anyone booked off that day`}>
                    <span className="font-semibold text-slate-700">{crewHeadcount} millwrights</span><span className="text-slate-400">(−booked off)</span>
                    <span className="inline-block px-1 rounded border bg-emerald-100 text-emerald-700 border-emerald-400 text-[9px] font-bold leading-3">≤{crewHeadcount - 2}</span>
                    <span className="inline-block px-1 rounded border bg-yellow-100 text-yellow-800 border-yellow-400 text-[9px] font-bold leading-3">{crewHeadcount - 1}–{crewHeadcount}</span>
                    <span className="inline-block px-1 rounded border bg-orange-100 text-orange-800 border-orange-400 text-[9px] font-bold leading-3">{crewHeadcount + 1}–{crewHeadcount + 3}</span>
                    <span className="inline-block px-1 rounded border bg-red-100 text-red-700 border-red-400 text-[9px] font-bold leading-3">{crewHeadcount + 4}+</span>
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap justify-end" data-testid="schedule-toolbar">
              <button
                onClick={() => setGroupByPM(!groupByPM)}
                className={`${TB_BTN} ${groupByPM ? TB_ON : TB_OFF}`}
                title="Group projects by Project Manager"
                data-testid="schedule-group-pm-btn"
              >
                <Users className="h-3.5 w-3.5" /> {groupByPM ? 'By PM' : 'All projects'}
              </button>

              <button
                onClick={() => setShowHiddenProjects(!showHiddenProjects)}
                className={`${TB_BTN} ${showHiddenProjects ? TB_ON : TB_OFF}`}
                title={showHiddenProjects ? 'Show only selected projects' : 'Show all projects to add/remove'}
                data-testid="schedule-show-all-btn"
              >
                {showHiddenProjects ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showHiddenProjects ? 'Selected only' : 'Show all'} <span className="opacity-70">({filteredProjects.length})</span>
              </button>

              <button
                onClick={handleBulletproofRefresh}
                className={`${TB_BTN} ${TB_OFF}`}
                title="Refresh schedule data safely"
                data-testid="schedule-refresh-btn"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>

              {canEdit && (
                <button
                  onClick={() => setShowReconcile(true)}
                  className={`${TB_BTN} ${TB_OFF}`}
                  title="Make past days match the field timesheets (as-worked)"
                  data-testid="schedule-reconcile-btn"
                >
                  <Clock className="h-3.5 w-3.5 text-emerald-700" /> Reconcile
                </button>
              )}

              {user?.role === 'super_admin' && <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const headers = { Authorization: `Bearer ${token}` };
                    // Always preview first — this used to hard-delete every work period keyed by WO number
                    const dry = await axios.delete(`${backendUrl}/api/schedule/cleanup-orphans?dry_run=true`, { headers });
                    const n = dry.data.would_delete || 0;
                    if (!n) { toast.success('No orphaned work periods — nothing to clean'); return; }
                    const sample = (dry.data.preview || []).slice(0, 8).map((w) => `• ${w.project_id} ${w.start_date}→${w.end_date}${w.crew?.length ? ` (${w.crew.join(', ')})` : ''}`).join('\n');
                    if (!window.confirm(`Remove ${n} of ${dry.data.total_work_periods} work periods whose project no longer exists?\n\n${sample}${n > 8 ? `\n…and ${n - 8} more` : ''}\n\nThey are archived and can be undone with "Undo clean".`)) return;
                    const r = await axios.delete(`${backendUrl}/api/schedule/cleanup-orphans`, { headers });
                    toast.success(`Cleaned ${r.data.deleted} orphaned work periods`, {
                      duration: 15000,
                      action: { label: 'Undo', onClick: async () => { const u = await axios.post(`${backendUrl}/api/schedule/undo-cleanup`, { batch: r.data.batch }, { headers }); toast.success(`Restored ${u.data.restored} work periods`); handleBulletproofRefresh(); } },
                    });
                    handleBulletproofRefresh();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                }}
                className={`${TB_BTN} ${TB_OFF} hover:border-red-300 hover:text-red-700`}
                title="Remove work periods with no matching project (previews first, undoable)"
                data-testid="schedule-clean-orphans-btn"
              >
                <Trash2 className="h-3.5 w-3.5" /> Clean orphans
              </button>}

              <span className="w-px h-6 bg-slate-200 mx-1" />

              <div className="inline-flex items-center rounded-full border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="schedule-nav">
                <button onClick={() => navigate(-1)} className="h-8 w-8 flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors" title="Previous" data-testid="schedule-prev-btn">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button onClick={() => setCurrentDate(new Date())} className="h-8 px-3 text-xs font-bold text-slate-800 border-x border-slate-200 hover:bg-slate-100 transition-colors" data-testid="schedule-today-btn">
                  Today
                </button>
                <button onClick={() => navigate(1)} className="h-8 w-8 flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors" title="Next" data-testid="schedule-next-btn">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Day tab (dailies for one day) — replaces the grid while a day tab is picked */}
        {dayTab && (
          <div className="flex-1 min-h-0 overflow-auto px-5 py-4 bg-slate-50" data-testid="schedule-day-tab-panel">
            <SheetTools canEdit={canModifySchedCrew} monthKey={dayTabMonth} onSynced={() => { setDayTabKey(k => k + 1); fetchScheduleData(true, true); }} />
            <DayView key={`${dayTab}-${dayTabKey}`} date={dayTab} canEdit={canEdit} compact onChanged={() => fetchScheduleData(true, true)} />
          </div>
        )}
        {/* Calendar Grid - Single Unified Scroll Container */}
        {!dayTab && <div 
          className="flex-1 flex flex-col min-h-0 overflow-auto" 
          key={`calendar-${renderKey}`}
        >
          <div className="flex flex-col min-w-fit">
            {/* STICKY HEADER ROW */}
            <div className="flex border-b border-slate-200 bg-white/85 backdrop-blur-sm flex-shrink-0 z-30 sticky top-0">
              {/* Sticky Project Column Header */}
              <div className="w-[280px] p-2 border-r border-slate-200 flex-shrink-0 bg-white/85 backdrop-blur-sm">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Project</span>
              </div>
              
              {/* Date Headers */}
              <div className="flex">
                <div className="flex">
                {viewMode === 'week' ? (
                  extendedDays.map((day, dayIdx) => {
                    const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                    const isToday = formatDate(day) === formatDate(new Date());
                    const dayOfWeekLetter = ['M', 'T', 'W', 'T', 'F', 'S', 'S'][(day.getDay() + 6) % 7]; // Monday=0, Sunday=6
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6; // Sunday or Saturday
                    const showMonth = dayIdx === 0 || day.getDate() === 1; // "Nov 30 · 1 Dec" — never guess the month from the number alone
                    return (
                      <div 
                        key={day.toISOString()} 
                        className={`w-[42px] min-w-[42px] p-1 border-r border-slate-200 text-center flex-shrink-0 ${!isCurrentMonth ? 'bg-slate-100/70' : ''} ${isToday ? 'bg-sky-100 shadow-[inset_0_-2px_0_0_#0284c7]' : ''} ${isWeekend && !isToday ? 'bg-slate-100' : ''} ${showMonth && dayIdx > 0 ? 'border-l-2 border-l-slate-400' : ''}`}
                        data-testid={`schedule-day-${formatDate(day)}`}
                      >
                        <div className={`text-xs font-semibold leading-tight ${isCurrentMonth ? 'text-slate-900' : 'text-slate-400'} ${isToday ? 'text-sky-800' : ''}`}>
                          {showMonth && <span className="block text-[8px] font-bold uppercase tracking-wide text-slate-500 -mb-0.5">{day.toLocaleDateString('en-US', { month: 'short' })}</span>}
                          {day.getDate()}
                        </div>
                        <div className={`text-[9px] font-medium ${isCurrentMonth ? 'text-slate-600' : 'text-slate-500'} ${isToday ? 'text-blue-700' : ''}`}>
                          {dayOfWeekLetter}
                        </div>
                        {manpowerBadge(day)}
                      </div>
                    );
                  })
                ) : (
                  ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayName) => (
                    <div key={dayName} className="min-w-[200px] p-4 border-r border-slate-200 text-center flex-shrink-0">
                      <span className="font-medium text-slate-900">{dayName}</span>
                    </div>
                  ))
                )}
                </div>
              </div>
            </div>

            {/* SCROLLABLE BODY - Project Rows */}
            <div className="flex-1 min-h-0">
            {viewMode === 'week' ? (
              // Weekly View
              Object.entries(groupedProjects).map(([pmName, pmProjects]) => (
                <React.Fragment key={pmName}>
                  {/* PM Header Row */}
                  {groupByPM && (
                    <div className="flex border-b border-sky-200 bg-sky-50/90 sticky top-0 z-10">
                      <div className="w-[280px] p-2 flex-shrink-0 flex items-center justify-between border-r border-sky-200">
                        <div>
                          <span className="font-bold text-sky-900 text-sm">{pmName}</span>
                          <span className="text-[11px] text-sky-700 ml-2">{pmProjects.length} project{pmProjects.length !== 1 ? 's' : ''}</span>
                        </div>
                        {canEdit && <button
                          onClick={() => createTempScheduleObject(pmName)}
                          className="p-1 hover:bg-sky-100 rounded text-sky-700 hover:text-sky-900 transition-colors"
                          title="Add temporary scheduling object"
                          data-testid={`pm-add-temp-${pmName}`}
                        >
                          <Plus className="h-4 w-4" />
                        </button>}
                      </div>
                      <div className="flex-1 p-2 bg-sky-50/90">
                        {/* Empty space for calendar alignment */}
                      </div>
                    </div>
                  )}
                  {/* Projects for this PM */}
                  {pmProjects.length === 0 ? (
                    // Show empty placeholder row if PM has no projects
                    <div className="flex border-b border-slate-200" style={{ height: '28px', minHeight: '28px', marginBottom: '2px' }}>
                      <div className="w-[280px] p-2 border-r border-slate-200 backdrop-blur-sm flex-shrink-0 bg-slate-900/10">
                        <span className="text-xs text-slate-500 italic">No projects</span>
                      </div>
                      <div className="flex-1 bg-slate-900/5">
                        {/* Empty calendar space */}
                      </div>
                    </div>
                  ) : (
                    pmProjects.map((project) => {
                const isHidden = !hiddenProjects.has(project.project_number);
                const isTemp = project.isTemp;
                return (
                <div key={project.id} className="flex border-b border-slate-200" style={{ height: '28px', minHeight: '28px', marginBottom: '2px' }}>
                  {/* Sticky Project Column */}
                  <div className={`w-[280px] p-2 border-r border-slate-200 backdrop-blur-sm flex-shrink-0 ${isTemp ? 'bg-blue-50/50' : isHidden ? 'bg-gray-300/50' : 'bg-slate-900/10'}`}>
                    {isTemp ? (
                      /* Temporary Scheduling Object */
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-xs text-blue-700">•</span>
                          {project.isEditing ? (
                            <input
                              type="text"
                              placeholder="Enter description (e.g., Shop - Millwrights)"
                              value={project.title}
                              onChange={(e) => updateTempObjectTitle(project.id, e.target.value)}
                              onBlur={() => finishEditingTempObject(project.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                              }}
                              autoFocus
                              className="flex-1 px-2 py-1 text-xs border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white"
                            />
                          ) : (
                            <div className="flex-1 flex items-center gap-2">
                              <span className="font-medium text-xs text-slate-700 italic">
                                {project.title || 'Temporary Schedule Object'}
                              </span>
                              <button
                                onClick={() => toggleTempObjectEdit(project.id)}
                                className="p-0.5 hover:bg-blue-50 rounded text-blue-600 opacity-60 hover:opacity-100"
                                title="Edit description"
                              >
                                <Edit className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => deleteTempScheduleObject(project.id)}
                          className="p-0.5 hover:bg-red-50 rounded text-red-500 hover:text-red-700 flex-shrink-0 opacity-60 hover:opacity-100"
                          title="Delete temporary object"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ) : (
                      /* Single Line Compact View */
                      <div className="flex items-center justify-between gap-1 h-full">
                        <div 
                          className="flex items-center gap-1 flex-1 min-w-0 cursor-pointer hover:bg-slate-900/10 rounded px-1 transition-colors h-full"
                          onClick={() => {
                            const projectWPs = workPeriods.filter(wp => wp.project_id === project.id || wp.project_id === project.project_number);
                            if (projectWPs.length > 0) {
                              focusProjectWorkPeriod(projectWPs);
                            } else {
                              // No work periods — go to project page
                              window.location.href = `/projects/${project.project_number}`;
                            }
                          }}
                          title={`${project.project_number} - ${project.title} (${project.client_name}) — Click to view work period`}
                        >
                          {isHidden && <span className="text-xs">👁️‍🗨️</span>}
                          <span className={`font-bold text-xs whitespace-nowrap ${isHidden ? 'text-slate-600' : 'text-slate-900'}`}>
                            {project.project_number}
                          </span>
                          {project.placeholder && (
                            <span className="text-[9px] font-bold uppercase tracking-wide rounded px-1 bg-amber-100 text-amber-800 border border-amber-300 whitespace-nowrap" title="This WO is on the dispatch sheet but not in the WO book yet — details fill in once it's added there" data-testid={`project-placeholder-${project.project_number}`}>sheet only</span>
                          )}
                          <span className={`text-xs truncate ${isHidden ? 'text-slate-500' : 'text-slate-700'}`}>
                            {project.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {(() => {
                            const wpCount = workPeriods.filter(wp => wp.project_id === project.id || wp.project_id === project.project_number).length;
                            if (wpCount > 0) {
                              return (
                                <button onClick={(e) => {
                                  e.stopPropagation();
                                  focusProjectWorkPeriod(workPeriods.filter(wp => wp.project_id === project.id || wp.project_id === project.project_number));
                                }} className="text-emerald-500 hover:text-emerald-700 px-0.5" title={`${wpCount} work period(s) — click to view`}>
                                  <span className="text-[10px]">📋{wpCount}</span>
                                </button>
                              );
                            }
                            return null;
                          })()}
                          {canSchedule && (
                            <button onClick={(e) => { e.stopPropagation(); setProjectionFor(project); }}
                              className="p-0.5 rounded flex-shrink-0 text-amber-600 hover:bg-amber-100 hover:text-amber-800 font-black text-[11px] leading-none w-4 h-4 inline-flex items-center justify-center"
                              title="Projection — drop a headcount on this job before the crew is named" data-testid={`projection-add-${project.project_number || project.id}`}>+</button>
                          )}
                          <button
                            onClick={() => toggleProjectVisibility(project.id)}
                          className={`p-0.5 hover:bg-white/50 rounded flex-shrink-0 ${isHidden ? 'text-blue-600' : 'text-slate-600'}`}
                        >
                          {isHidden ? <span className="text-xs">👁️</span> : <X className="h-2.5 w-2.5" />}
                        </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Calendar Days Container - No individual scrollbars */}
                  <div className="flex-1">
                    <div className="flex">
                      {extendedDays.map((day) => {
                      const workPeriod = getWorkPeriodForCell(project.id || project.project_number, day);
                      const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6; // Sunday or Saturday
                      // Show work period on the first day it appears in the extended view
                      const isWorkPeriodStart = workPeriod && (
                        formatDate(new Date(workPeriod.start_date)) === formatDate(day) || // Starts on this day
                        (new Date(workPeriod.start_date) < day && formatDate(day) === formatDate(extendedDays[0])) // Started earlier but this is first day of extended range
                      );
                      const workPeriodDuration = workPeriod ? 
                        Math.ceil((new Date(workPeriod.end_date) - new Date(workPeriod.start_date)) / (1000 * 60 * 60 * 24)) + 1 : 1;
                      
                      // Calculate content height for this cell based on resources
                      const resourceCount = workPeriod?.resources?.length || 0;
                      const badgeRows = Math.ceil(resourceCount / 4); // Allow 4 badges per row
                      const baseHeight = compactView ? 50 : 120; // Much shorter in compact view
                      const extraHeight = compactView ? 0 : (badgeRows > 1 ? (badgeRows - 1) * 35 : 0); // No extra height in compact
                      const minHeight = `${baseHeight + extraHeight}px`;
                      
                      // Determine background color based on weekend status
                      const bgClass = isWeekend 
                        ? (isCurrentMonth ? 'bg-slate-100/80 hover:bg-slate-200/60' : 'bg-slate-100/80 hover:bg-slate-200/60 opacity-60')
                        : (isCurrentMonth ? 'bg-white/60 hover:bg-sky-50' : 'bg-white/40 hover:bg-sky-50 opacity-60');

                      // Drag crosshair highlight: strong on the hovered cell, lighter
                      // across the whole hovered row (project) and column (date).
                      const cellProjectKey = project.id || project.project_number;
                      const cellDateKey = formatDate(day);
                      const isHoverCell = dragHover && dragHover.projectKey === cellProjectKey && dragHover.dateKey === cellDateKey;
                      const isHoverRow = dragHover && dragHover.projectKey === cellProjectKey;
                      const isHoverCol = dragHover && dragHover.dateKey === cellDateKey;
                      const highlightStyle = isHoverCell
                        ? { backgroundColor: dragHover.conflict ? 'rgba(251,191,36,0.55)' : 'rgba(59,130,246,0.55)',
                            boxShadow: dragHover.conflict ? 'inset 0 0 0 2px rgba(245,158,11,0.95)' : 'inset 0 0 0 2px rgba(59,130,246,0.95)' }
                        : (isHoverRow || isHoverCol ? { backgroundColor: 'rgba(96,165,250,0.18)' } : {});

                      return (
                        <div
                          key={`${project.id || project.project_number}-${day.toISOString()}`}
                          className={`w-[42px] min-w-[42px] p-0 border-r border-slate-200 relative flex flex-col ${bgClass} transition-colors flex-shrink-0 overflow-visible`}
                          style={{ height: '28px', minHeight: '28px', overflow: 'visible', zIndex: isHoverCell ? 15 : (workPeriod && isWorkPeriodStart ? 5 : 'auto'), ...highlightStyle }}
                      data-date={formatDate(day)}
                      data-project={project.project_number || project.id}
                      onDragOver={handleDragOver}
                      onDragEnter={() => handleCellDragEnter(project, day)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDropOnCalendar(e, project.id || project.project_number, day)}
                      onMouseEnter={(e) => {
                        // Show tooltip when hovering over any day cell that has a work period
                        if (workPeriod && compactView) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top });
                          setHoveredWorkPeriod(workPeriod);
                          setHoveredDay(day);
                        }
                      }}
                      onMouseLeave={() => {
                        if (workPeriod && compactView) {
                          setHoveredWorkPeriod(null);
                          setHoveredDay(null);
                        }
                      }}
                      onClick={async (e) => {
                        // Check for Ctrl+Click to exclude day from work period
                        if (e.ctrlKey && workPeriod) {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          const clickedDate = formatDate(day);
                          const wpStart = formatDate(new Date(workPeriod.start_date));
                          const wpEnd = formatDate(new Date(workPeriod.end_date));
                          
                          // Don't allow excluding the start or end day
                          if (clickedDate === wpStart || clickedDate === wpEnd) {
                            toast.error('Cannot exclude the first or last day of a work period');
                            return;
                          }
                          
                          // Confirm with user
                          if (window.confirm(`Exclude ${clickedDate} from this work period?\n\nThis will split the work period into two separate periods.`)) {
                            await handleExcludeDay(workPeriod, day);
                          }
                          return;
                        }
                        
                        // Alternative method: click to assign selected resource
                        if (draggedItem && draggedItemType) {
                          // Create a fake event object for handleDropOnCalendar
                          const fakeEvent = { preventDefault: () => {} };
                          await handleDropOnCalendar(fakeEvent, project.id, day);
                        } else {
                          // No resource selected
                        }
                      }}
                    >
                      {(() => {
                        const shouldRender = workPeriod && isWorkPeriodStart;
                        // Debug statement removed for performance
                        return shouldRender;
                      })() && (
                        <div 
                          className={`wp-bubble absolute rounded-md transition-all duration-150 ${
                            isDraggingWorkPeriod && draggedWorkPeriod?.id === workPeriod.id 
                              ? 'opacity-50 shadow-xl border-dashed' 
                              : 'hover:shadow-lg'
                          }`}
                          style={(() => { const t = wpTone(workPeriod); return {
                            width: `calc(${workPeriodDuration * 100}% - 4px)`,
                            height: '22px',
                            top: '3px',
                            left: '2px',
                            zIndex: 20,
                            pointerEvents: 'auto',
                            background: t.bg,
                            border: t.border,
                            borderLeft: `4px solid ${t.edge}`,
                            color: t.text,
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)'
                          }; })()}
                          data-work-period-id={workPeriod.id}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            // Don't open modal on mousedown - wait for click
                          }}
                          onClick={(e) => {
                            // Only open modal if not resizing
                            if (!isResizing && !justResized) {
                              e.stopPropagation();
                              setSelectedWorkPeriod(workPeriod);
                              setIsDetailModalOpen(true);
                            }
                          }}
                        >
                          {/* LEFT RESIZE HANDLE — appears on bubble hover only */}
                          {canEdit && <div
                            className="resize-handle wp-resize-handle absolute left-0 top-0 bottom-0 cursor-col-resize z-50 transition-all duration-200"
                            style={{
                              width: '7px',
                              height: '100%',
                              position: 'absolute',
                              left: '0px',
                              top: '0',
                              zIndex: 50,
                              cursor: 'col-resize',
                              backgroundColor: wpTone(workPeriod).edge,
                              borderRadius: '3px 0 0 3px'
                            }}
                            title="◀ Drag to resize start date (full days only)"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              handleBulletproofResize(e, workPeriod, 'left');
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />}
                          
                          {/* RIGHT RESIZE HANDLE — appears on bubble hover only */}
                          {canEdit && <div
                            className="resize-handle wp-resize-handle absolute right-0 top-0 bottom-0 cursor-col-resize z-50 transition-all duration-200"
                            style={{
                              width: '7px',
                              height: '100%',
                              position: 'absolute',
                              right: '0px',
                              top: '0',
                              zIndex: 50,
                              cursor: 'col-resize',
                              backgroundColor: wpTone(workPeriod).edge,
                              borderRadius: '0 3px 3px 0'
                            }}
                            title="Drag to resize end date (full days only) ▶"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              handleBulletproofResize(e, workPeriod, 'right');
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />}
                          
                          {/* Main content area - CLICK TO OPEN MODAL, SHIFT+CLICK TO DRAG */}
                          <div 
                            className="absolute cursor-pointer"
                            style={{ 
                              inset: '2px',
                              overflow: 'visible',
                              zIndex: 21
                            }}
                            onMouseDown={async (e) => {
                              if (!canEdit) return;
                              
                              // Check for Ctrl+Click to exclude day from work period
                              if (e.ctrlKey) {
                                console.log('🔑 Ctrl key detected in mouseDown!');
                                e.preventDefault();
                                e.stopPropagation();
                                
                                // CRITICAL FIX: Calculate which actual day was clicked based on mouse position
                                // The work period bar spans multiple days, but 'day' is always the start day
                                const rect = e.currentTarget.getBoundingClientRect();
                                const clickX = e.clientX - rect.left; // Click position within the work period bar
                                const cellWidth = 42; // Width of each day cell
                                const dayOffset = Math.floor(clickX / cellWidth); // Which day cell was clicked (0 = first, 1 = second, etc)
                                
                                // Calculate the actual clicked date
                                const actualClickedDay = new Date(workPeriod.start_date);
                                actualClickedDay.setDate(actualClickedDay.getDate() + dayOffset);
                                const clickedDate = formatDate(actualClickedDay);
                                
                                const wpStart = workPeriod.start_date;
                                const wpEnd = workPeriod.end_date;
                                
                                console.log('📅 Checking dates:', { 
                                  clickedDate, 
                                  wpStart, 
                                  wpEnd,
                                  dayOffset,
                                  clickX,
                                  isStart: clickedDate === wpStart,
                                  isEnd: clickedDate === wpEnd
                                });
                                
                                // Don't allow excluding the start or end day
                                if (clickedDate === wpStart || clickedDate === wpEnd) {
                                  console.log('❌ Blocked: Clicked date matches start or end');
                                  toast.error('Cannot exclude the first or last day of a work period');
                                  return;
                                }
                                
                                console.log('✅ Valid day to exclude, showing confirmation...');
                                
                                // Confirm with user
                                if (window.confirm(`Exclude ${clickedDate} from this work period?\n\nThis will split the work period into two separate periods.`)) {
                                  console.log('✅ User confirmed, calling handleExcludeDay');
                                  await handleExcludeDay(workPeriod, actualClickedDay);
                                }
                                return;
                              }
                              
                              // CRITICAL FIX: Check if click is on dispatch button or remove resource button - if so, don't open modal
                              if (e.target.closest('.dispatch-button') || 
                                  e.target.closest('.remove-resource-btn') || 
                                  e.target.closest('[data-remove-button]') ||
                                  e.target.hasAttribute('data-remove-button')) {

                                return;
                              }
                              
                              e.preventDefault();
                              e.stopPropagation();
                              
                              // Check if Shift key is held for dragging
                              if (e.shiftKey && !isResizing) {

                                handleBulletproofWorkPeriodDrag(e, workPeriod);
                              }
                            }}
                            onClick={(e) => {
                              // Regular click - open modal (unless ctrl/shift/resize)
                              if (!e.ctrlKey && !e.shiftKey && !isResizing && !justResized) {
                                e.stopPropagation();
                                setSelectedWorkPeriod(workPeriod);
                                setIsDetailModalOpen(true);
                              }
                            }}
                            title="Ctrl+Click to exclude day • Click to edit • Shift+Drag to move"
                          >
                            <div className="flex items-center justify-center h-full" style={{ overflow: 'visible' }}>
                              {!compactView && (
                                <div className="flex justify-between items-start mb-2">
                                  <div className="text-xs font-semibold text-slate-900">
                                    {workPeriod.start_time} - {workPeriod.end_time}
                                  </div>
                                  <DispatchStatusIndicator 
                                    workPeriod={workPeriod} 
                                    dispatchStatuses={dispatchStatuses} 
                                  />
                                </div>
                              )}
                              <div className={`flex flex-wrap gap-1 ${compactView ? '' : ''}`}>
                                {!compactView && workPeriod.status === 'cancelled' && (
                                  <div className="bg-red-700 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded tracking-wide" data-testid={`wp-cancelled-chip-${workPeriod.id}`}>
                                    CANCELLED
                                  </div>
                                )}
                                {workPeriod.resources && workPeriod.resources.length > 0 ? (
                                  compactView ? (() => {
                                    // Compact View — crew count badge(s). When crew members have their own
                                    // start/end dates, show a count per segment so headcount changes are visible.
                                    const crewRes = workPeriod.resources.filter(r => r.type === 'crew');
                                    const dayList = [];
                                    const d0 = new Date(workPeriod.start_date + 'T00:00:00');
                                    for (let di = 0; di < workPeriodDuration; di++) {
                                      const dd = new Date(d0); dd.setDate(d0.getDate() + di);
                                      dayList.push(formatDate(dd));
                                    }
                                    const countOn = (ds) => {
                                      const plan = workPeriod.plan_by_day && workPeriod.plan_by_day[ds];
                                      if (plan != null) return Number(plan); // the sheet's own cell for this day
                                      return crewRes.filter(r =>
                                        (!r.start_date || r.start_date <= ds) && (!r.end_date || r.end_date >= ds)
                                        && !((workPeriod.excluded_dates || {})[ds] || []).includes(`crew_${r.id}`)).length;
                                    };
                                    const counts = dayList.map(countOn);
                                    const varying = counts.some(c => c !== counts[0]);
                                    if (!varying) {
                                      return (
                                        <div
                                          className={`absolute inset-0 flex items-center justify-center gap-1 overflow-hidden whitespace-nowrap px-0.5 cursor-pointer ${workPeriod.status === 'cancelled' ? 'line-through' : ''}`}
                                          onMouseEnter={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top });
                                            setHoveredWorkPeriod(workPeriod);
                                            setHoveredDay(day);
                                          }}
                                          onMouseLeave={() => {
                                            setHoveredWorkPeriod(null);
                                            setHoveredDay(null);
                                          }}
                                        >
                                          <WpCount tone={wpTone(workPeriod)} testId={`wp-count-${workPeriod.id}`}>
                                            {counts[0]}{workPeriod.dispatch_status === 'tentative' ? '?' : ''}
                                          </WpCount>
                                          <WpStatusChip workPeriod={workPeriod} dispatchStatuses={dispatchStatuses} duration={workPeriodDuration} />
                                        </div>
                                      );
                                    }
                                    // Segments of consecutive days with the same headcount
                                    const segs = [];
                                    counts.forEach((c, i) => {
                                      if (i === 0 || c !== counts[i - 1]) segs.push({ start: i, len: 1, count: c });
                                      else segs[segs.length - 1].len++;
                                    });
                                    return (
                                      <div className="absolute inset-0 flex items-center rounded-md overflow-hidden" data-testid={`wp-varying-crew-${workPeriod.id}`}>
                                        {segs.map((sg, si) => (
                                          <div key={si} style={{ width: `${sg.len * 42}px`, ...(si > 0 ? { borderLeft: `1px solid ${wpTone(workPeriod).edge}66` } : {}) }}
                                            className="flex items-center justify-center h-full"
                                            onMouseEnter={(e) => {
                                              const rect = e.currentTarget.getBoundingClientRect();
                                              setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top });
                                              setHoveredWorkPeriod(workPeriod);
                                              setHoveredDay(new Date(dayList[sg.start] + 'T00:00:00'));
                                            }}
                                            onMouseLeave={() => {
                                              setHoveredWorkPeriod(null);
                                              setHoveredDay(null);
                                            }}
                                          >
                                            <WpCount tone={wpTone(workPeriod)}>{sg.count}</WpCount>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  })() : (
                                  <>
                                    {sortResourcesByPriority(workPeriod.resources).map((resource, idx) => {
                                      const getResourceColor = (type) => {
                                        const resourceType = type?.toLowerCase() || '';
                                        if (resourceType.includes('truck')) return 'bg-green-600';
                                        if (resourceType.includes('trailer')) return 'bg-orange-600';  
                                        if (resourceType.includes('welder')) return 'bg-purple-600';
                                        if (resourceType.includes('crew') || resourceType.includes('employee')) return 'bg-blue-600';
                                        return 'bg-slate-200';
                                      };
                                      
                                      const isCrew = resource.type === 'crew';
                                      const isForeman = isCrew && workPeriod.foreman_id === resource.id;
                                      
                                      return (
                                        <div 
                                          key={`${resource.type}-${resource.id}-${idx}`} 
                                          className={`${getResourceColor(resource.type)} text-white text-xs px-2 py-1 rounded flex-shrink-0 mb-1 flex items-center gap-1 group relative mr-2`}
                                          title={`${isForeman ? '👑 ' : ''}${resource.name} (${resource.type})`}
                                          style={{marginRight: '8px'}} // Extra space to prevent X overlap
                                        >
                                          <span className="pr-1">
                                            {isForeman && '👑 '}
                                            {resource.name && resource.name.length > 6 ? resource.name.substring(0, 6) + '...' : resource.name || 'Unknown'}
                                          </span>
                                          {/* REMOVE BUTTON - POSITIONED WITHIN BADGE BOUNDARIES */}
                                          <button
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              e.nativeEvent?.stopImmediatePropagation();
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              e.nativeEvent?.stopImmediatePropagation();

                                              handleRemoveResource(workPeriod.id, resource.id, resource.type, resource.name);
                                            }}
                                            className="remove-resource-btn bg-red-500 hover:bg-red-600 text-white rounded-full w-3 h-3 flex items-center justify-center font-bold transition-all ml-1 flex-shrink-0"
                                            title={`Remove ${resource.name}`}
                                            data-remove-button="true"
                                            style={{
                                              fontSize: '8px',
                                              lineHeight: '1',
                                              zIndex: 50
                                            }}
                                          >
                                            ×
                                          </button>
                                        </div>
                                      );
                                    })}
                                    {workPeriod.resources.length > 1 && (
                                      <div className="text-xs text-slate-900 bg-slate-100 px-2 py-1 rounded opacity-75">
                                        ({workPeriod.resources.length} total)
                                      </div>
                                    )}
                                  </>
                                  )
                                ) : (workPeriod.source === 'projection' || workPeriod.crew_tbd || workPeriod.sheet_plan) && workPeriod.planned_crew ? (
                                  <div className="absolute inset-0 flex items-center justify-center cursor-pointer" data-testid={`wp-projection-${workPeriod.id}`}
                                    onMouseEnter={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top }); setHoveredWorkPeriod(workPeriod); setHoveredDay(day); }}
                                    onMouseLeave={() => { setHoveredWorkPeriod(null); setHoveredDay(null); }}>
                                    <WpCount tone={WP_TONES.tentative} testId={`wp-count-${workPeriod.id}`}>{workPeriod.planned_crew}?</WpCount>
                                  </div>
                                ) : (
                                  <div className="text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap" style={{ color: wpTone(workPeriod).text, background: 'rgba(255,255,255,0.6)' }}>
                                    {workPeriod.source === 'manpower_sheet' ? 'sheet — no names yet' : 'No resources'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        )}
                      
                      {/* One small ⏱ per bar (on its first day) when it was rebuilt / adjusted from timesheets */}
                      {workPeriod && (workPeriod.as_worked || workPeriod.timesheet_adjustments?.length > 0) && formatDate(day) === workPeriod.start_date && (() => {
                        const adj = workPeriod.timesheet_adjustments || [];
                        const title = (workPeriod.as_worked ? 'As worked — rebuilt from the timesheets' : 'Adjusted from timesheets')
                          + (adj.length ? ':\n' + adj.slice(0, 12).map(a => `${a.action === 'added' ? '+' : '−'} ${a.employee_name} ${a.date}`).join('\n') + (adj.length > 12 ? `\n…${adj.length - 12} more` : '') : '');
                        return (
                          <div className="absolute bottom-0 left-0 z-30 bg-emerald-700 text-white text-[8px] font-bold leading-none px-1 py-0.5 rounded-tr-md rounded-bl-md"
                            title={title} data-testid={`wp-ts-adjusted-${workPeriod.id}`}>⏱</div>
                        );
                      })()}

                      {/* Exclusion indicator for this specific day (as-worked bars show headcount instead) */}
                      {workPeriod && workPeriod.excluded_dates && !workPeriod.as_worked && (() => {
                        const dateStr = formatDate(day);
                        const hasExclusions = workPeriod.excluded_dates[dateStr] && workPeriod.excluded_dates[dateStr].length > 0;
                        return hasExclusions ? (
                          <div 
                            className="absolute top-0 right-0 w-3 h-3 bg-orange-500 rounded-bl-lg z-30"
                            title={`${workPeriod.excluded_dates[dateStr].length} resource(s) excluded on this day`}
                            style={{
                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                            }}
                          >
                            <div className="absolute top-0 right-0 w-full h-full flex items-center justify-center text-white text-[8px] font-bold">
                              !
                            </div>
                          </div>
                        ) : null;
                      })()}
                      
                      {/* Drop zone indicator */}
                      {!workPeriod && (
                        <div className="absolute inset-1 border-2 border-dashed border-slate-300 rounded-lg opacity-0 hover:opacity-100 transition-opacity">
                          <div className="flex items-center justify-center h-full text-gray-500 text-xs">
                            Drop here
                          </div>
                        </div>
                      )}
                        </div>
                      );
                    })}
                    </div>
                  </div>
                </div>
              );
              }))}
                  </React.Fragment>
                ))
            ) : (
              // Monthly View - Calendar Grid
              <div className="grid grid-cols-7 gap-0">
                {getMonthDays().map((day, index) => {
                  const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                  const isToday = formatDate(day) === formatDate(new Date());
                  
                  // Get all work periods for this day across all projects
                  const dayWorkPeriods = workPeriods.filter(wp => {
                    // Create new date objects to avoid mutating originals
                    const wpStart = new Date(wp.start_date);
                    const wpEnd = new Date(wp.end_date);
                    const currentDay = new Date(day);
                    
                    // Set all times to midnight for accurate date-only comparison
                    wpStart.setHours(0, 0, 0, 0);
                    wpEnd.setHours(23, 59, 59, 999); // End of day for end date
                    currentDay.setHours(12, 0, 0, 0); // Noon for current day
                    
                    return currentDay >= wpStart && currentDay <= wpEnd;
                  });
                  
                  return (
                    <div
                      key={day.toISOString()}
                      className={`min-h-[120px] border border-gray-300 p-2 ${
                        isCurrentMonth ? 'bg-white' : 'bg-gray-100 opacity-70'
                      } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                      data-date={formatDate(day)}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => {
                        // For monthly view, try to find the first project or create a default
                        const firstProject = projects[0];
                        if (firstProject) {
                          handleDropOnCalendar(e, firstProject.id, day);
                        }
                      }}
                    >
                      {/* Day number + manpower total */}
                      <div className={`text-sm font-medium mb-2 flex items-center gap-1.5 ${
                        isCurrentMonth ? 'text-gray-900' : 'text-gray-500'
                      } ${isToday ? 'text-blue-600' : ''}`}>
                        {day.getDate()}
                        {(headerCounts[formatDate(day)] || 0) > 0 && (
                          <span className="text-[10px] font-bold px-1.5 rounded border bg-emerald-100 text-emerald-700 border-emerald-400"
                            title={`${headerCounts[formatDate(day)]} crew scheduled`}>
                            {headerCounts[formatDate(day)]}
                          </span>
                        )}
                      </div>
                      
                      {/* Work periods for this day */}
                      <div className="space-y-1">
                        {dayWorkPeriods.slice(0, 3).map((wp, wpIndex) => {
                          const project = projects.find(p => p.id === wp.project_id || p.project_number === wp.project_id);
                          return (
                            <div
                              key={`${wp.id}-${wpIndex}`}
                              className="bg-blue-600 text-white text-xs px-2 py-1 rounded cursor-pointer hover:bg-blue-700 flex justify-between items-center"
                              onClick={() => {
                                setSelectedWorkPeriod(wp);
                                setIsDetailModalOpen(true);
                              }}
                              title={`${project?.project_number || wp.project_id} - ${wp.start_time}-${wp.end_time}`}
                            >
                              <span>{project?.project_number || wp.project_id}</span>
                              <DispatchStatusIndicator 
                                workPeriod={wp} 
                                dispatchStatuses={dispatchStatuses} 
                              />
                            </div>
                          );
                        })}
                        {dayWorkPeriods.length > 3 && (
                          <div className="text-xs text-slate-500">
                            +{dayWorkPeriods.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </div>

            {/* Upcoming Vacations (Google Calendar + in-app markers) — sticky so it doesn't stretch with the grid */}
            <div className="px-4 py-4 sticky left-0" style={{ width: 'min(1100px, calc(100vw - 400px))' }}>
              <UpcomingVacations />
            </div>
          </div>
        </div>}
        <ScheduleDayTabs monthKey={dayTabMonth} onMonth={setDayTabMonth} active={dayTab} onPick={setDayTab} counts={headerCounts} />
      </div>

      {/* Work Period Detail Modal */}
      {isDetailModalOpen && selectedWorkPeriod && !canEdit && (
        <WorkPeriodPeek workPeriod={selectedWorkPeriod} project={(projects || []).find(p => p.id === selectedWorkPeriod.project_id || p.project_number === selectedWorkPeriod.project_id)} onClose={() => setIsDetailModalOpen(false)} />
      )}
      {isDetailModalOpen && selectedWorkPeriod && canEdit && (
        <WorkPeriodDetailModal
          workPeriod={selectedWorkPeriod}
          onClose={() => setIsDetailModalOpen(false)}
          onUpdate={updateWorkPeriod}
          onDelete={deleteWorkPeriod}
          onDispatch={handleDispatchWorkPeriod}
          onDispatchOne={sendIndividualDispatch}
          onDispatchStatusRefresh={fetchDispatchStatus}
          onCancelJob={(wp) => setCancelJobWP(wp)}
          onRebookJob={(wp) => setRebookWP(wp)}
          onModifyCrew={canModifySchedCrew ? openModifyCrew : undefined}
          onCrewSwapped={handleCrewChanged}
          dispatchStatuses={dispatchStatuses}
          formatDate={formatDate}
          projects={projects}
        />
      )}

      {renderCrewModals()}
      {showReconcile && (
        <ScheduleReconcileModal onClose={() => setShowReconcile(false)} onApplied={handleBulletproofRefresh} />
      )}
      {/* Job Cancelled confirm + crew SMS */}
      {cancelJobWP && (
        <CancelJobModal
          workPeriodId={cancelJobWP.id}
          onClose={() => setCancelJobWP(null)}
          onCancelled={() => {
            setCancelJobWP(null);
            setIsDetailModalOpen(false);
            setWorkPeriods(prev => prev.map(wp => wp.id === cancelJobWP.id ? { ...wp, status: 'cancelled' } : wp));
          }}
        />
      )}
      
      {/* Re-book a cancelled job onto new dates */}
      {rebookWP && (
        <RebookJobModal
          workPeriodId={rebookWP.id}
          jobLabel={`Was scheduled ${rebookWP.start_date} to ${rebookWP.end_date}`}
          onClose={() => setRebookWP(null)}
          onRebooked={() => {
            setRebookWP(null);
            setIsDetailModalOpen(false);
            handleBulletproofRefresh();
          }}
        />
      )}

      {/* SMS Dispatch Confirmation Modal */}
      {showDispatchConfirm && selectedWorkPeriod && (
        <DispatchConfirmationModal
          workPeriod={selectedWorkPeriod}
          dispatchMessage={dispatchMessage}
          setDispatchMessage={setDispatchMessage}
          onConfirm={sendDispatch}
          onCancel={() => setShowDispatchConfirm(false)}
          projects={projects}
        />
      )}
      
      {projectionFor && (
        <ProjectionDialog project={projectionFor} onClose={() => setProjectionFor(null)} onCreated={() => handleBulletproofRefresh()} />
      )}

      {/* Tooltip Portal - renders above everything */}
      <TooltipPortal 
        workPeriod={hoveredWorkPeriod} 
        hoveredDay={hoveredDay}
        position={tooltipPosition}
        sortResourcesByPriority={sortResourcesByPriority}
      />
      
      {/* Conflict Resolution Modal */}
      <ConflictResolutionModal
        show={showConflictModal}
        conflictData={conflictData}
        onConfirm={handleConfirmReassignment}
        onCancel={() => {
          setShowConflictModal(false);
          setConflictData(null);
          setDraggedItem(null);
          setDraggedItemType(null);
        }}
      />
    </div>
  );
};

// Work Period Detail Modal Component
const localDateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// "Off starting / Starting" chooser used by remove, add and swap. value '' = whole bar.
const WhenChips = ({ label, value, onChange, wpStart, wpEnd, wholeLabel, testPrefix }) => {
  const today = localDateKey(new Date());
  const tomorrow = localDateKey(new Date(Date.now() + 86400000));
  const nm = new Date(); nm.setDate(nm.getDate() + ((8 - nm.getDay()) % 7 || 7));
  const nextMonday = localDateKey(nm);
  const inBar = (d) => d >= wpStart && d <= wpEnd;
  const quick = [['Today', today], ['Tomorrow', tomorrow], ['Next Monday', nextMonday]].filter(([, d]) => inBar(d));
  const isQuick = quick.some(([, d]) => d === value);
  const [custom, setCustom] = useState(!!value && !isQuick);
  const chip = (active) => `px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-yellow-400 border-yellow-400 text-gray-900' : 'bg-white border-slate-300 text-slate-800 hover:border-yellow-400'}`;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testPrefix}-when`}>
      <span className="text-xs text-slate-700 mr-1">{label}</span>
      {quick.map(([lbl, d]) => (
        <button key={lbl} type="button" className={chip(value === d && !custom)} onClick={() => { setCustom(false); onChange(d); }}
          data-testid={`${testPrefix}-when-${lbl.toLowerCase().replace(' ', '-')}`}>{lbl} <span className="opacity-70">{d.slice(5).replace('-', '/')}</span></button>
      ))}
      <button type="button" className={chip(custom)} onClick={() => { setCustom(true); if (!value) onChange(''); }} data-testid={`${testPrefix}-when-pick`}>Pick a date</button>
      <button type="button" className={chip(!value && !custom)} onClick={() => { setCustom(false); onChange(''); }} data-testid={`${testPrefix}-when-whole`}>{wholeLabel}</button>
      {custom && (
        <input type="date" value={value} min={wpStart} max={wpEnd} onChange={(e) => onChange(e.target.value)}
          className="bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs text-slate-900" data-testid={`${testPrefix}-when-input`} />
      )}
    </div>
  );
};

const WorkPeriodDetailModal = ({ workPeriod, onClose, onUpdate, onDelete, onDispatch, onDispatchOne, onDispatchStatusRefresh, onCancelJob, onRebookJob, onModifyCrew, onCrewSwapped, dispatchStatuses, formatDate, projects }) => {
  const [formData, setFormData] = useState({
    start_date: workPeriod.start_date,
    end_date: workPeriod.end_date,
    start_time: workPeriod.start_time || '07:00',
    end_time: workPeriod.end_time || '15:00',
    foreman_id: workPeriod.foreman_id || null,
    rental_equipment: workPeriod.rental_equipment || [],
    notes: workPeriod.notes || '',
    dispatch_status: workPeriod.dispatch_status || 'confirmed',
    site_contact: workPeriod.site_contact || '',
    tool_list: workPeriod.tool_list || '',
    site_address: workPeriod.site_address || '',
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const notesTimeoutRef = React.useRef(null);
  const pendingSaveRef = React.useRef({});
  const [newRental, setNewRental] = useState({ equipment_name: '', vendor: '', arrival_time: '', needs_offrent_reminder: false });
  const [woInput, setWoInput] = useState('');
  const [woMoving, setWoMoving] = useState(false);
  const currentProject = (projects || []).find(p => p.id === workPeriod.project_id || p.project_number === workPeriod.project_id);
  const woMatch = (projects || []).find(p => (p.project_number || '').toLowerCase() === woInput.trim().toLowerCase());

  // Check if work period has crew members for dispatch
  const crewMembers = workPeriod.resources?.filter(r => r.type === 'crew') || [];
  const hasCrewMembers = crewMembers.length > 0;
  
  // Get dispatch status for this work period
  const dispatchStatus = dispatchStatuses?.[workPeriod.id];

  // Crew assigned to the work period who have never been texted a dispatch
  const dispatchedIds = new Set((dispatchStatus?.crew_dispatches || []).map(c => c.crew_member_id));
  const notYetDispatched = dispatchStatus?.dispatched
    ? crewMembers.filter(c => !dispatchedIds.has(c.id))
    : [];
  const [pendingTextId, setPendingTextId] = useState(null);
  const [sendingTextId, setSendingTextId] = useState(null);
  const [showCrewMsg, setShowCrewMsg] = useState(false);
  const [crewMsg, setCrewMsg] = useState('');
  const [crewMsgSending, setCrewMsgSending] = useState(false);
  const [chatThreadId, setChatThreadId] = useState(undefined); // undefined = loading, null = none yet
  const { user: authUser } = useAuth();
  const [swapOut, setSwapOut] = useState(null);       // crew resource being swapped off
  const [swapCrewList, setSwapCrewList] = useState([]);
  const [swapInId, setSwapInId] = useState('');
  const [swapping, setSwapping] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // crew resource being taken off
  const [swapDate, setSwapDate] = useState('');
  const [addDate, setAddDate] = useState('');
  const [removeDate, setRemoveDate] = useState('');

  const wpStart = String(workPeriod.start_date || '').slice(0, 10);
  const wpEnd = String(workPeriod.end_date || '').slice(0, 10);
  const todayKey = localDateKey(new Date());
  const tomorrowKey = localDateKey(new Date(Date.now() + 86400000));
  const inBar = (d) => d >= wpStart && d <= wpEnd;
  const barStarted = wpStart <= todayKey;
  // Defaults: remove → tomorrow (they finish today); add → today; swap → tomorrow. Bar not started yet → whole bar.
  const defaultRemoveDate = barStarted && inBar(tomorrowKey) ? tomorrowKey : '';
  const defaultAddDate = barStarted && inBar(todayKey) ? todayKey : '';
  const prettyDay = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
  const dayBefore = (d) => localDateKey(new Date(new Date(`${d}T12:00:00`).getTime() - 86400000));

  const loadCrewList = async () => {
    if (swapCrewList.length) return;
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dispatch/resources`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setSwapCrewList(r.data.crew || []);
    } catch { toast.error('Could not load the crew list'); }
  };
  const closePanels = () => { setSwapOut(null); setAddMode(false); setRemoveTarget(null); setSwapInId(''); };
  const openSwap = async (resource) => { closePanels(); setSwapOut(resource); setSwapDate(defaultRemoveDate); await loadCrewList(); };
  const openAdd = async () => { closePanels(); setAddMode(true); setAddDate(defaultAddDate); await loadCrewList(); };
  const openRemove = (resource) => { closePanels(); setRemoveTarget(resource); setRemoveDate(defaultRemoveDate); };

  const postCrewChange = async (path, body) => {
    const r = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/dispatch/work-periods/${workPeriod.id}/${path}`, body,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    if (r.data?.sheet_wins && r.data?.sheet_note) toast.warning(r.data.sheet_note, { duration: 12000 });
    return r;
  };

  const doSwap = async () => {
    if (!swapOut || !swapInId || swapping) return;
    const inName = swapCrewList.find(c => c.id === swapInId)?.name || 'the new member';
    const when = swapDate ? ` starting ${prettyDay(swapDate)} (${swapOut.name} keeps the days before)` : '';
    if (!window.confirm(`Swap ${swapOut.name} → ${inName}${when}?\n\nBoth are notified in the job chat: ${swapOut.name} is told they're off, ${inName} gets the dispatch with ✓ / ✗ to confirm.`)) return;
    setSwapping(true);
    try {
      const r = await postCrewChange('swap-crew', { out_id: swapOut.id, in_id: swapInId, date: swapDate || undefined });
      toast.success(`Swapped ${r.data.out.name} → ${r.data.in.name}${r.data.in.from_date ? ` from ${prettyDay(r.data.in.from_date)}` : ''} — both notified in the job chat`);
      if (r.data.warning) toast.warning(r.data.warning, { duration: 9000 });
      closePanels();
      if (onCrewSwapped) onCrewSwapped(workPeriod.id, r.data.out, r.data.in, r.data.work_period);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Swap failed');
    } finally {
      setSwapping(false);
    }
  };
  const doAdd = async () => {
    if (!swapInId || swapping) return;
    const inName = swapCrewList.find(c => c.id === swapInId)?.name || 'the new member';
    const when = addDate ? ` starting ${prettyDay(addDate)}` : '';
    if (!window.confirm(`Add ${inName} to this job${when}?\n\nThey get the dispatch in the job chat with ✓ / ✗ to confirm; the PM and dispatch get an FYI.`)) return;
    setSwapping(true);
    try {
      const r = await postCrewChange('add-crew', { in_id: swapInId, from_date: addDate || undefined });
      toast.success(`${r.data.in.name} added${r.data.in.from_date ? ` from ${prettyDay(r.data.in.from_date)}` : ''} — dispatch sent in the job chat`);
      if (r.data.warning) toast.warning(r.data.warning, { duration: 9000 });
      closePanels();
      if (onCrewSwapped) onCrewSwapped(workPeriod.id, null, r.data.in, r.data.work_period);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add them');
    } finally {
      setSwapping(false);
    }
  };
  const doRemove = async () => {
    if (!removeTarget || swapping) return;
    setSwapping(true);
    try {
      const r = await postCrewChange('remove-crew', { out_id: removeTarget.id, off_date: removeDate || undefined });
      toast.success(r.data.out.off_date
        ? `${r.data.out.name} is off as of ${prettyDay(r.data.out.off_date)} (last day ${prettyDay(dayBefore(r.data.out.off_date))}) — notified in the job chat`
        : `${r.data.out.name} removed from this job — notified in the job chat`);
      closePanels();
      if (onCrewSwapped) onCrewSwapped(workPeriod.id, r.data.out, null, r.data.work_period);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove them');
    } finally {
      setSwapping(false);
    }
  };

  const loadCrewThread = async () => {
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/dispatch-chat/for-work-period/${workPeriod.id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setChatThreadId(r.data.thread_id || null);
    } catch { setChatThreadId(null); }
  };

  const toggleCrewMsg = () => {
    setShowCrewMsg(v => {
      if (!v) loadCrewThread();
      return !v;
    });
  };

  // Starts the job chat (no dispatch posted yet) with a first message to the whole crew
  const sendCrewMessage = async () => {
    if (!crewMsg.trim() || crewMsgSending) return;
    setCrewMsgSending(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/dispatch/work-periods/${workPeriod.id}/text-crew`,
        { message: crewMsg.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`Job chat started — ${r.data.sent} crew notified`);
      setCrewMsg('');
      if (r.data.thread_id) setChatThreadId(r.data.thread_id); else loadCrewThread();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to message the crew');
    } finally {
      setCrewMsgSending(false);
    }
  };

  const handleIndividualText = async (crew) => {
    if (pendingTextId !== crew.id) {
      setPendingTextId(crew.id);
      setTimeout(() => setPendingTextId(prev => (prev === crew.id ? null : prev)), 5000);
      return;
    }
    setPendingTextId(null);
    setSendingTextId(crew.id);
    try {
      await onDispatchOne(workPeriod, crew.id, crew.name);
    } finally {
      setSendingTextId(null);
    }
  };

  // Auto-save function
  const autoSave = (updates) => {
    setIsSaving(true);
    const normalizedFormData = {
      ...formData,
      ...updates,
      start_date: formatDate(updates.start_date || formData.start_date),
      end_date: formatDate(updates.end_date || formData.end_date),
      resources: workPeriod.resources || []
    };
    
    onUpdate(workPeriod.id, normalizedFormData);
    setTimeout(() => setIsSaving(false), 500);
  };

  // Move this job to a different work order (fixes a wrong WO number)
  const moveToWorkOrder = async () => {
    if (!woMatch) return;
    const fromSheet = workPeriod.source === 'manpower_sheet';
    const oldWo = workPeriod.sheet_wo || (workPeriod.sheet_group || '').split('|')[0] || currentProject?.project_number || '';
    if (!window.confirm(`Move this job to WO ${woMatch.project_number}${woMatch.client_name ? ` — ${woMatch.client_name}` : ''}?\n\nIt will show under that project on the schedule, and any linked dispatch request gets the corrected work order number.${fromSheet ? `\n\nThis bar comes from the manpower sheet: from ${new Date(formData.start_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} on, every sheet row for ${oldWo.toUpperCase()} will land on ${woMatch.project_number} automatically (rule shown on the Manpower Sheet page).` : ''}`)) return;
    setWoMoving(true);
    try {
      await onUpdate(workPeriod.id, {
        ...formData,
        start_date: formatDate(formData.start_date),
        end_date: formatDate(formData.end_date),
        resources: workPeriod.resources || [],
        project_id: woMatch.id,
      });
      if (fromSheet) toast.success(`Moved to ${woMatch.project_number}. Future sheet rows for ${oldWo.toUpperCase()} will follow it.`, { duration: 8000 });
      onClose();
    } finally {
      setWoMoving(false);
    }
  };

  // Handle field changes with immediate save (except notes)
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Debounce free-text fields so we don't fire a PUT per keystroke
    if (['notes', 'site_contact', 'site_address', 'tool_list'].includes(field)) {
      pendingSaveRef.current = { ...pendingSaveRef.current, [field]: value };
      if (notesTimeoutRef.current) {
        clearTimeout(notesTimeoutRef.current);
      }
      notesTimeoutRef.current = setTimeout(() => {
        autoSave(pendingSaveRef.current);
        pendingSaveRef.current = {};
      }, 1000); // 1 second debounce for notes
    } else {
      // Immediate save for dates, times, foreman
      autoSave({ [field]: value });
    }
  };

  // Rental equipment management
  const addRentalEquipment = () => {
    if (newRental.equipment_name && newRental.vendor) {
      const rental = {
        id: Date.now().toString(),
        equipment_name: newRental.equipment_name,
        vendor: newRental.vendor,
        arrival_time: newRental.arrival_time || null,
        needs_offrent_reminder: newRental.needs_offrent_reminder
      };
      const updatedRentals = [...formData.rental_equipment, rental];
      handleFieldChange('rental_equipment', updatedRentals);
      setNewRental({ equipment_name: '', vendor: '', arrival_time: '', needs_offrent_reminder: false });
    }
  };

  const removeRentalEquipment = (rentalId) => {
    const updatedRentals = formData.rental_equipment.filter(r => r.id !== rentalId);
    handleFieldChange('rental_equipment', updatedRentals);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form submission is now handled by auto-save
  };

  const handleDelete = () => {





    
    // Show custom confirmation dialog instead of window.confirm
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {

    setShowDeleteConfirm(false);
    onDelete(workPeriod.id);
  };

  const cancelDelete = () => {

    setShowDeleteConfirm(false);
  };

  const calculateHours = () => {
    const start = new Date(`2000-01-01T${formData.start_time}`);
    const end = new Date(`2000-01-01T${formData.end_time}`);
    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours > 0 ? diffHours.toFixed(1) : 0;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="bg-white border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-gray-900 flex items-center gap-2">
              Work Period Details
              {workPeriod.source === 'training' && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-purple-50 text-purple-900 border border-purple-200 rounded px-1.5 py-0.5" title="Booked from Safety & Training → Mass Training. Reschedule or cancel it there so the attendees are told." data-testid="wp-training-badge">
                  🎓 Training session
                </span>
              )}
              {workPeriod.source === 'manpower_sheet' && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5" title="Created from the dispatch sheet — the sheet wins for now: crew and dates follow it (re-read every 5 min). A WO move or job details you change here are pinned and kept" data-testid="wp-sheet-badge">
                  📋 From dispatch sheet · sheet wins
                </span>
              )}
              {workPeriod.source !== 'manpower_sheet' && workPeriod.sheet_fill_sig && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5" title="This Ops bar sits on days the dispatch sheet names a crew — the sheet's names are copied in and win for now; change the crew on the sheet" data-testid="wp-sheet-filled-badge">
                  📋 Crew from dispatch sheet
                </span>
              )}
              {workPeriod.source === 'manpower_sheet' && workPeriod.pm_overrides && Object.keys(workPeriod.pm_overrides).length > 0 && (
                <span className="text-[10px] font-bold bg-blue-50 text-blue-900 border border-blue-200 rounded px-1.5 py-0.5" data-testid="wp-pinned-badge"
                  title={`Pinned in 365Ops (the sheet sync won't overwrite): ${Object.entries(workPeriod.pm_overrides).map(([k, v]) => `${k === 'project_id' ? 'WO → ' + v : k.replace('_', ' ')}`).join(', ')}${workPeriod.pm_override_meta ? ` — by ${workPeriod.pm_override_meta.by}` : ''}`}>
                  📌 {Object.keys(workPeriod.pm_overrides).includes('project_id') ? `WO moved → ${workPeriod.pm_overrides.project_id}` : `${Object.keys(workPeriod.pm_overrides).length} edit${Object.keys(workPeriod.pm_overrides).length !== 1 ? 's' : ''} pinned`}
                </span>
              )}
              {workPeriod.source === 'projection' && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5" title="Headcount only — nobody is named yet" data-testid="wp-projection-badge">
                  {workPeriod.planned_crew}? Projection
                </span>
              )}
              {workPeriod.sheet_plan && workPeriod.planned_crew > 0 && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5" title="Number typed on the manpower sheet's Monthly View — no names on the day tab yet. Change it on the sheet." data-testid="wp-sheet-plan-badge">
                  {workPeriod.planned_crew}? Sheet plan
                </span>
              )}
              {workPeriod.crew_tbd && workPeriod.planned_crew > 0 && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5" title="Approved with the crew to be determined — pick the names with Modify Crew on the dispatch request" data-testid="wp-tbd-badge">
                  {workPeriod.planned_crew}? Crew TBD
                </span>
              )}
              {workPeriod.source === 'timesheet' && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5" title={workPeriod.as_worked ? `Rebuilt from the field timesheets${workPeriod.consolidated_from?.length ? ` — merged ${workPeriod.consolidated_from.length + 1} bars into one` : ''}` : 'Nobody was scheduled here but hours were logged — built from the timesheets'} data-testid="wp-timesheet-badge">
                  ⏱ As worked{workPeriod.consolidated_from?.length ? ` · merged ${workPeriod.consolidated_from.length + 1} bars` : ''}
                </span>
              )}
              {workPeriod.timesheet_adjustments?.length > 0 && workPeriod.source !== 'timesheet' && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5" title="Some days were changed to match the field timesheets — see the list below" data-testid="wp-ts-adjusted-badge">
                  ⏱ Adjusted from timesheets
                </span>
              )}
              {isSaving && <span className="text-xs text-green-700">● Saving...</span>}
            </CardTitle>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-900 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {workPeriod.source === 'projection' && (
            <ProjectionEditor workPeriod={workPeriod} onSaved={(n) => { onCrewSwapped?.(workPeriod.id, null, null, { planned_crew: n }); onClose(); }} />
          )}
          {workPeriod.status === 'cancelled' && (
            <div className="mb-4 bg-red-100 border border-red-600 rounded-lg px-4 py-3 text-sm text-red-800 flex items-center justify-between gap-3" data-testid="wp-cancelled-banner">
              <span>
                <span className="font-bold">CANCELLED</span>
                {workPeriod.cancelled_by_name ? ` by ${workPeriod.cancelled_by_name}` : ''}
                {workPeriod.cancel_note ? ` — ${workPeriod.cancel_note}` : ''}
              </span>
              {onRebookJob && (
                <button
                  type="button"
                  data-testid="wp-rebook-btn"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRebookJob(workPeriod); }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
                  title="Copy this job to a new date with the same crew & equipment"
                >
                  📅 Re-book
                </button>
              )}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Work Order (fix a wrong WO number) */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Work Order #
                <span className="text-xs text-gray-600 ml-2">
                  Current: <span className="font-mono text-blue-700" data-testid="wp-wo-current">{currentProject?.project_number || workPeriod.project_id || 'TBD'}</span>
                  {currentProject?.client_name ? ` — ${currentProject.client_name}` : ''}
                </span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  list="wp-wo-options"
                  value={woInput}
                  onChange={(e) => setWoInput(e.target.value)}
                  placeholder="Wrong WO? Type the correct one (e.g. 26-3069)"
                  className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="wp-wo-input"
                />
                <datalist id="wp-wo-options">
                  {(projects || []).map(p => (
                    <option key={p.id} value={p.project_number}>{p.client_name || p.title || ''}</option>
                  ))}
                </datalist>
                <button
                  type="button"
                  onClick={moveToWorkOrder}
                  disabled={woMoving || !woMatch || woMatch.id === workPeriod.project_id || woMatch.project_number === workPeriod.project_id}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold whitespace-nowrap"
                  data-testid="wp-wo-move-btn"
                >
                  {woMoving ? 'Moving…' : 'Move job'}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/tool-crib/kits`, { work_order_number: workPeriod.project_id, work_period_id: workPeriod.id }, { headers: { Authorization: `Bearer ${token}` } });
                      window.location.assign(`/tool-crib?kit=${r.data.id}`);
                    } catch (e) { toast.error(e.response?.data?.detail || 'Could not open the job kit'); }
                  }}
                  disabled={!(projects || []).some(p => p.project_number === workPeriod.project_id || p.id === workPeriod.project_id)}
                  className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold whitespace-nowrap"
                  title={(projects || []).some(p => p.project_number === workPeriod.project_id || p.id === workPeriod.project_id) ? 'Tool list for this job — build it here, the tool crib packs & checks it back in' : 'Move this bar onto a real work order first'}
                  data-testid="wp-job-kit-btn"
                >
                  🧰 Job kit
                </button>
              </div>
              {woInput.trim() !== '' && !woMatch && (
                <p className="text-xs text-amber-700 mt-1" data-testid="wp-wo-nomatch">No project found with that work order number</p>
              )}
            </div>

            {/* Date Range — a sheet-mirrored bar's days come from the sheet (the sheet wins for now) */}
            {(() => { const sheetDays = workPeriod.source === 'manpower_sheet'; const dateTitle = sheetDays ? 'Days come from the manpower sheet — change the day tab / Monthly View there and 365Ops follows within 5 minutes' : undefined; return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" title={dateTitle}>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Start Date {sheetDays && <span className="ml-1 text-[10px] font-semibold text-emerald-800" data-testid="wp-dates-sheet-note">· set on the sheet</span>}
                </label>
                <input
                  type="date"
                  value={formatDate(formData.start_date)}
                  onChange={(e) => handleFieldChange('start_date', e.target.value)}
                  disabled={sheetDays}
                  data-testid="wp-start-date"
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 disabled:text-slate-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={formatDate(formData.end_date)}
                  onChange={(e) => handleFieldChange('end_date', e.target.value)}
                  disabled={sheetDays}
                  data-testid="wp-end-date"
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 disabled:text-slate-500"
                />
              </div>
            </div>
            ); })()}

            {/* Time Range */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => handleFieldChange('start_time', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  End Time
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => handleFieldChange('end_time', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Total Hours
                </label>
                <div className="px-3 py-2 bg-slate-200 border border-slate-300 rounded-lg text-slate-900">
                  {calculateHours()} hours
                </div>
              </div>
            </div>

            {/* Assigned Resources */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-900">
                  Assigned Resources
                  {workPeriod.resources.filter(r => r.type === 'crew').length > 1 && (
                    <span className="text-xs text-gray-600 ml-2">(Right-click crew to set as foreman)</span>
                  )}
                </label>
                {workPeriod.request_id && onModifyCrew && (
                  <button
                    onClick={() => onModifyCrew(workPeriod)}
                    data-testid="wp-modify-crew-btn"
                    className="text-xs px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
                    title="Add or remove crew on this dispatch — offers to text new members"
                  >
                    Modify Crew
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {workPeriod.resources.map((resource, idx) => {
                  const getResourceColor = (type) => {
                    const resourceType = type?.toLowerCase() || '';
                    if (resourceType.includes('truck')) return 'bg-green-600 text-white';
                    if (resourceType.includes('trailer')) return 'bg-orange-600 text-white';  
                    if (resourceType.includes('welder')) return 'bg-purple-600 text-white';
                    if (resourceType.includes('crew') || resourceType.includes('employee')) return 'bg-blue-600 text-white';
                    return 'bg-slate-200 text-slate-900';
                  };
                  
                  const isCrew = resource.type === 'crew';
                  const isForeman = isCrew && formData.foreman_id === resource.id;
                  
                  return (
                    <div 
                      key={idx} 
                      className={`px-3 py-1 rounded-full text-sm font-medium ${getResourceColor(resource.type)} ${isCrew ? 'cursor-pointer hover:ring-2 hover:ring-yellow-400' : ''}`}
                      onContextMenu={(e) => {
                        if (isCrew) {
                          e.preventDefault();
                          const newForemanId = formData.foreman_id === resource.id ? null : resource.id;
                          handleFieldChange('foreman_id', newForemanId);
                        }
                      }}
                      onClick={(e) => {
                        if (isCrew && e.shiftKey) {
                          e.preventDefault();
                          const newForemanId = formData.foreman_id === resource.id ? null : resource.id;
                          handleFieldChange('foreman_id', newForemanId);
                        }
                      }}
                      title={isCrew ? 'Right-click or Shift+Click to set as foreman' : ''}
                    >
                      {isForeman && '👑 '}
                      {resource.name} ({resource.type})
                      {isCrew && (resource.start_date > wpStart || (resource.end_date && resource.end_date < wpEnd)) && (
                        <span className="ml-1 text-[11px] opacity-80" data-testid={`crew-dates-${resource.id}`}>
                          · {resource.start_date > wpStart ? `from ${prettyDay(resource.start_date)}` : ''}{resource.start_date > wpStart && resource.end_date && resource.end_date < wpEnd ? ', ' : ''}{resource.end_date && resource.end_date < wpEnd ? `to ${prettyDay(resource.end_date)}` : ''}
                        </span>
                      )}
                      {isCrew && (
                        <button
                          type="button"
                          data-testid={`swap-crew-btn-${resource.id}`}
                          title={`Swap ${resource.name} for someone else (texts both)`}
                          onClick={(e) => { e.stopPropagation(); openSwap(resource); }}
                          className="ml-1.5 text-slate-600 hover:text-slate-900 font-bold"
                        >⇄</button>
                      )}
                      {isCrew && (
                        <button
                          type="button"
                          data-testid={`remove-crew-btn-${resource.id}`}
                          title={`Take ${resource.name} off this job (no replacement)`}
                          onClick={(e) => { e.stopPropagation(); openRemove(resource); }}
                          disabled={swapping}
                          className="ml-1 text-slate-600 hover:text-red-800 font-bold disabled:opacity-40"
                        >✕</button>
                      )}
                    </div>
                  );
                })}
                <button
                  type="button"
                  data-testid="add-crew-btn"
                  onClick={openAdd}
                  className="px-3 py-1 rounded-full text-sm font-medium border border-dashed border-blue-400 text-blue-700 hover:bg-blue-50"
                  title="Add someone to this job — they get the dispatch with ✓ / ✗ to confirm"
                >＋ Add crew</button>
              </div>

              {(workPeriod.crew_overrides || []).some(o => o.sheet_behind) && (
                <p className="mt-2 text-xs text-amber-700" data-testid="crew-overrides-note">
                  Crew adjusted in Ops — the manpower sheet still
                  {(() => {
                    const rem = workPeriod.crew_overrides.filter(o => o.sheet_behind && o.action === 'removed').map(o => o.name);
                    const add = workPeriod.crew_overrides.filter(o => o.sheet_behind && o.action === 'added').map(o => o.name);
                    return ` ${[rem.length ? `lists ${rem.join(', ')}` : '', add.length ? `doesn't list ${add.join(', ')}` : ''].filter(Boolean).join(' and ')}.`;
                  })()}
                </p>
              )}

              {/* Add picker */}
              {addMode && (
                <div className="mt-3 bg-slate-100 border border-slate-300 rounded-lg p-3 space-y-2" data-testid="add-crew-panel">
                  <p className="text-sm text-slate-800">Add someone to this job — who?</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={swapInId}
                      onChange={(e) => setSwapInId(e.target.value)}
                      data-testid="add-crew-select"
                      className="flex-1 min-w-[200px] bg-white border border-slate-300 rounded-lg p-2 text-sm text-slate-900"
                    >
                      <option value="">— Pick who's going on —</option>
                      {swapCrewList
                        .filter(c => !workPeriod.resources.some(r => r.type === 'crew' && r.id === c.id))
                        .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button
                      type="button"
                      data-testid="add-crew-confirm"
                      onClick={doAdd}
                      disabled={!swapInId || swapping}
                      className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium py-2 px-4 rounded-lg"
                    >
                      {swapping ? 'Adding…' : 'Add & Dispatch'}
                    </button>
                    <button type="button" onClick={closePanels} className="text-slate-500 hover:text-slate-900 px-2">✕</button>
                  </div>
                  <WhenChips label="Starting:" value={addDate} onChange={setAddDate} wpStart={wpStart} wpEnd={wpEnd} wholeLabel="From the start" testPrefix="add-crew" />
                  <p className="text-xs text-slate-700">They get the full dispatch text with ✓ / ✗ to confirm · the PM and dispatch get an FYI.</p>
                </div>
              )}

              {/* Remove picker */}
              {removeTarget && (
                <div className="mt-3 bg-slate-100 border border-red-300 rounded-lg p-3 space-y-2" data-testid="remove-crew-panel">
                  <p className="text-sm text-slate-800">
                    Take <b className="text-slate-900">{removeTarget.name}</b> off this job — no replacement.
                  </p>
                  <WhenChips label="Off starting:" value={removeDate} onChange={setRemoveDate} wpStart={wpStart} wpEnd={wpEnd} wholeLabel="Entire period" testPrefix="remove-crew" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      data-testid="remove-crew-confirm"
                      onClick={doRemove}
                      disabled={swapping}
                      className="bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-medium py-2 px-4 rounded-lg"
                    >
                      {swapping ? 'Removing…' : (removeDate && removeDate > wpStart ? `Remove as of ${prettyDay(removeDate)}` : 'Remove from entire period')}
                    </button>
                    <button type="button" onClick={closePanels} className="text-slate-500 hover:text-slate-900 px-2">✕</button>
                  </div>
                  <p className="text-xs text-slate-700">
                    {removeDate && removeDate > wpStart
                      ? `${removeTarget.name} keeps the days up to ${prettyDay(dayBefore(removeDate))} (timesheets/headcount stay right) and is told they're off as of ${prettyDay(removeDate)}.`
                      : `${removeTarget.name} is taken off every day of this bar and told they're off.`} The PM and dispatch get an FYI.
                  </p>
                </div>
              )}

              {/* Swap picker */}
              {swapOut && (
                <div className="mt-3 bg-slate-100 border border-slate-300 rounded-lg p-3 space-y-2" data-testid="swap-crew-panel">
                  <p className="text-sm text-slate-800">
                    Swap <b className="text-slate-900">{swapOut.name}</b> out — who's covering?
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={swapInId}
                      onChange={(e) => setSwapInId(e.target.value)}
                      data-testid="swap-crew-select"
                      className="flex-1 min-w-[200px] bg-white border border-slate-300 rounded-lg p-2 text-sm text-slate-900"
                    >
                      <option value="">— Pick the replacement —</option>
                      {swapCrewList
                        .filter(c => c.id !== swapOut.id && !workPeriod.resources.some(r => r.type === 'crew' && r.id === c.id))
                        .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button
                      type="button"
                      data-testid="swap-crew-confirm"
                      onClick={doSwap}
                      disabled={!swapInId || swapping}
                      className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium py-2 px-4 rounded-lg"
                    >
                      {swapping ? 'Swapping…' : 'Swap & Text Both'}
                    </button>
                    <button type="button" onClick={closePanels} className="text-slate-500 hover:text-slate-900 px-2">✕</button>
                  </div>
                  <WhenChips label="Starting:" value={swapDate} onChange={setSwapDate} wpStart={wpStart} wpEnd={wpEnd} wholeLabel="Whole period" testPrefix="swap-crew" />
                  <p className="text-xs text-slate-700">
                    {swapOut.name} gets a "you're off this job" text{swapDate && swapDate > wpStart ? ` (keeps the days up to ${prettyDay(dayBefore(swapDate))})` : ''} · the replacement gets the full dispatch text with ✓ / ✗ to confirm.
                  </p>
                </div>
              )}
            </div>

            {/* Rental Equipment */}
            <div className="border-t border-gray-400/60 pt-4">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                🚚 Rental Equipment
              </label>
              
              {/* Existing Rental Equipment */}
              {formData.rental_equipment.length > 0 && (
                <div className="mb-3 space-y-2">
                  {formData.rental_equipment.map((rental) => (
                    <div key={rental.id} className="bg-slate-100 p-3 rounded-lg flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-slate-900 font-medium">{rental.equipment_name}</span>
                          {rental.needs_offrent_reminder && (
                            <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded" title="Off-rent reminder enabled">
                              📋 Reminder
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-700">
                          <span className="font-medium">Vendor:</span> {rental.vendor}
                        </div>
                        {rental.arrival_time && (
                          <div className="text-sm text-slate-700">
                            <span className="font-medium">Arrival:</span> {rental.arrival_time}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeRentalEquipment(rental.id)}
                        className="text-red-600 hover:text-red-700 ml-2"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Rental Equipment */}
              <div className="bg-slate-100 p-3 rounded-lg space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <input
                      type="text"
                      placeholder="Equipment Name"
                      value={newRental.equipment_name}
                      onChange={(e) => setNewRental(prev => ({ ...prev, equipment_name: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Vendor Name"
                      value={newRental.vendor}
                      onChange={(e) => setNewRental(prev => ({ ...prev, vendor: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <input
                      type="time"
                      placeholder="Arrival Time (optional)"
                      value={newRental.arrival_time}
                      onChange={(e) => setNewRental(prev => ({ ...prev, arrival_time: e.target.value }))}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newRental.needs_offrent_reminder}
                        onChange={(e) => setNewRental(prev => ({ ...prev, needs_offrent_reminder: e.target.checked }))}
                        className="w-4 h-4 rounded"
                      />
                      <span>Off-rent reminder</span>
                    </label>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addRentalEquipment}
                  disabled={!newRental.equipment_name || !newRental.vendor}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  + Add Rental Equipment
                </button>
              </div>
            </div>

            {/* Dispatch Card Fields */}
            <div className="border-t border-gray-400/60 pt-4">
              <label className="block text-sm font-medium text-gray-900 mb-2">Dispatch Details</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700">Status</label>
                  <select value={formData.dispatch_status} onChange={(e) => handleFieldChange('dispatch_status', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm">
                    <option value="confirmed">Confirmed (Have PO)</option>
                    <option value="tentative">Tentative (No PO)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Site Contact</label>
                  <input value={formData.site_contact} onChange={(e) => handleFieldChange('site_contact', e.target.value)}
                    data-testid="wp-site-contact-input"
                    placeholder="Name + phone" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Site Address</label>
                  <input value={formData.site_address} onChange={(e) => handleFieldChange('site_address', e.target.value)}
                    data-testid="wp-site-address-input"
                    placeholder="Override project address" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Tool List</label>
                  <input value={formData.tool_list} onChange={(e) => handleFieldChange('tool_list', e.target.value)}
                    placeholder="e.g. shop tools, hand tools, complete" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm" />
                </div>
              </div>
            </div>

            {/* Changes made from timesheets */}
            {workPeriod.timesheet_adjustments?.length > 0 && (
              <div className="border-t border-gray-400/60 pt-4" data-testid="wp-ts-adjustments">
                <label className="block text-sm font-medium text-gray-900 mb-2">⏱ Changed to match timesheets</label>
                <ul className="space-y-1.5">
                  {[...workPeriod.timesheet_adjustments].sort((a, b) => a.date.localeCompare(b.date)).map((a) => (
                    <li key={a.id} className="flex items-start justify-between gap-3 text-sm bg-white/70 border border-gray-300 rounded-lg px-3 py-2" data-testid={`wp-ts-adj-${a.id}`}>
                      <div className="min-w-0">
                        <span className="font-semibold text-gray-900">{a.employee_name}</span>
                        <span className={`ml-2 text-[11px] font-bold px-1.5 py-0.5 rounded ${a.action === 'added' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{a.action === 'added' ? 'added' : 'removed'}</span>
                        <span className="ml-2 text-gray-800">{new Date(`${a.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                        <p className="text-xs text-gray-700 mt-0.5">
                          {a.action === 'added'
                            ? `${a.hours}h on the timesheet${a.moved_from?.length ? ` — was scheduled on ${a.moved_from.join(', ')}` : ''}${a.created_period ? ' — bar created from timesheets' : ''}`
                            : a.kind === 'moved' ? `Timesheet shows ${a.hours_elsewhere}h on ${(a.worked_wos || []).join(', ')} instead` : 'No hours on any timesheet that day'}
                          {' · '}by {a.applied_by} on {new Date(a.applied_at).toLocaleDateString()}
                        </p>
                      </div>
                      <a href={a.timesheet_link} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline whitespace-nowrap" data-testid={`wp-ts-adj-link-${a.id}`}>Open timesheet ↗</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add notes about this work period..."
              />
            </div>

            {/* Dispatch Status - Show crew responses */}
            {dispatchStatus?.dispatched && dispatchStatus?.crew_dispatches && (
              <div className="border-t border-gray-400/60 pt-4">
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  📣 Dispatch Status
                </label>
                <div className="space-y-2">
                  {dispatchStatus.crew_dispatches.map((crew, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-100 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-900 font-medium">{crew.crew_member_name}</span>
                        {crew.crew_member_id === formData.foreman_id && (
                          <span className="text-xs">👑</span>
                        )}
                        {(crew.role === 'pm' || crew.role === 'dispatch') && (
                          <span className="text-[10px] uppercase tracking-wide text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">
                            {crew.role === 'pm' ? 'PM' : 'Dispatch'}
                          </span>
                        )}
                      </div>
                      <div>
                        {crew.status === 'notified' && (
                          <span className="flex items-center gap-1 text-blue-700 text-sm font-medium">
                            <span>📨</span> Notified
                          </span>
                        )}
                        {crew.status === 'confirmed' && (
                          <span className="flex items-center gap-1 text-green-700 text-sm font-medium">
                            <span>✓</span> Confirmed
                          </span>
                        )}
                        {crew.status === 'declined' && (
                          <span className="flex items-center gap-1 text-red-600 text-sm font-medium">
                            <span>✗</span> Declined
                          </span>
                        )}
                        {(crew.status === 'pending' || crew.status === 'sent') && (
                          <span className="flex items-center gap-1 text-amber-700 text-sm font-medium">
                            <span>⏳</span> Pending
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {notYetDispatched.map((crew) => (
                    <div key={crew.id} data-testid={`wp-not-dispatched-${crew.id}`} className="flex items-center justify-between bg-slate-100 border border-amber-200 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-900 font-medium">{crew.name}</span>
                        <span className="flex items-center gap-1 text-amber-700 text-xs font-medium">
                          ⚠ Not yet dispatched
                        </span>
                      </div>
                      <button
                        type="button"
                        data-testid={`wp-send-text-${crew.id}`}
                        disabled={sendingTextId === crew.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleIndividualText(crew);
                        }}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                          pendingTextId === crew.id
                            ? 'bg-orange-500 hover:bg-orange-400 text-white'
                            : 'bg-yellow-600 hover:bg-yellow-500 text-white'
                        } ${sendingTextId === crew.id ? 'opacity-60 cursor-wait' : ''}`}
                      >
                        {sendingTextId === crew.id ? 'Posting…' : pendingTextId === crew.id ? 'Tap again to dispatch' : '📣 Dispatch'}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-xs text-gray-700">
                  Crew tap ✓ / ✗ in the job chat · PM &amp; dispatch see everything (no reply needed)
                  {notYetDispatched.length > 0 && (
                    <span className="block text-amber-700 mt-1">
                      "Dispatch" pings ONLY that person — the Dispatch Crew button below re-posts to the whole crew and resets their replies.
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Job group chat — crew, PM & dispatch all see the same thread */}
            {hasCrewMembers && (
              <div className="bg-slate-100 border border-slate-300 rounded-lg p-4" data-testid="wp-job-chat-section">
                <button
                  type="button"
                  data-testid="wp-text-crew-toggle"
                  onClick={toggleCrewMsg}
                  className="w-full flex items-center justify-between text-sm font-medium text-slate-800 hover:text-slate-900"
                >
                  <span>💬 Job Chat with Crew ({crewMembers.length})</span>
                  <span className="text-slate-500">{showCrewMsg ? '▴' : '▾'}</span>
                </button>
                {showCrewMsg && (
                  <div className="mt-3 space-y-2">
                    {chatThreadId === undefined ? (
                      <p className="text-xs text-slate-500">Loading chat…</p>
                    ) : chatThreadId ? (
                      <>
                        <DispatchThreadPanel threadId={chatThreadId} currentUser={authUser} embedded onStatusChange={() => onDispatchStatusRefresh?.(workPeriod.id)} />
                        <a href={`/dispatch-chat?thread=${chatThreadId}`} className="block text-right text-[11px] text-blue-700 hover:text-blue-800" data-testid="wp-open-full-chat">Open full-screen chat →</a>
                      </>
                    ) : (
                      <div className="space-y-2" data-testid="wp-start-chat">
                        <p className="text-xs text-slate-500">No job chat yet. Post the dispatch (button below) or start the chat with a message — every crew member gets a push notification.</p>
                        <textarea
                          value={crewMsg}
                          onChange={(e) => setCrewMsg(e.target.value)}
                          rows={2}
                          maxLength={600}
                          placeholder="Gate code, start time change, bring fall-arrest, etc."
                          data-testid="wp-text-crew-input"
                          className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500"
                        />
                        <div className="flex justify-end">
                          <button
                            type="button"
                            data-testid="wp-text-crew-send"
                            onClick={sendCrewMessage}
                            disabled={!crewMsg.trim() || crewMsgSending}
                            className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-4 rounded-lg transition-colors"
                          >
                            {crewMsgSending ? 'Sending…' : `Start chat & message ${crewMembers.length} crew`}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              {/* DISPATCH CREW BUTTON (posts to job chat) - Only show if work period has crew members */}
              {hasCrewMembers && (
                <button
                  type="button"
                  data-testid="wp-dispatch-sms-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    onDispatch({ ...workPeriod, site_address: formData.site_address, site_contact: formData.site_contact });
                  }}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-500 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                  title="Post the dispatch to the job chat — crew get a push notification and confirm with ✓ / ✗"
                >
                  📣 Dispatch Crew
                  {dispatchStatus?.dispatched && (
                    <span className="text-xs bg-amber-100 text-amber-900 px-2 py-1 rounded">
                      Posted
                    </span>
                  )}
                </button>
              )}
              
              {onCancelJob && workPeriod.status !== 'cancelled' && (
                <button
                  type="button"
                  data-testid="wp-cancel-job-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCancelJob(workPeriod);
                  }}
                  className="bg-red-700 hover:bg-red-800 text-white py-2 px-4 rounded-lg transition-colors"
                  title="Cancel this job and text the assigned crew"
                >
                  ❌ Job Cancelled
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {


                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete();
                }}
                className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors"
                data-testid="wp-detail-delete"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </CardContent>
        
        {/* Custom Delete Confirmation Dialog — fixed to the viewport so it's visible no matter how far the modal is scrolled */}
        {showDeleteConfirm && ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4" data-testid="wp-delete-confirm">
            <div className="bg-white border border-slate-200 shadow-2xl rounded-lg p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete this work period?</h3>
              <p className="text-slate-700 mb-6 text-sm">
                {workPeriod.project_id ? <span className="font-mono text-blue-700">{workPeriod.project_id}</span> : 'This work period'} · {formatDate ? `${formatDate(workPeriod.start_date)} → ${formatDate(workPeriod.end_date)}` : `${workPeriod.start_date} → ${workPeriod.end_date}`}
                <br />The crew and equipment come off the schedule. This cannot be undone.
              </p>
              {(workPeriod.source === 'manpower_sheet' || workPeriod.sheet_wo) && (
                <div className="mb-5 -mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="wp-delete-sheet-note">
                  <p className="font-semibold text-amber-800 mb-1">This bar mirrors the manpower sheet (WO {(workPeriod.sheet_wo || (workPeriod.sheet_group || '').split('|')[0] || workPeriod.project_id || '').toString().toUpperCase()}).</p>
                  <p>Deleting hides <em>these days only</em> — the sheet keeps sending this WO. If the job now belongs to a different work order (e.g. this month's shift-coverage WO), <b>use "Move to WO" instead</b>: it moves this bar <u>and</u> teaches the read to put every future row on the right WO.</p>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={confirmDelete}
                  data-testid="wp-delete-confirm-yes"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors font-semibold"
                >
                  Yes, Delete
                </button>
                <button
                  onClick={cancelDelete}
                  data-testid="wp-delete-confirm-cancel"
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-800 py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </Card>
    </div>
  );
};

// SMS Dispatch Status Indicator Component
// Compact single-chip dispatch reply summary — always fits inside the bubble.
// Green = all confirmed, red = any declined, amber = replies pending. Details on hover.
const TB_BTN = 'h-8 inline-flex items-center gap-1.5 px-3 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap';
const TB_ON = 'bg-slate-900 border-slate-900 text-white hover:bg-sky-800 hover:border-sky-800 shadow-sm';
const TB_OFF = 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 shadow-sm';

// Light-theme bar tones: pale tint + strong coloured left edge + dark text
const WP_TONES = {
  cancelled: { bg: 'repeating-linear-gradient(135deg, #fee2e2 0, #fee2e2 8px, #fecaca 8px, #fecaca 16px)', edge: '#dc2626', text: '#7f1d1d', border: '1px solid rgba(220,38,38,0.45)' },
  tentative: { bg: '#fef3c7', edge: '#d97706', text: '#78350f', border: '1px dashed rgba(217,119,6,0.8)' },
  training: { bg: '#ede9fe', edge: '#7c3aed', text: '#4c1d95', border: '1px solid rgba(124,58,237,0.35)' },
  manpower_sheet: { bg: '#ccfbf1', edge: '#0f766e', text: '#134e4a', border: '1px solid rgba(15,118,110,0.35)' },
  timesheet: { bg: '#d1fae5', edge: '#047857', text: '#064e3b', border: '1px solid rgba(4,120,87,0.35)' },
  dispatched: { bg: '#dbeafe', edge: '#1d4ed8', text: '#1e3a8a', border: '1px solid rgba(29,78,216,0.35)' },
};
const wpTone = (wp) => wp.status === 'cancelled' ? WP_TONES.cancelled
  : wp.dispatch_status === 'tentative' ? WP_TONES.tentative
  : wp.crew_tbd && wp.planned_crew > 0 && !(wp.resources || []).some(r => r.type === 'crew') ? WP_TONES.tentative
  : wp.source === 'training' ? WP_TONES.training
  : wp.source === 'manpower_sheet' ? WP_TONES.manpower_sheet
  : wp.source === 'timesheet' ? WP_TONES.timesheet
  : WP_TONES.dispatched;
const WpCount = ({ tone, children, testId }) => (
  <span className="inline-flex items-center justify-center rounded-full font-bold tabular-nums shadow-sm" data-testid={testId}
    style={{ minWidth: '18px', height: '16px', padding: '0 5px', fontSize: '11px', lineHeight: 1, background: tone.edge, color: '#fff' }}>
    {children}
  </span>
);

const WpStatusChip = ({ workPeriod, dispatchStatuses, duration }) => {
  const dispatch = dispatchStatuses?.[workPeriod.id];
  if (!dispatch?.dispatched) return null;
  const crewOnly = (dispatch.crew_dispatches || []).filter(cd => cd.role !== 'pm' && cd.role !== 'dispatch' && cd.status !== 'notified');
  const total = crewOnly.length;
  if (!total) return null;
  const confirmed = crewOnly.filter(cd => cd.status === 'confirmed').length;
  const declined = crewOnly.filter(cd => cd.status === 'declined').length;
  const color = declined > 0 ? '#b91c1c' : confirmed === total ? '#15803d' : '#b45309';
  const title = crewOnly.map(cd => `${cd.status === 'confirmed' ? '✓' : cd.status === 'declined' ? '✗' : '⏳'} ${cd.crew_member_name || 'Unknown'}`).join('\n');
  if (duration < 2) {
    return <span className="rounded-full flex-shrink-0 cursor-help" title={title} style={{ width: '6px', height: '6px', backgroundColor: color, display: 'inline-block' }} />;
  }
  return (
    <span className="font-bold flex-shrink-0 cursor-help" title={title} style={{ fontSize: '10px', color, lineHeight: 1 }}>
      {declined > 0 ? `✗${declined}` : `✓${confirmed}/${total}`}
    </span>
  );
};

const DispatchStatusIndicator = ({ workPeriod, dispatchStatuses }) => {
  const dispatch = dispatchStatuses?.[workPeriod.id];
  
  if (!dispatch?.dispatched) {
    return null; // No dispatch sent yet
  }
  
  const crewDispatches = dispatch.crew_dispatches || [];
  const notifiedCrew = crewDispatches.filter(cd => cd.status === 'notified');
  const confirmedCrew = crewDispatches.filter(cd => cd.status === 'confirmed');
  const declinedCrew = crewDispatches.filter(cd => cd.status === 'declined');
  const pendingCrew = crewDispatches.filter(cd => cd.status === 'pending' || cd.status === 'sent');
  
  // Create hover tooltip content
  const getTooltipContent = () => {
    const lines = [];
    
    if (notifiedCrew.length > 0) {
      lines.push('📨 NOTIFIED (FYI):');
      notifiedCrew.forEach(crew => {
        lines.push(`  • ${crew.crew_member_name || 'Unknown'}${crew.role === 'pm' ? ' (PM)' : crew.role === 'dispatch' ? ' (Dispatch)' : ''}`);
      });
    }
    
    if (confirmedCrew.length > 0) {
      lines.push('✅ CONFIRMED:');
      confirmedCrew.forEach(crew => {
        lines.push(`  • ${crew.crew_member_name || 'Unknown'}`);
      });
    }
    
    if (declinedCrew.length > 0) {
      lines.push('❌ DECLINED:');
      declinedCrew.forEach(crew => {
        lines.push(`  • ${crew.crew_member_name || 'Unknown'}`);
      });
    }
    
    if (pendingCrew.length > 0) {
      lines.push('⏳ PENDING:');
      pendingCrew.forEach(crew => {
        lines.push(`  • ${crew.crew_member_name || 'Unknown'}`);
      });
    }
    
    return lines.join('\n');
  };
  
  return (
    <div 
      className="flex items-center gap-1 cursor-help" 
      title={getTooltipContent()}
    >
      {notifiedCrew.length > 0 && (
        <div className="flex items-center gap-1" title={`${notifiedCrew.length} notified (FYI): ${notifiedCrew.map(c => c.crew_member_name).join(', ')}`}>
          <span className="text-blue-700 text-xs">📨</span>
          <span className="text-blue-700 text-xs">{notifiedCrew.length}</span>
        </div>
      )}
      {confirmedCrew.length > 0 && (
        <div className="flex items-center gap-1" title={`${confirmedCrew.length} confirmed: ${confirmedCrew.map(c => c.crew_member_name).join(', ')}`}>
          <span className="text-green-700 text-xs">✓</span>
          <span className="text-green-700 text-xs">{confirmedCrew.length}</span>
        </div>
      )}
      {declinedCrew.length > 0 && (
        <div className="flex items-center gap-1" title={`${declinedCrew.length} declined: ${declinedCrew.map(c => c.crew_member_name).join(', ')}`}>
          <span className="text-red-600 text-xs">✗</span>
          <span className="text-red-600 text-xs">{declinedCrew.length}</span>
        </div>
      )}
      {pendingCrew.length > 0 && (
        <div className="flex items-center gap-1" title={`${pendingCrew.length} pending: ${pendingCrew.map(c => c.crew_member_name).join(', ')}`}>
          <span className="text-amber-700 text-xs">⏳</span>
          <span className="text-amber-700 text-xs">{pendingCrew.length}</span>
        </div>
      )}
    </div>
  );
};

// Tooltip Portal Component - renders outside main DOM to avoid z-index issues
const TooltipPortal = ({ workPeriod, hoveredDay, position, sortResourcesByPriority }) => {
  if (!workPeriod) return null;
  
  // Helper to format date for exclusion checking
  const formatDateForExclusion = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  
  // Check if a resource is excluded on the hovered day and get target project
  const getResourceExclusionInfo = (resource) => {
    if (!hoveredDay || !workPeriod.excluded_dates) return null;
    const dateStr = formatDateForExclusion(hoveredDay);
    if (!dateStr || !workPeriod.excluded_dates[dateStr]) return null;
    const resourceKey = `${resource.type}_${resource.id}`;
    
    // Check for new format: "resourceKey|projectNumber"
    const exclusionEntry = workPeriod.excluded_dates[dateStr].find(e => 
      e.startsWith(resourceKey + '|') || e === resourceKey
    );
    
    if (!exclusionEntry) return null;
    
    // Parse the exclusion entry
    if (exclusionEntry.includes('|')) {
      const [key, targetProject] = exclusionEntry.split('|');
      return { excluded: true, targetProject };
    }
    
    // Old format (just resourceKey)
    return { excluded: true, targetProject: 'elsewhere' };
  };
  
  // Crew members with their own start/end dates: grey them out on days they're not on
  const offByDay = (r) => {
    if (!hoveredDay || r.type !== 'crew') return false;
    const ds = formatDateForExclusion(hoveredDay);
    if (!ds) return false;
    return !!((r.start_date && ds < r.start_date) || (r.end_date && ds > r.end_date));
  };

  // Count non-excluded resources
  const activeResourceCount = (workPeriod.resources || []).filter(r => !getResourceExclusionInfo(r) && !offByDay(r)).length;
  const isProjection = (workPeriod.source === 'projection' || workPeriod.crew_tbd || workPeriod.sheet_plan) && workPeriod.planned_crew > 0 && !(workPeriod.resources || []).some(r => r.type === 'crew');
  const isTbd = isProjection && workPeriod.crew_tbd;
  const isSheetPlan = isProjection && workPeriod.sheet_plan;
  const hoverKey = hoveredDay ? formatDateForExclusion(hoveredDay) : null;
  const sheetPlanToday = hoverKey && workPeriod.plan_by_day ? workPeriod.plan_by_day[hoverKey] : null;
  const namedToday = (workPeriod.resources || []).filter(r => r.type === 'crew' && !getResourceExclusionInfo(r) && !offByDay(r)).length;
  const details = [
    workPeriod.site_address && ['📍', workPeriod.site_address],
    (workPeriod.start_time || workPeriod.end_time) && ['🕖', `${workPeriod.start_time || ''}${workPeriod.end_time ? ` – ${workPeriod.end_time}` : ''}`],
    workPeriod.site_contact && ['☎', workPeriod.site_contact],
    workPeriod.tool_list && ['🧰', workPeriod.tool_list],
    workPeriod.notes && !isProjection && ['📝', String(workPeriod.notes).split('\n')[0].slice(0, 90)],
  ].filter(Boolean);
  
  return ReactDOM.createPortal(
    <div 
      className="fixed bg-white text-slate-900 text-sm rounded-lg shadow-2xl p-4 whitespace-nowrap border-2 border-blue-500 pointer-events-none"
      style={{
        left: `${position.x}px`,
        top: `${position.y - 12}px`,
        transform: 'translate(-50%, -100%)',
        zIndex: 999999
      }}
    >
      <div className={`font-bold mb-2 border-b border-slate-300 pb-2 text-center ${isProjection ? 'text-amber-700' : 'text-blue-700'}`} data-testid="wp-tooltip-title">
        {isProjection ? (isTbd ? `Crew TBD — ${workPeriod.planned_crew} guys, names to come` : isSheetPlan ? `Sheet plan — ${workPeriod.planned_crew} guys (Monthly View), no names on the day tab yet` : `Projection — ${workPeriod.planned_crew} guys, no names yet`) : <>{activeResourceCount} Active Resource{activeResourceCount !== 1 ? 's' : ''} {activeResourceCount !== (workPeriod.resources || []).length && `(${(workPeriod.resources || []).length - activeResourceCount} excluded)`}</>}
      </div>
      {sheetPlanToday != null && (
        <div className={`mb-2 text-xs rounded px-2 py-1 whitespace-normal max-w-[360px] ${Number(sheetPlanToday) === namedToday ? 'bg-slate-100 text-slate-600' : 'bg-amber-50 text-amber-800 border border-amber-200'}`} data-testid="wp-tooltip-sheet-plan">
          Sheet Monthly View says <b>{sheetPlanToday}</b> for this day{Number(sheetPlanToday) !== namedToday ? <> — but <b>{namedToday}</b> {namedToday === 1 ? 'name is' : 'names are'} on the day tab. The bubble and the header use the sheet's number.</> : ' — matches the names on the day tab.'}
        </div>
      )}
      {details.length > 0 && (
        <div className="mb-2 text-xs text-slate-700 space-y-0.5 max-w-[360px] whitespace-normal" data-testid="wp-tooltip-details">
          {details.map(([ic, txt], i) => <div key={i} className="flex gap-1.5"><span>{ic}</span><span>{txt}</span></div>)}
        </div>
      )}
      {isProjection && <div className="text-xs text-slate-600 whitespace-normal max-w-[300px]">{isTbd ? 'Approved by dispatch with the crew to be determined — pick the names with Modify Crew on the request.' : isSheetPlan ? 'Blair typed this number on the sheet; it firms up when the day tab gets names (or name them here on the day tab).' : 'Name the crew on the Dailies page for these days and this bar firms up on its own.'}</div>}
      <div className="space-y-1">
        {sortResourcesByPriority(workPeriod.resources || []).map((r, idx) => {
          const isForeman = workPeriod.foreman_id === r.id;
          const exclusionInfo = getResourceExclusionInfo(r);
          const isOffDates = offByDay(r);
          const isExcluded = exclusionInfo !== null || isOffDates;
          const getIcon = (type) => {
            if (type === 'crew') return '👤';
            if (type === 'trucks') return '🚚';
            if (type === 'trailers') return '🚛';
            if (type === 'welders') return '⚡';
            return '•';
          };
          return (
            <div 
              key={idx} 
              className={`py-1 font-medium ${isOffDates ? 'text-slate-700 line-through' : isExcluded ? 'text-slate-500 line-through opacity-60' : 'text-slate-800'}`}
              title={isOffDates ? `On this job ${r.start_date || workPeriod.start_date} to ${r.end_date || workPeriod.end_date || workPeriod.start_date}` : isExcluded ? `Assigned to ${exclusionInfo.targetProject} on this day` : ''}
            >
              {isForeman ? '👑 ' : getIcon(r.type)} {r.name} {isOffDates ? `(${r.start_date || workPeriod.start_date} → ${r.end_date || workPeriod.end_date || workPeriod.start_date})` : isExcluded && exclusionInfo ? `(${exclusionInfo.targetProject})` : ''}{r.unmatched ? <span className="text-teal-700 text-xs"> · sheet name</span> : null}
            </div>
          );
        })}
      </div>
      {/* Arrow pointing down */}
      <div 
        className="absolute left-1/2 -translate-x-1/2"
        style={{ top: '100%', marginTop: '-2px' }}
      >
        <div className="border-[10px] border-transparent border-t-blue-500"></div>
      </div>
    </div>,
    document.body
  );
};

// Conflict Resolution Modal Component
const ConflictResolutionModal = ({ show, conflictData, onConfirm, onCancel }) => {
  if (!show || !conflictData) return null;
  
  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold text-slate-900 mb-4">⚠️ Scheduling Conflict</h2>
        
        <div className="mb-4">
          <p className="text-slate-700 mb-3">
            <strong>{conflictData.resourceName}</strong> is already scheduled on:
          </p>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
            {conflictData.conflicts.map((conflict, idx) => (
              <div key={idx} className="mb-2 last:mb-0">
                <div className="text-sm font-medium text-yellow-900">
                  {conflict.projectName || 'Unknown Project'}
                </div>
                <div className="text-xs text-yellow-700">{conflict.dateRange}</div>
              </div>
            ))}
          </div>
          
          <p className="text-slate-700 mb-2">
            Do you want to <strong>reassign</strong> them to:
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <div className="text-sm font-medium text-blue-900">
              {conflictData.targetProject?.project_number} - {conflictData.targetProject?.title || 'Temporary Object'}
            </div>
            <div className="text-xs text-blue-700">
              {formatDate(conflictData.targetDate)}
            </div>
          </div>
        </div>
        
        <p className="text-sm text-slate-600 mb-4">
          This will mark them as unavailable on the original job for this date (shown crossed out) and assign them to the new job. The work period stays intact with visual indication of the exclusion.
        </p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Yes, Reassign
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleNew;